import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { UserPlus, RefreshCw, CheckCircle2, AlertTriangle, Loader2, ShieldCheck, Moon, CalendarDays, ChevronLeft, ChevronRight, Send, ClipboardCheck, Check, X, Pencil, DollarSign, Copy, ClipboardPaste, Save, ArrowLeftRight, Megaphone, Eye, Trash2, ScanLine, Camera, Inbox, Clock, QrCode, MapPin, Printer, Flag, Lock, Users, FileText, Archive } from 'lucide-react';
import QRCode from 'qrcode';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SorterTaxSuperRequest from '../components/SorterTaxSuperRequest';
import BriefText from '../components/BriefText';
import PeakAvailabilityView from './PeakAvailabilityView';

// ── Night Sorters admin ───────────────────────────────────────────────────────
// Manager view: roster (click-to-edit grid with per-shift time overrides, week
// templates, swaps, worker shift offers), AP paper sign-in sheet capture + OCR
// reconciliation, the morning finish-time broadcast, timesheet approvals,
// payroll and onboarding.

// Pay level follows grade (see levelFor in sorter-pay-sb.js): grade2→L2, grade6→L3,
// everything else→L1. A Team-Leader roster slot pays L3 for that shift regardless.
const GRADES = [
  { id: 'grade1', label: 'Grade 1 — Sorter (L1)' },
  { id: 'grade2', label: 'Grade 2 (L2)' },
  { id: 'grade3', label: 'Grade 3 (L1)' },
  { id: 'grade6', label: 'Grade 6 — Team Leader (L3)' },
  { id: 'grade8', label: 'Grade 8 — Foreperson (L1)' },
];
const FACILITIES = ['City', 'Darlinghurst', 'Broadway'];

const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 14, padding: 20, marginBottom: 20 };
const label = { display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 };
const input = { width: '100%', background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', fontSize: '0.92rem', padding: '10px 12px', boxSizing: 'border-box', fontFamily: 'var(--font-body)' };
const btn = (bg) => ({ padding: '11px 18px', background: bg, color: 'var(--hp-black)', fontWeight: 800, border: 'none', borderRadius: 10, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-body)' });
const pill = (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.7rem', fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: bg, color: fg });

const ok = (text) => <span style={pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)')}><CheckCircle2 size={12} />{text}</span>;
const warn = (text) => <span style={pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)')}><AlertTriangle size={12} />{text}</span>;

const EMPTY = { login: '', name: '', grade: 'grade1', mobile: '', email: '', facility: '', startDate: '', baseRate: '', bankName: '', accountName: '', bsb: '', accNumber: '' };

// Non-JSON responses (Netlify's HTML timeout/error pages) become a readable message
// instead of "Unexpected token '<'".
const api = (action, payload) => fetch('/.netlify/functions/appsscript', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) })
  .then(async r => {
    const text = await r.text();
    try { return JSON.parse(text); }
    catch { throw new Error(`The server didn't answer in time (${r.status}) — please try again.`); }
  });
// NEVER toISOString() for calendar dates — it converts to UTC, which knocks Sydney
// dates back a day (a Sunday labels itself Saturday and rows save under wrong dates).
const isoLocal = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
const fmtD = (iso) => new Date(iso + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
function sundayOf(iso) { const d = new Date(iso + 'T00:00'); d.setDate(d.getDate() - d.getDay()); return isoLocal(d); }
function weekDates(weekStart) { const d = new Date(weekStart + 'T00:00'); return Array.from({ length: 7 }, (_, i) => { const x = new Date(d); x.setDate(d.getDate() + i); return isoLocal(x); }); }
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// A night operates if its weekday is in cfg.days, OR its date sits inside the peak window
// [peakFrom,peakTo] and its weekday is in cfg.peakDays (e.g. Friday during pre-Xmas peak).
function isOperatingDay(dateISO, cfg) {
  if (!cfg || !Array.isArray(cfg.days)) return true;
  const dow = new Date(dateISO + 'T00:00').getDay();
  if (cfg.days.includes(dow)) return true;
  if (cfg.peakFrom && cfg.peakTo && dateISO >= cfg.peakFrom && dateISO <= cfg.peakTo && (cfg.peakDays || []).includes(dow)) return true;
  return false;
}
// ISO date (YYYY-MM-DD) → "Mon 06/07/2026" — day name + DD/MM/YYYY to match the date pickers.
const fmtDayDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00');
  if (isNaN(d)) return iso;
  return `${DOW[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

// ── Finish time — AP's morning message, keyed in + broadcast to the team ──────
// The shift night defaults to yesterday before midday (AP text arrives in the
// morning, about the shift that just ended) and to today from midday on.
function defaultShiftDate() {
  const d = new Date();
  if (d.getHours() < 12) d.setDate(d.getDate() - 1);
  return isoLocal(d);
}

function FinishTimeCard({ testMode, onSaved }) {
  const [shiftDate, setShiftDate] = useState(defaultShiftDate);
  const [time, setTime] = useState('');
  const [note, setNote] = useState('');
  const [cur, setCur] = useState(null);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const flash = (text, ok = true) => { setToast({ text, ok }); setTimeout(() => setToast(null), 6000); };

  const load = useCallback(async () => {
    try {
      const r = await api('nsGetFinishTime', { shiftDate });
      setCur(r && r.success ? r.finish : null);
      if (r && r.success && r.finish) { setTime(r.finish.finishTime); setNote(r.finish.note || ''); }
      else { setTime(''); setNote(''); }
    } catch (e) { setCur(null); }
  }, [shiftDate]);
  useEffect(() => { load(); }, [load]);

  async function save() {
    if (!time) { flash('Enter the finish time first.', false); return; }
    setBusy('save');
    try { const r = await api('nsSetFinishTime', { shiftDate, finishTime: time, note }); if (!r.success) throw new Error(r.error || 'Failed'); flash('Finish time saved — it now shows in the sorter portal.'); load(); if (onSaved) onSaved(); }
    catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }
  async function broadcast() {
    setBusy('bc');
    try {
      const r = await api('nsBroadcastFinishTime', { shiftDate });
      if (!r.success) throw new Error(r.error || 'Failed');
      if (r.testMode) flash(`TEST MODE — sent to ${r.sent} TEST account${r.sent === 1 ? '' : 's'} only; ${r.skippedLive} real sorters were NOT messaged.`, true);
      else flash(`SMS sent to ${r.sent} of ${r.total} sorters${r.noMobile ? ` (${r.noMobile} have no mobile on file)` : ''}${r.failed && r.failed.length ? ` — failed: ${r.failed.join(', ')}` : ''}.`, r.sent > 0);
      load();
    } catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <Clock size={18} color="var(--hp-gold)" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Shift finish time</h2>
        {cur && cur.broadcastAt && <span style={pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)')}><Megaphone size={12} /> sent to {cur.broadcastCount} at {new Date(cur.broadcastAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
      </div>
      {toast && <div style={{ ...pill(toast.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', toast.ok ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)'), padding: '8px 12px', marginBottom: 12 }}>{toast.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {toast.text}</div>}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div><label style={label}>Shift night</label><input type="date" style={{ ...input, width: 160 }} value={shiftDate} onChange={e => setShiftDate(e.target.value)} /></div>
        <div><label style={label}>Finish time</label><input type="time" style={{ ...input, width: 120 }} value={time} onChange={e => setTime(e.target.value)} /></div>
        <div style={{ flex: 1, minWidth: 180 }}><label style={label}>Note (optional, goes in the SMS)</label><input style={input} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. heavy volume tomorrow" /></div>
        <button onClick={save} disabled={busy === 'save'} style={btn('var(--hp-gold)')}>{busy === 'save' ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Check size={14} />} Save</button>
        <button onClick={broadcast} disabled={busy === 'bc' || !cur} title={cur ? (testMode ? 'TEST mode: only TEST accounts get the SMS' : 'SMS every active sorter with a mobile on file') : 'Save the time first'} style={btn(testMode ? 'var(--hp-amber, #F59E0B)' : 'var(--hp-green, #22C55E)')}>{busy === 'bc' ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Megaphone size={14} />} {testMode ? 'Broadcast (TEST only)' : 'Broadcast SMS'}</button>
      </div>
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', marginTop: 10 }}>When AP text the closing time in the morning, key it in and Save — it appears in every sorter's portal immediately. Broadcast pushes it by SMS to all active sorters with a mobile on file.</p>
    </div>
  );
}

// ── Weekly roster builder — click-to-edit cells, time overrides, templates ────
const shortName = (name, login) => {
  const parts = (name || '').replace(/\(.*?\)/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!parts[0]) return login || '—';
  return parts.length > 1 ? parts[0] + ' ' + parts[parts.length - 1][0] + '.' : parts[0];
};
// HOUSE RULE: Runner positions (incl. "Team Leader / Runner") always start and finish
// exactly 1 hour after the sorter positions — the grid warns whenever a runner drifts.
// Category gates the name test: "Sorter City 4 - Used when TL is Runner" is a sorter.
const isRunnerSlot = (sl) => (sl && (sl.Category || '') === 'lead') && /runner/i.test(sl.Slot || '');

// Slot-group colour scheme — colours the roster by area so the grid reads at a glance.
const SLOT_CATEGORIES = [
  { key: 'lead', label: 'Team Leader', colour: '#A855F7' },
  { key: 'city', label: 'City', colour: '#3B82F6' },
  { key: 'darlo', label: 'Darlinghurst', colour: '#14B8A6' },
  { key: 'broadway', label: 'Broadway', colour: '#F97316' },
  { key: 'xmas', label: 'Xmas additional', colour: '#E11D48' },
];
function slotCategory(sl) {
  const cat = (sl && sl.Category) || '', n = (sl && sl.Slot) || '';
  if (cat === 'lead') return SLOT_CATEGORIES[0];
  if (/broadway/i.test(n)) return SLOT_CATEGORIES[3];
  if (/darlo|darlinghurst/i.test(n)) return SLOT_CATEGORIES[2];
  if (/xmas|christmas/i.test(n)) return SLOT_CATEGORIES[4];
  if (/city/i.test(n)) return SLOT_CATEGORIES[1];
  return { key: 'other', label: 'Other', colour: '#6B7280' };
}

// Area grouping for the roster grid — one box per area so each area (Team Leader &
// Runners · City · Darlinghurst · Broadway · Xmas) reads as its own block on screen.
// Order matters: "Sorter City 4 – Used when TL is Runner" is a sorter (city), so the
// city test runs before the lead check.
function slotGroup(sl) {
  const cat = (sl && sl.Category) || '', n = (sl && sl.Slot) || '';
  if (/broadway/i.test(n)) return { key: 'broadway', label: 'Broadway', colour: '#F97316' };
  if (/darlo|darlinghurst/i.test(n)) return { key: 'darlo', label: 'Darlinghurst', colour: '#14B8A6' };
  if (/xmas|christmas/i.test(n)) return { key: 'xmas', label: 'Xmas Casual', colour: '#E11D48' };
  if (/city/i.test(n)) return { key: 'city', label: 'City', colour: '#3B82F6' };
  if (cat === 'lead') return { key: 'lead', label: 'Team Leader & Runners', colour: '#A855F7' };
  return { key: 'other', label: 'Other', colour: '#6B7280' };
}

// Shift lifecycle → tile treatment, derived from attendance (no separate status machine):
// accepted (amber) → completed/clocked-out (green) → authorised/approved (blue + lock);
// a past night with no attendance reads as not-completed (red).
const LIFE = {
  amber: { bd: 'var(--hp-amber, #F59E0B)', bg: 'rgba(245,158,11,0.12)' },
  green: { bd: 'var(--hp-green, #22C55E)', bg: 'rgba(34,197,94,0.12)' },
  red:   { bd: 'var(--hp-red, #EF4444)',   bg: 'rgba(239,68,68,0.12)' },
  blue:  { bd: 'var(--hp-blue, #3B82F6)',  bg: 'rgba(59,130,246,0.14)' },
};
function lifecycleOf(att, date, login, today) {
  const a = att[date + '|' + (login || '').toUpperCase()];
  if (a) {
    if (a.status === 'Approved') return { ...LIFE.blue, lock: true };
    if (a.status === 'Absent') return LIFE.red;  // recorded as away — the slot went unworked
    return LIFE.green; // Pending or Open — clocked out / mid-shift = worked
  }
  return date < today ? LIFE.red : LIFE.amber; // no attendance: past = missed, else accepted
}

// draftMode = the scoped roster-manager view (/sort-roster): "Publish" becomes "Submit for
// approval" (writes a draft via sort-roster-draft-sb, never the live roster), and the
// admin-only cards (finish time, shift-offer resolution, template writes) are hidden.
// In the admin view (draftMode false) a Pending-drafts panel lets an admin approve/reject.
function RosterBuilder({ sorters, testMode, draftMode = false, managerName = '' }) {
  const [weekStart, setWeekStart] = useState(() => sundayOf(isoLocal(new Date())));
  const [slots, setSlots] = useState([]);
  const [grid, setGrid] = useState({});       // key `date|slot` → {login, name, start, end}
  const [att, setAtt] = useState({});          // key `date|LOGIN` → {status, hasOut} (from attendance)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [sel, setSel] = useState(null);        // selected cell key (opens the editor)
  const [swapSrc, setSwapSrc] = useState(null); // first cell of a pending swap
  const [noticeTick, setNoticeTick] = useState(0);  // bumped on publish so the change log reloads
  const [clip, setClip] = useState(null);       // copied driver { login, name } for paste
  const [tileMenu, setTileMenu] = useState(null); // { key, x, y } small Copy/Remove menu on a filled tile
  const [templates, setTemplates] = useState([]);
  const [tplSel, setTplSel] = useState('');
  const [offers, setOffers] = useState([]);
  const [offerPick, setOfferPick] = useState({}); // offerId → replacement login
  const [dirty, setDirty] = useState(false);
  const [dragOver, setDragOver] = useState(null); // cell key currently hovered by a drag
  const [dayEdit, setDayEdit] = useState(null);   // {date, start, end} — night-time editor
  const [dayDelta, setDayDelta] = useState({});   // date → {ds, de} minute shifts vs slot defaults
  const [weekdayDef, setWeekdayDef] = useState({}); // dow 0-6 → {start,end} persisted per-weekday default
  const [standing, setStanding] = useState([]);   // [{slot, login, name}] permanent slot occupants
  const [stdForm, setStdForm] = useState({ slot: '', login: '' }); // admin: add-a-standing-position form
  const [operating, setOperating] = useState({ days: [0, 1, 2, 3, 4], peakDays: [5], peakFrom: '', peakTo: '' }); // operating nights
  const [opForm, setOpForm] = useState(null);     // admin: draft edit of the operating-nights config
  const [inability, setInability] = useState([]); // [{id,login,name,date,reason}] nights-off requests this week
  const [inabForm, setInabForm] = useState({ login: '', date: '', reason: '' }); // office: log a request on behalf
  const [memberColours, setMemberColours] = useState({}); // { LOGIN: {gender,colour,nick} } per-person colours
  const [showColours, setShowColours] = useState(false);  // expand the member-colours editor
  const [showTotals, setShowTotals] = useState(false);    // expand the weekly shift-totals summary
  const [draftStatus, setDraftStatus] = useState(null); // draftMode: this week's submitted draft meta
  const [pendingDrafts, setPendingDrafts] = useState([]); // admin: weeks awaiting approval
  const [reviewBusy, setReviewBusy] = useState('');       // admin: weekStart being approved/rejected
  const [savedCount, setSavedCount] = useState(0);        // shifts already saved on the live roster for this week
  const [backup, setBackup] = useState(null);             // {clearedAt, count} — undo snapshot of a cleared week
  const [nextWeek, setNextWeek] = useState(null);         // admin: is NEXT week submitted, and was Valeria reminded?
  const [remindBusy, setRemindBusy] = useState(false);
  const days = weekDates(weekStart);
  // The week Valeria should be building now: the one after the week we're in today.
  const nextWeekStart = (() => { const d = new Date(sundayOf(isoLocal(new Date())) + 'T00:00'); d.setDate(d.getDate() + 7); return isoLocal(d); })();
  const flash = (text, ok = true) => { setToast({ text, ok }); setTimeout(() => setToast(null), 6000); };

  const slotByName = (name) => slots.find(s => s.Slot === name);

  const load = useCallback(async () => {
    setLoading(true); setSel(null); setSwapSrc(null); setDirty(false);
    try {
      const ds = weekDates(weekStart);
      const [s, r, t, o, ts, wd, st, op, inab, mc] = await Promise.all([
        api('getSorterSlots', {}),
        api('getSorterRoster', { from: ds[0], to: ds[6] }),
        api('listSorterRosterTemplates', {}),
        api('listSorterShiftOffers', { status: 'Open' }),
        api('getSorterTimesheetsAdmin', { status: 'All', from: ds[0], to: ds[6] }),
        api('getRosterWeekdayDefaults', {}),
        api('getStandingAssignments', {}),
        api('getOperatingDays', {}),
        api('listInability', { from: ds[0], to: ds[6] }),
        api('getMemberColours', {}),
      ]);
      const stdList = (st && st.success && Array.isArray(st.assignments)) ? st.assignments : [];
      setStanding(stdList);
      const opCfg = (op && op.success && op.operating) ? op.operating : { days: [0, 1, 2, 3, 4], peakDays: [5], peakFrom: '', peakTo: '' };
      setOperating(opCfg);
      setInability((inab && inab.success && Array.isArray(inab.requests)) ? inab.requests : []);
      setMemberColours((mc && mc.success && mc.colours) ? mc.colours : {});
      setSlots(s && s.success ? s.slots : []);
      // Per-weekday default start/end (e.g. Sunday 23:15) → seed dayDelta so new assignments
      // and the night heading pick it up. Base = the slot sorter default (23:45–06:15);
      // dayDelta stores the minute offset, and every slot (incl. runners +1h) shifts by it.
      const wdMap = (wd && wd.success && wd.defaults) ? wd.defaults : {};
      setWeekdayDef(wdMap);
      const baseSlot = (s && s.success ? s.slots : []).find(sl => (sl.Category || '') === 'sorter') || (s && s.slots || [])[0];
      const baseS = ((baseSlot && baseSlot['Default Start']) || '23:45');
      const baseE = ((baseSlot && baseSlot['Default End']) || '06:15');
      const toM = (x) => { const m = /^(\d{1,2}):(\d{2})$/.exec((x || '').trim()); return m ? (+m[1]) * 60 + (+m[2]) : null; };
      const seed = {};
      ds.forEach((d) => {
        const dow = new Date(d + 'T00:00').getDay();
        const wdd = wdMap[dow] || wdMap[String(dow)];
        if (!wdd || !wdd.start) return;
        const ds0 = toM(wdd.start), de0 = toM(wdd.end != null && wdd.end !== '' ? wdd.end : baseE);
        const bs = toM(baseS), be = toM(baseE);
        if (ds0 == null || bs == null || be == null) return;
        seed[d] = { ds: ds0 - bs, de: (de0 == null ? be : de0) - be };
      });
      setDayDelta(seed);
      let baseGrid = {};
      ((r && r.roster) || []).forEach(row => {
        baseGrid[row.Date + '|' + row.Slot] = { login: (row.Login || '').toUpperCase(), name: row.Name || '', start: row.Start || '', end: row.End || '' };
      });
      setSavedCount(Object.keys(baseGrid).length); // what's really on the roster, before standing rules fill in
      // Shift lifecycle: join each roster tile to its attendance row → tile border colour.
      const am = {};
      ((ts && ts.timesheets) || []).forEach(row => {
        if ((row.Status || '') === 'Discarded') return;
        am[row.Date + '|' + (row.Login || '').toUpperCase()] = { status: row.Status || '', hasOut: !!row.actualOut };
      });
      setAtt(am);
      setTemplates(t && t.success ? t.templates : []);
      setOffers(o && o.success ? o.offers : []);
      // Draft overlay (manager resumes her own submitted week) / approval queue (admin).
      if (draftMode) {
        try {
          const d = await api('getRosterDraft', { weekStart });
          const draft = d && d.success ? d.draft : null;
          if (draft && (draft.status === 'Pending' || draft.status === 'Rejected') && Array.isArray(draft.assignments)) {
            const dg = {};
            draft.assignments.forEach(a => { if (a && a.login) dg[a.date + '|' + a.slot] = { login: (a.login || '').toUpperCase(), name: a.name || '', start: a.start || '', end: a.end || '' }; });
            baseGrid = dg;
          }
          setDraftStatus(draft);
        } catch (_) { setDraftStatus(null); }
      } else {
        try {
          const dl = await api('listRosterDrafts', {});
          setPendingDrafts(dl && dl.success ? (dl.drafts || []).filter(x => x.status === 'Pending') : []);
        } catch (_) { setPendingDrafts([]); }
        // Is there a cleared week sitting in the undo snapshot? Drives the Restore button.
        try { const b = await api('getSorterRosterBackup', { weekStart }); setBackup(b && b.success ? b.backup : null); } catch (_) { setBackup(null); }
        // Chase state for the week AFTER the current one — always measured from today, not
        // from whichever week is on screen.
        try { const nw = await api('getRosterReminder', { weekStart: nextWeekStart }); setNextWeek(nw && nw.success ? nw : null); } catch (_) { setNextWeek(null); }
      }
      // Standing positions: a sorter permanently tied to a slot auto-fills every empty night
      // (drag a replacement over any night they're out). Times follow the weekday default.
      let added = 0;
      const slotList = (s && s.success ? s.slots : []);
      stdList.forEach(rule => {
        if (!rule || !rule.login || !rule.slot) return;
        ds.forEach((d) => {
          if (!isOperatingDay(d, opCfg)) return; // no-shift night (Fri/Sat outside peak)
          const key = d + '|' + rule.slot;
          if (baseGrid[key] && baseGrid[key].login) return; // published / draft / replacement already there
          const dd = seed[d];
          const slDef = slotList.find(x => x.Slot === rule.slot);
          const bs = toM((slDef && slDef['Default Start']) || '23:45'), be = toM((slDef && slDef['Default End']) || '06:15');
          const start = (dd && bs != null) ? fromMin(bs + dd.ds) : ((slDef && slDef['Default Start']) || '');
          const end = (dd && be != null) ? fromMin(be + dd.de) : ((slDef && slDef['Default End']) || '');
          baseGrid[key] = { login: (rule.login || '').toUpperCase(), name: rule.name || rule.login, start, end, standing: true };
          added++;
        });
      });
      setGrid(baseGrid);
      if (added) setDirty(true); // fresh week pre-filled from rules → prompt review/submit
    } catch (e) {} finally { setLoading(false); }
  }, [weekStart, draftMode]);
  useEffect(() => { load(); }, [load]);

  const shiftWeek = (n) => { const d = new Date(weekStart + 'T00:00'); d.setDate(d.getDate() + n * 7); setWeekStart(isoLocal(d)); };
  const toMin = (t) => { const m = /^(\d{1,2}):(\d{2})$/.exec((t || '').trim()); return m ? (+m[1]) * 60 + (+m[2]) : null; };
  const fromMin = (n) => { n = ((n % 1440) + 1440) % 1440; return String(Math.floor(n / 60)).padStart(2, '0') + ':' + String(n % 60).padStart(2, '0'); };
  const sorterDefault = () => slots.find(sl => (sl.Category || '') === 'sorter') || slots[0];
  // When the night's times were changed (AP moved the start), new assignments on that
  // night inherit the same shift instead of the stale slot default.
  const newAssignTimes = (key) => {
    const [date, slotName] = key.split('|');
    const dd = dayDelta[date];
    if (!dd) return { start: '', end: '' };
    const sl = slotByName(slotName);
    const s = toMin((sl && sl['Default Start']) || '23:45'), e = toMin((sl && sl['Default End']) || '06:15');
    return { start: fromMin(s + dd.ds), end: fromMin(e + dd.de) };
  };
  const people = sorters.slice().sort((a, b) => (a.Name || a.Login || '').localeCompare(b.Name || b.Login || ''));
  const personName = (login) => { const p = people.find(x => (x.Login || '').toUpperCase() === login); return p ? p.Name : login; };

  const todayISO = isoLocal(new Date()); // for shift-lifecycle tile colours
  // Same person twice on the same night = double-booked → red cell.
  const conflicts = {};
  days.forEach(d => {
    const seen = {};
    slots.forEach(sl => { const a = grid[d + '|' + sl.Slot]; if (a && a.login) seen[a.login] = (seen[a.login] || 0) + 1; });
    conflicts[d] = new Set(Object.keys(seen).filter(l => seen[l] > 1));
  });
  // Nights-off requests keyed `LOGIN|date` → the request, for the on-grid "asked off" flag.
  const inabilityByKey = {};
  inability.forEach(x => { if (x && x.login && x.date) inabilityByKey[(x.login || '').toUpperCase() + '|' + x.date] = x; });
  // How many filled shifts will publish/submit — shown on the button — and a per-person tally.
  let filledCount = 0;
  const weekTally = {};
  days.forEach(d => { if (isOperatingDay(d, operating)) slots.forEach(sl => { const a = grid[d + '|' + sl.Slot]; if (a && a.login) { filledCount++; weekTally[a.login] = (weekTally[a.login] || 0) + 1; } }); });

  // Header time per day: the most common start–finish among that night's assigned
  // SORTER shifts (runners are always +1h, so they'd skew the heading); slot defaults
  // when the night is empty.
  const dayTimes = {};
  days.forEach(d => {
    const counts = {};
    slots.forEach(sl => {
      if ((sl.Category || '') !== 'sorter') return;
      const a = grid[d + '|' + sl.Slot];
      if (!(a && a.login)) return;
      const range = ((a.start) || sl['Default Start'] || '') + '–' + ((a.end) || sl['Default End'] || '');
      if (range !== '–') counts[range] = (counts[range] || 0) + 1;
    });
    let best = '';
    Object.entries(counts).forEach(([r, n]) => { if (!best || n > counts[best]) best = r; });
    if (!best) {
      const sorterSlot = slots.find(sl => (sl.Category || '') === 'sorter') || slots[0];
      if (sorterSlot && sorterSlot['Default Start']) {
        // Empty night: show the per-weekday default (via dayDelta seed) if one is set,
        // else the plain slot default.
        const dd = dayDelta[d];
        const bs = toMin(sorterSlot['Default Start']), be = toMin(sorterSlot['Default End']);
        best = dd && bs != null && be != null
          ? fromMin(bs + dd.ds) + '–' + fromMin(be + dd.de)
          : sorterSlot['Default Start'] + '–' + sorterSlot['Default End'];
      }
    }
    dayTimes[d] = best;
  });

  const setCell = (key, patch) => { setGrid(g => ({ ...g, [key]: patch })); setDirty(true); };

  const swapCells = (a, b) => {
    setGrid(g => { const va = g[a] || null, vb = g[b] || null; const n = { ...g }; if (vb) n[a] = vb; else delete n[a]; if (va) n[b] = va; else delete n[b]; return n; });
    setDirty(true);
  };

  function onCellClick(key, e) {
    if (swapSrc) {
      if (swapSrc !== key) { swapCells(swapSrc, key); flash('Swapped — publish to make it live.'); }
      setSwapSrc(null); setSel(null);
      return;
    }
    const filled = grid[key] && grid[key].login;
    // A driver is on the clipboard and this position is EMPTY → drop them straight in,
    // so one copy can be pasted across a whole week with a click per position.
    if (clip && clip.login && !filled) { pasteCell(key, { quiet: true }); return; }
    if (filled) {
      // Filled tile → a small Copy / Remove menu anchored at the tile (toggle off if same).
      if (tileMenu && tileMenu.key === key) { setTileMenu(null); setSel(null); return; }
      const r = e && e.currentTarget && e.currentTarget.getBoundingClientRect ? e.currentTarget.getBoundingClientRect() : null;
      setSel(key);
      setTileMenu({ key, x: r ? r.left : 0, y: r ? r.bottom + 4 : 0 });
      return;
    }
    // Empty tile, nothing copied → open the full editor to assign / set times.
    setTileMenu(null);
    setSel(sel === key ? null : key);
  }

  // Copy / paste / delete a driver between positions. Copy stows the person; paste
  // drops them into the target position, recomputing that slot's start/end (a runner
  // sits 1h after the sorters, so times must fit the destination, not the source).
  function copyCell(key) {
    const a = grid[key];
    if (!(a && a.login)) { flash('Nothing to copy — that position is empty.', false); return; }
    setClip({ login: a.login, name: a.name || personName(a.login) });
    flash(`Copied ${a.name || personName(a.login)} — select a position and Paste.`);
  }
  function pasteCell(key, opts) {
    if (!(clip && clip.login)) { flash('Nothing copied yet — Copy a driver first.', false); return; }
    const cur = grid[key];
    // Guard: pasting over a DIFFERENT driver replaces them — confirm first.
    if (cur && cur.login && cur.login !== clip.login) {
      const [date, slot] = key.split('|');
      if (!window.confirm(`Replace ${cur.name || personName(cur.login)} with ${personName(clip.login) || clip.name} on ${slot} (${DOW[days.indexOf(date)] || ''} ${fmtD(date)})?`)) return;
    }
    const t = newAssignTimes(key);
    setCell(key, { login: clip.login, name: personName(clip.login), start: t.start, end: t.end });
    if (!(opts && opts.quiet)) setSel(key); // quiet = click-to-fill, leave the editor closed
    flash(`Pasted ${personName(clip.login) || clip.name}.`);
  }
  function deleteCell(key) {
    if (!(grid[key] && grid[key].login)) return;
    setGrid(g => { const n = { ...g }; delete n[key]; return n; });
    setDirty(true);
    flash('Removed — publish to make it live.');
  }

  // Drag & drop: a name chip from the palette drops onto a shift ("L:LOGIN"); a filled
  // cell drags onto another cell ("C:key") — move if the target is empty, swap if not.
  function onCellDrop(e, key) {
    e.preventDefault(); setDragOver(null);
    const payload = e.dataTransfer.getData('text/plain') || '';
    if (payload.startsWith('L:')) {
      const login = payload.slice(2);
      const cur = grid[key];
      const t = newAssignTimes(key);
      setCell(key, { login, name: personName(login), start: (cur && cur.start) || t.start, end: (cur && cur.end) || t.end });
      setSel(null);
    } else if (payload.startsWith('C:')) {
      const src = payload.slice(2);
      if (src !== key && grid[src]) { swapCells(src, key); setSel(null); }
    }
  }

  // AP moved the night's times: edit the sorter start–finish for one night and every
  // shift that night moves by the same amount, so leads/runners keep their usual
  // offset from the sorters (AP shifts the whole night together, per the paper sheets).
  function openDayEdit(date) {
    const def = sorterDefault();
    const cur = (dayTimes[date] || '').split('–');
    setDayEdit({
      date,
      start: (cur[0] || (def && def['Default Start']) || '23:45').trim(),
      end: (cur[1] || (def && def['Default End']) || '06:15').trim(),
    });
    setSel(null); setSwapSrc(null);
  }

  function applyDayTimes() {
    const { date, start, end } = dayEdit;
    const def = sorterDefault();
    const s = toMin(start), e = toMin(end);
    if (s == null || e == null) { flash('Enter both times.', false); return; }
    setGrid(g => {
      const n = { ...g };
      slots.forEach(sl => {
        const key = date + '|' + sl.Slot;
        const a = n[key];
        if (!(a && a.login)) return;
        // HOUSE RULE: Runner positions always start AND finish 1 hour after the sorters.
        const off = isRunnerSlot(sl) ? 60 : 0;
        n[key] = { ...a, start: fromMin(s + off), end: fromMin(e + off) };
      });
      return n;
    });
    setDayDelta(m => ({ ...m, [date]: { ds: s - toMin((def && def['Default Start']) || '23:45'), de: e - toMin((def && def['Default End']) || '06:15') } }));
    setDirty(true); setDayEdit(null);
    flash(`${DOW[days.indexOf(date)]} ${date}: sorters ${start}–${end}, runners ${fromMin(s + 60)}–${fromMin(e + 60)} (always +1h). Publish to make it live.`);
  }

  // Admin: persist this night's sorter times as the REMEMBERED default for that weekday
  // (e.g. every Sunday → 23:15). Runners still derive +1h. Then apply to the on-screen night.
  async function saveWeekdayDefault() {
    if (!dayEdit) return;
    const { date, start, end } = dayEdit;
    const s = toMin(start), e = toMin(end);
    if (s == null || e == null) { flash('Enter both times.', false); return; }
    const dow = new Date(date + 'T00:00').getDay();
    const r = await api('setRosterWeekdayDefault', { dow, start, end });
    if (!r || !r.success) { flash((r && r.error) || 'Could not save default', false); return; }
    setWeekdayDef(r.defaults || {});
    applyDayTimes(); // apply to this night, record dayDelta for the date, close the editor
    flash(`Saved — every ${DOW[dow]} now defaults to ${start}–${end} (runners +1h). Publish to apply to this week.`);
  }

  // Admin: set/clear a permanent occupant for a slot. Re-loads so the week auto-fills.
  async function setStandingPosition(slot, login) {
    if (!slot) { flash('Pick a slot first.', false); return; }
    const name = login ? personName((login || '').toUpperCase()) : '';
    const r = await api('setStandingAssignment', { slot, login: login || '', name });
    if (!r || !r.success) { flash((r && r.error) || 'Could not save the rule', false); return; }
    setStanding(r.assignments || []);
    setStdForm({ slot: '', login: '' });
    flash(login ? `${name} is now the standing ${slot} — they'll auto-fill every night.` : `Cleared the standing occupant for ${slot}.`);
    load();
  }

  // Admin: save which nights the sorter week operates (Sun–Thu standard; Friday in peak).
  async function saveOperating() {
    if (!opForm) return;
    const r = await api('setOperatingDays', opForm);
    if (!r || !r.success) { flash((r && r.error) || 'Could not save operating nights', false); return; }
    setOperating(r.operating); setOpForm(null);
    flash('Operating nights saved. The roster grid now greys out no-shift nights.');
    load();
  }
  const toggleDow = (field, dow) => setOpForm(f => ({ ...f, [field]: (f[field] || []).includes(dow) ? f[field].filter(x => x !== dow) : [...(f[field] || []), dow].sort((a, b) => a - b) }));

  // Office logs a phoned-in nights-off request on a sorter's behalf (best-endeavours only).
  async function logInability() {
    const { login, date, reason } = inabForm;
    if (!login || !date) { flash('Pick a sorter and a date.', false); return; }
    const r = await api('submitInability', { login, date, name: personName((login || '').toUpperCase()), reason });
    if (!r || !r.success) { flash((r && r.error) || 'Could not log the request', false); return; }
    setInabForm({ login: '', date: '', reason: '' });
    flash('Nights-off request logged. It shows on the roster as a flag — they can still be rostered.');
    load();
  }
  async function cancelInabilityReq(id) {
    const r = await api('cancelInability', { id });
    if (!r || !r.success) { flash((r && r.error) || 'Could not remove', false); return; }
    setInability(list => list.filter(x => x.id !== id));
  }
  // Manual "Can't accommodate" — tell the sorter now, with an optional note.
  async function cantAccommodate(id) {
    const note = window.prompt("Optional note to the sorter (why we couldn't free them up):");
    if (note === null) return; // cancelled
    const r = await api('resolveInability', { id, outcome: 'NotMet', note });
    if (!r || !r.success) { flash((r && r.error) || 'Could not update', false); return; }
    flash("Marked couldn't-be-met — the sorter sees it on their portal. On approval it re-checks the roster.");
    load();
  }
  const inabStatus = (s) => s === 'Granted' ? ok('Night off granted') : s === 'NotMet' ? <span style={pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)')}><X size={11} />Couldn't be met</span> : warn('Awaiting decision');

  const colourOf = (login) => (memberColours[(login || '').toUpperCase()] || {}).colour || '';
  const genderOf = (login) => (memberColours[(login || '').toUpperCase()] || {}).gender || '';
  const nickOf = (login) => (memberColours[(login || '').toUpperCase()] || {}).nick || '';
  // Preferred name if set, else the shortened full name. For the palette pill, fall back to full name.
  const gridName = (login, fullName) => nickOf(login) || shortName(fullName, login);
  const pillName = (login, fullName) => nickOf(login) || fullName || login;
  // Set a member's ♀/♂ flag → the palette re-picks their colour server-side.
  async function setGender(login, gender) {
    const r = await api('setMemberGender', { login: (login || '').toUpperCase(), gender });
    if (r && r.success) setMemberColours(r.colours || {});
    else flash((r && r.error) || 'Could not update colour', false);
  }
  // Pick an exact colour for one person (overrides the auto-pick).
  async function setColour(login, colour) {
    const r = await api('setMemberColour', { login: (login || '').toUpperCase(), colour });
    if (r && r.success) setMemberColours(r.colours || {});
    else flash((r && r.error) || 'Could not set colour', false);
  }
  // Set a person's preferred / nickname (shown on the pills + grid).
  async function setNick(login, nick) {
    const r = await api('setMemberNick', { login: (login || '').toUpperCase(), nick });
    if (r && r.success) setMemberColours(r.colours || {});
    else flash((r && r.error) || 'Could not save name', false);
  }

  async function publish() {
    // Hard gate: nobody may hold two positions on the same night. Name the day + shifts.
    const clashes = [];
    days.forEach(d => {
      if (!isOperatingDay(d, operating)) return;
      const byLogin = {};
      slots.forEach(sl => { const a = grid[d + '|' + sl.Slot]; if (a && a.login) (byLogin[a.login] = byLogin[a.login] || []).push(sl.Slot); });
      Object.entries(byLogin).forEach(([login, slotNames]) => { if (slotNames.length > 1) clashes.push({ date: d, name: personName(login), slots: slotNames }); });
    });
    if (clashes.length) {
      const lines = clashes.map(c => `• ${DOW[days.indexOf(c.date)]} ${fmtD(c.date)}: ${c.name} is in "${c.slots.join('" AND "')}"`);
      window.alert(`Can't ${draftMode ? 'submit' : 'publish'} — someone is rostered to two positions on the same night:\n\n${lines.join('\n')}\n\nFix the clashes (the red cells), then try again.`);
      flash(`${clashes.length} double-booking${clashes.length === 1 ? '' : 's'} — nobody can hold two positions in one night. See the red cells.`, false);
      return;
    }
    setSaving(true);
    try {
      const assignments = [];
      slots.forEach(sl => days.forEach(d => {
        // No-shift nights are always submitted empty — clears any stray assignment there.
        const op = isOperatingDay(d, operating);
        const a = op ? grid[d + '|' + sl.Slot] : null;
        assignments.push({
          date: d, slot: sl.Slot, login: (a && a.login) || '',
          name: a && a.login ? (a.name || personName(a.login)) : '',
          start: (a && a.start) || sl['Default Start'] || '', end: (a && a.end) || sl['Default End'] || '',
        });
      }));
      if (draftMode) {
        // Never touches the live roster — stored as a Pending draft for an admin to approve.
        const r = await api('submitRosterDraft', { weekStart, assignments, byName: managerName, note: (draftStatus && draftStatus.note) || '' });
        if (!r.success) throw new Error(r.error || 'Submit failed');
        setDirty(false);
        setDraftStatus({ weekStart, status: 'Pending', count: r.count, submittedAt: new Date().toISOString(), submittedByName: managerName, note: '' });
        flash(`Submitted for approval — ${r.count} shift${r.count === 1 ? '' : 's'}. It won't reach the team until an admin approves it.`);
      } else {
        const r = await api('saveSorterRoster', { assignments, status: 'Published', by: 'admin' });
        if (!r.success) throw new Error(r.error || 'Save failed');
        setDirty(false);
        // Resolve this week's nights-off requests against what was just published.
        try { await api('resolveInabilityWeek', { weekStart, assignments }); } catch (_) {}
        // Republishing a live week moves people on and off nights; the backend diffs the
        // publish and leaves each of them a notice. Say so here, because "published" alone
        // gives no clue that anyone was told anything.
        const told = r.notified
          ? ` ${r.notified} roster change${r.notified === 1 ? '' : 's'} sent to the ${new Set((r.notices || []).map(n => n.login)).size} people affected.`
          : '';
        // The backend closes any submitted draft for this week — publishing it IS the decision.
        if (r.draftsClosed) setPendingDrafts(p => p.filter(x => x.weekStart !== weekStart));
        flash(`Published — ${r.saved} shifts assigned, ${r.cleared} cleared.${told}`);
        setNoticeTick(t => t + 1);
        load();
      }
    } catch (e) { flash(e.message, false); } finally { setSaving(false); }
  }

  // Admin: approve a pending week (publishes it to the live roster) or reject with a reason.
  async function reviewDraft(weekStartOfDraft, resolution) {
    let note = '';
    if (resolution === 'reject') { note = window.prompt('Reason for rejecting this roster (the manager will see it):') || ''; if (note === '') return; }
    setReviewBusy(weekStartOfDraft);
    try {
      const r = await api(resolution === 'approve' ? 'approveRosterDraft' : 'rejectRosterDraft', { weekStart: weekStartOfDraft, note });
      if (!r.success) throw new Error(r.error || 'Failed');
      flash(resolution === 'approve' ? `Approved — ${r.saved} shifts published to the live roster.` : 'Rejected — the manager will see your note.');
      if (weekStartOfDraft === weekStart) load(); else setPendingDrafts(p => p.filter(x => x.weekStart !== weekStartOfDraft));
    } catch (e) { flash(e.message, false); } finally { setReviewBusy(''); }
  }

  // Admin: clear submissions off the queue without approving or sending back — kept on
  // record as Archived, and the manager gets no rejection note. One week, or all of them.
  async function archiveDrafts(weeks) {
    if (weeks.length > 1 && !window.confirm(`Archive all ${weeks.length} submissions? They stay on record; the live roster is not changed and Valeria is not told anything.`)) return;
    setReviewBusy(weeks.length > 1 ? '*' : weeks[0]);
    try {
      const r = await api('archiveRosterDraft', { weekStarts: weeks });
      if (!r.success) throw new Error(r.error || 'Failed');
      setPendingDrafts(p => p.filter(x => !weeks.includes(x.weekStart)));
      flash(`Archived ${r.archived} submission${r.archived === 1 ? '' : 's'} — live roster unchanged.`);
    } catch (e) { flash(e.message, false); } finally { setReviewBusy(''); }
  }

  // Admin: pull a submitted week into the grid to eyeball before approving.
  async function loadDraftIntoGrid(weekStartOfDraft) {
    if (weekStartOfDraft !== weekStart) { setWeekStart(weekStartOfDraft); }
    try {
      const d = await api('getRosterDraft', { weekStart: weekStartOfDraft });
      const draft = d && d.success ? d.draft : null;
      if (!draft || !Array.isArray(draft.assignments)) { flash('Could not load that draft.', false); return; }
      const dg = {};
      const wd = weekDates(weekStartOfDraft);
      draft.assignments.forEach(a => { if (a && a.login && wd.includes(a.date)) dg[a.date + '|' + a.slot] = { login: (a.login || '').toUpperCase(), name: a.name || '', start: a.start || '', end: a.end || '' }; });
      setGrid(dg); setDirty(false);
      flash(`Loaded ${Object.keys(dg).length} proposed shifts from ${draft.submittedByName || draft.submittedBy || 'the manager'} — review, then Approve.`);
    } catch (e) { flash(e.message, false); }
  }

  async function copyLastWeek() {
    try {
      const prev = new Date(weekStart + 'T00:00'); prev.setDate(prev.getDate() - 7);
      const p0 = isoLocal(prev);
      const pDays = weekDates(p0);
      const r = await api('getSorterRoster', { from: pDays[0], to: pDays[6] });
      if (!r.success) throw new Error(r.error || 'Could not load last week');
      const g = {};
      (r.roster || []).forEach(row => {
        const idx = pDays.indexOf(row.Date);
        if (idx >= 0) g[days[idx] + '|' + row.Slot] = { login: (row.Login || '').toUpperCase(), name: row.Name || '', start: row.Start || '', end: row.End || '' };
      });
      if (!Object.keys(g).length) { flash('Last week has no published roster to copy.', false); return; }
      setGrid(g); setDirty(true);
      flash(`Copied ${Object.keys(g).length} shifts from last week — review, then publish.`);
    } catch (e) { flash(e.message, false); }
  }

  // Loaded the wrong week (template / copy-last-week / a bad build) and want to start over.
  // Two ways out, deliberately separate:
  //   Undo changes — throw away what's on screen and reload what's actually saved.
  //   Clear week   — take the whole week out, on screen AND (if it was published) off the
  //                  live roster, so no sorter is left looking at shifts that aren't real.
  function revertWeek() {
    if (!dirty && !filledCount) { flash('Nothing to undo — this week is empty.'); return; }
    if (dirty && !window.confirm('Throw away your unsaved changes to this week and reload the saved roster?')) return;
    load();
    flash(savedCount ? `Reloaded the ${savedCount} saved shift${savedCount === 1 ? '' : 's'} for this week.` : 'Reloaded — nothing is saved for this week yet.');
  }

  async function clearWeek() {
    const live = !draftMode && savedCount > 0;
    if (!filledCount && !live) { flash('This week is already empty.'); return; }
    const msg = [
      `Remove ALL shifts for ${fmtD(days[0])} → ${fmtD(days[6])}?`,
      '',
      `• ${filledCount} shift${filledCount === 1 ? '' : 's'} on screen will be cleared.`,
      live
        ? `• ${savedCount} already published for this week will be deleted from the live roster — sorters will see the week as empty.`
        : '• Nothing is published for this week, so nothing changes for the team.',
      '',
      "Standing positions re-fill the grid next time you open the week.",
      live ? 'The week is snapshotted first — “Restore cleared week” brings it back.' : '',
    ].join('\n');
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      if (live) {
        // Server-side: snapshots the week to the undo store FIRST, then deletes it.
        const r = await api('clearSorterRosterWeek', { weekStart });
        if (!r.success) throw new Error(r.error || 'Could not clear the live roster');
        setSavedCount(0);
        setBackup({ clearedAt: new Date().toISOString(), count: r.cleared });
        flash(`Week cleared — ${r.cleared} shift${r.cleared === 1 ? '' : 's'} taken off the roster. “Restore cleared week” puts them back.`);
      } else {
        flash(`Week cleared. Build it again, then ${draftMode ? 'Submit for approval' : 'Publish'}.`);
      }
      setGrid({}); setSel(null); setSwapSrc(null); setClip(null); setDirty(false);
    } catch (e) { flash(e.message, false); } finally { setSaving(false); }
  }

  // Put back the week the Clear button took off the roster. The server refuses if the week
  // has been rebuilt since, so a restore can never silently bury a newer roster.
  async function restoreWeek() {
    if (!backup) return;
    const when = backup.clearedAt ? new Date(backup.clearedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'earlier';
    if (!window.confirm(`Put back the ${backup.count} shift${backup.count === 1 ? '' : 's'} cleared ${when}?\n\nThis restores the week exactly as it was on the roster.`)) return;
    setSaving(true);
    try {
      const r = await api('restoreSorterRosterWeek', { weekStart });
      if (!r.success) throw new Error(r.error || 'Could not restore');
      flash(`Restored — ${r.restored} shift${r.restored === 1 ? '' : 's'} back on the roster.`);
      load();
    } catch (e) { flash(e.message, false); } finally { setSaving(false); }
  }

  // Text the roster manager that next week hasn't come through yet. The message is shown
  // in full before anything is sent, and the server refuses if she has already submitted.
  async function remindManager() {
    const wk = (nextWeek && nextWeek.weekStart) || nextWeekStart;
    const last = nextWeek && nextWeek.lastReminder;
    const preview = `HPR: Hi Valeria, the night sort roster for the week starting ${fmtD(wk)} has not been submitted yet. Please build it in the roster app and send it through for approval. Thanks.`;
    const lastLine = last && last.sentAt ? `\n\nShe was already reminded ${new Date(last.sentAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}.` : '';
    if (!window.confirm(`Text Valeria this now?\n\n"${preview}"${lastLine}`)) return;
    setRemindBusy(true);
    try {
      const r = await api('remindRosterManager', { weekStart: wk });
      if (!r.success) throw new Error(r.error || 'Could not send it');
      flash(`Reminder texted to ${r.sentTo}.`);
      const nw = await api('getRosterReminder', { weekStart: wk });
      setNextWeek(nw && nw.success ? nw : null);
    } catch (e) { flash(e.message, false); } finally { setRemindBusy(false); }
  }

  async function saveTemplate() {
    const name = window.prompt('Template name (e.g. "Standard week", "Peak / Xmas"):');
    if (!name) return;
    const entries = [];
    Object.entries(grid).forEach(([key, a]) => {
      if (!a || !a.login) return;
      const [date, slot] = key.split('|');
      const dow = days.indexOf(date);
      if (dow >= 0) entries.push({ dow, slot, login: a.login, name: a.name || personName(a.login), start: a.start || '', end: a.end || '' });
    });
    const r = await api('saveSorterRosterTemplate', { name, entries });
    if (r.success) { flash(`Template "${name}" saved (${r.count} shifts).`); const t = await api('listSorterRosterTemplates', {}); if (t.success) setTemplates(t.templates); }
    else flash(r.error || 'Could not save template', false);
  }

  function applyTemplate() {
    const t = templates.find(x => x.id === tplSel);
    if (!t) return;
    const g = {};
    (t.entries || []).forEach(e => { if (e.dow >= 0 && e.dow <= 6 && e.slot) g[days[e.dow] + '|' + e.slot] = { login: (e.login || '').toUpperCase(), name: e.name || '', start: e.start || '', end: e.end || '' }; });
    setGrid(g); setDirty(true);
    flash(`Template "${t.name}" applied to this week — review, then publish.`);
  }

  async function deleteTemplate() {
    const t = templates.find(x => x.id === tplSel);
    if (!t || !window.confirm(`Delete template "${t.name}"?`)) return;
    await api('deleteSorterRosterTemplate', { id: t.id });
    setTplSel(''); const r = await api('listSorterRosterTemplates', {}); if (r.success) setTemplates(r.templates);
  }

  async function resolveOffer(o, resolution) {
    const takenBy = offerPick[o.id] || '';
    const r = await api('resolveSorterShiftOffer', { id: o.id, resolution, takenBy });
    if (!r.success) { flash(r.error || 'Failed', false); return; }
    // The sorter is told either way — including a decline, which is the outcome they most
    // need to hear, since silence after asking to be let off reads as "granted".
    const told = r.notified ? ' They have been told in their portal.' : '';
    flash((resolution === 'accept' ? (takenBy ? `Shift handed to ${personName(takenBy)}.` : 'Offer accepted — the slot is now empty for that night.') : 'Offer declined — the shift stays with them.') + told);
    window.dispatchEvent(new Event('hp-sorter-offers-changed'));
    load();
  }

  const th = { padding: '6px 8px', fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', whiteSpace: 'nowrap' };
  const selCell = sel ? grid[sel] : null;
  const selSlotName = sel ? sel.split('|')[1] : '';
  const selSlot = sel ? slotByName(selSlotName) : null;
  const ghost = { ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '7px 11px', fontSize: '0.78rem' };
  const menuItem = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderRadius: 7, padding: '8px 10px', fontSize: '0.82rem', fontWeight: 600, color: 'var(--hp-text)', cursor: 'pointer', fontFamily: 'var(--font-body)' };

  return (
    <>
    {!draftMode && <FinishTimeCard testMode={testMode} />}

    {/* Manager (draftMode): status of the week they submitted. */}
    {draftMode && draftStatus && (draftStatus.status === 'Pending' || draftStatus.status === 'Rejected' || draftStatus.status === 'Approved' || draftStatus.status === 'Superseded') && (
      <div style={{ ...card, borderColor: draftStatus.status === 'Rejected' ? 'var(--hp-red, #EF4444)' : draftStatus.status === 'Approved' ? 'var(--hp-green, #22C55E)' : 'var(--hp-amber, #F59E0B)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {draftStatus.status === 'Pending' && <>{warn('Awaiting approval')}<span style={{ fontSize: '0.86rem' }}>This week is submitted and waiting for an admin to approve it. You can keep editing and re-submit.</span></>}
          {(draftStatus.status === 'Approved' || draftStatus.status === 'Superseded') && <>{ok('Approved')}<span style={{ fontSize: '0.86rem' }}>This week has been approved and published to the team.</span></>}
          {draftStatus.status === 'Rejected' && <><span style={pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)')}><X size={12} />Sent back</span><span style={{ fontSize: '0.86rem' }}>An admin sent this week back{draftStatus.reviewNote ? <>: “<strong>{draftStatus.reviewNote}</strong>”</> : '.'} Adjust and re-submit.</span></>}
        </div>
      </div>
    )}

    {/* Admin: next week still not submitted → one-tap SMS chase to the roster manager. */}
    {!draftMode && nextWeek && !nextWeek.submitted && (
      <div style={{ ...card, borderColor: 'var(--hp-amber, #F59E0B)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <AlertTriangle size={18} color="var(--hp-amber, #F59E0B)" />
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Next week ({fmtD(nextWeek.weekStart)}) hasn't been submitted</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
              {nextWeek.draftStatus === 'Rejected'
                ? 'You sent it back — Valeria hasn\u2019t re-submitted it yet.'
                : 'Nothing from Valeria yet, so there\u2019s nothing for you to approve.'}
              {nextWeek.lastReminder && nextWeek.lastReminder.sentAt
                ? ` · Reminded ${new Date(nextWeek.lastReminder.sentAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                : ''}
            </div>
          </div>
          <button onClick={remindManager} disabled={remindBusy} style={{ ...btn('var(--hp-gold)'), padding: '8px 14px', fontSize: '0.82rem' }}>
            {remindBusy ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Megaphone size={14} />}
            {nextWeek.lastReminder ? 'Remind again' : 'Text Valeria a reminder'}
          </button>
        </div>
      </div>
    )}

    {/* Admin (full view): weeks a roster manager has submitted, awaiting approval. */}
    {!draftMode && pendingDrafts.length > 0 && (
      <div style={{ ...card, borderColor: 'var(--hp-gold)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <ClipboardCheck size={18} color="var(--hp-gold)" />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Rosters awaiting approval</h2>
          <span style={pill('rgba(201,168,76,0.14)', 'var(--hp-gold)')}>{pendingDrafts.length}</span>
          {pendingDrafts.length > 1 && (
            <button onClick={() => archiveDrafts(pendingDrafts.map(x => x.weekStart))} disabled={!!reviewBusy} title="Clear them all off this list — kept on record, live roster unchanged" style={{ ...ghost, padding: '6px 11px', marginLeft: 'auto' }}><Archive size={13} /> Archive all</button>
          )}
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 6 }}>Publishing a week from the grid clears its submission automatically. <strong>Archive</strong> just takes one off this list — nothing is sent to Valeria.</div>
        {pendingDrafts.map(d => (
          <div key={d.weekStart} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid var(--hp-border)', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>Week of {fmtD(d.weekStart)} · {d.count} shift{d.count === 1 ? '' : 's'}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>Submitted by {d.submittedByName || d.submittedBy || '—'}{d.note ? <> · “{d.note}”</> : ''}</div>
            </div>
            <button onClick={() => loadDraftIntoGrid(d.weekStart)} style={{ ...ghost, padding: '7px 11px' }}><Eye size={13} /> Review in grid</button>
            <button onClick={() => reviewDraft(d.weekStart, 'approve')} disabled={reviewBusy === d.weekStart} style={{ ...btn('var(--hp-green, #22C55E)'), padding: '7px 12px', fontSize: '0.8rem' }}>{reviewBusy === d.weekStart ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Check size={13} />} Approve &amp; publish</button>
            <button onClick={() => reviewDraft(d.weekStart, 'reject')} disabled={reviewBusy === d.weekStart} style={{ ...ghost, color: 'var(--hp-red, #EF4444)' }}><X size={13} /> Send back</button>
            <button onClick={() => archiveDrafts([d.weekStart])} disabled={reviewBusy === d.weekStart} title="Take off this list — kept on record, live roster unchanged, Valeria not told" style={{ ...ghost, padding: '7px 11px' }}><Archive size={13} /> Archive</button>
          </div>
        ))}
      </div>
    )}

    {!draftMode && offers.length > 0 && (
      <div id="sorter-shift-offers" style={{ ...card, borderColor: 'var(--hp-amber, #F59E0B)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Inbox size={18} color="var(--hp-amber, #F59E0B)" />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Shift offers</h2>
          <span style={pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)')}>{offers.length} open</span>
        </div>
        {offers.map(o => (
          <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid var(--hp-border)', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{o.name || o.login} can't work {fmtD(o.date)} · {o.slot}</div>
              {o.reason && <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>“{o.reason}”</div>}
            </div>
            <select style={{ ...input, width: 190, marginBottom: 0, padding: '7px 10px', fontSize: '0.82rem' }} value={offerPick[o.id] || ''} onChange={e => setOfferPick(p => ({ ...p, [o.id]: e.target.value }))}>
              <option value="">Leave slot empty</option>
              {people.filter(p => (p.Login || '').toUpperCase() !== o.login).map(p => <option key={p.Login} value={(p.Login || '').toUpperCase()}>Give to {p.Name || p.Login}</option>)}
            </select>
            <button onClick={() => resolveOffer(o, 'accept')} style={{ ...btn('var(--hp-green, #22C55E)'), padding: '7px 12px', fontSize: '0.8rem' }}><Check size={13} /> Accept</button>
            <button onClick={() => resolveOffer(o, 'decline')} style={{ ...ghost, color: 'var(--hp-red, #EF4444)' }}><X size={13} /> Decline</button>
          </div>
        ))}
      </div>
    )}

    {/* Nights-off requests for this week — best-endeavours; they may still be rostered. */}
    <div style={{ ...card, borderColor: inability.length ? 'var(--hp-amber, #F59E0B)' : 'var(--hp-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <AlertTriangle size={18} color="var(--hp-amber, #F59E0B)" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Nights off requested</h2>
        {inability.length > 0 && <span style={pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)')}>{inability.length} this week</span>}
      </div>
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.8rem', margin: '0 0 12px' }}>Sorters can ask to have nights free. We do our best — but a request doesn't remove them from the roster, and they may still be rostered if needed. A ⚑ shows on the grid where someone rostered has asked for that night off.</p>
      {inability.length > 0 ? inability.map(x => (
        <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--hp-border)', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: '0.86rem', minWidth: 130 }}>{x.name || personName(x.login)}</span>
          <span style={pill('rgba(245,158,11,0.12)', 'var(--hp-amber, #F59E0B)')}>{fmtDayDate(x.date)}</span>
          {inabStatus(x.status)}
          {x.reason && <span style={{ flex: 1, fontSize: '0.8rem', color: 'var(--hp-dim)', minWidth: 120 }}>“{x.reason}”{x.reviewNote ? <span style={{ color: 'var(--hp-red, #EF4444)' }}> · office: “{x.reviewNote}”</span> : ''}</span>}
          <span style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            {x.status !== 'NotMet' && <button onClick={() => cantAccommodate(x.id)} title="Tell the sorter we couldn't free them up" style={{ ...ghost, color: 'var(--hp-amber, #F59E0B)', padding: '5px 9px' }}>Can't accommodate</button>}
            <button onClick={() => cancelInabilityReq(x.id)} title="Remove this request" style={{ ...ghost, color: 'var(--hp-red, #EF4444)', padding: '5px 9px' }}><X size={12} /> Remove</button>
          </span>
        </div>
      )) : <p style={{ color: 'var(--hp-dim)', fontSize: '0.82rem', margin: 0 }}>No nights-off requests for this week.</p>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--hp-border)' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Log a request</span>
        <select style={{ ...input, width: 180, marginBottom: 0, padding: '7px 10px', fontSize: '0.82rem' }} value={inabForm.login} onChange={e => setInabForm(f => ({ ...f, login: e.target.value }))}>
          <option value="">Sorter…</option>
          {people.map(p => <option key={p.Login} value={(p.Login || '').toUpperCase()}>{p.Name || p.Login}</option>)}
        </select>
        <input type="date" style={{ ...input, width: 150, marginBottom: 0, padding: '7px 10px', fontSize: '0.82rem' }} value={inabForm.date} onChange={e => setInabForm(f => ({ ...f, date: e.target.value }))} />
        <input style={{ ...input, flex: 1, minWidth: 140, marginBottom: 0, padding: '7px 10px', fontSize: '0.82rem' }} placeholder="Reason (optional)" value={inabForm.reason} onChange={e => setInabForm(f => ({ ...f, reason: e.target.value }))} />
        <button onClick={logInability} disabled={!inabForm.login || !inabForm.date} style={btn(inabForm.login && inabForm.date ? 'var(--hp-amber, #F59E0B)' : 'rgba(245,158,11,0.4)')}><CheckCircle2 size={14} /> Log</button>
      </div>
    </div>

    {/* Standing positions — a sorter permanently tied to a slot; auto-fills every night. */}
    {!draftMode ? (
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <MapPin size={18} color="var(--hp-gold)" />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Standing positions</h2>
        </div>
        <p style={{ color: 'var(--hp-dim)', fontSize: '0.8rem', margin: '0 0 12px' }}>A sorter permanently assigned to a slot — they auto-fill every night's roster. If they're out a night, drag a replacement over that shift; the rule keeps them in place for the rest of the week.</p>
        {standing.length > 0 && standing.map(a => (
          <div key={a.slot} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--hp-border)' }}>
            <span style={{ fontWeight: 700, fontSize: '0.86rem', minWidth: 150 }}>{a.slot}</span>
            <span style={{ color: 'var(--hp-gold)' }}>→</span>
            <span style={{ flex: 1, fontSize: '0.86rem' }}>{a.name || a.login} <span style={{ color: 'var(--hp-dim)', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.76rem' }}>{a.login}</span></span>
            <button onClick={() => setStandingPosition(a.slot, '')} style={{ ...ghost, color: 'var(--hp-red, #EF4444)', padding: '5px 9px' }}><Trash2 size={12} /> Remove</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--hp-border)' }}>
          <select style={{ ...input, width: 210, marginBottom: 0, padding: '8px 10px', fontSize: '0.84rem' }} value={stdForm.slot} onChange={e => setStdForm(f => ({ ...f, slot: e.target.value }))}>
            <option value="">Slot…</option>
            {slots.map(sl => <option key={sl.Slot} value={sl.Slot}>{sl.Slot}</option>)}
          </select>
          <span style={{ color: 'var(--hp-dim)' }}>→</span>
          <select style={{ ...input, width: 210, marginBottom: 0, padding: '8px 10px', fontSize: '0.84rem' }} value={stdForm.login} onChange={e => setStdForm(f => ({ ...f, login: e.target.value }))}>
            <option value="">Sorter…</option>
            {people.map(p => <option key={p.Login} value={(p.Login || '').toUpperCase()}>{p.Name || p.Login}</option>)}
          </select>
          <button onClick={() => setStandingPosition(stdForm.slot, stdForm.login)} disabled={!stdForm.slot || !stdForm.login} style={btn(stdForm.slot && stdForm.login ? 'var(--hp-gold)' : 'rgba(201,168,76,0.4)')}><CheckCircle2 size={14} /> Set standing</button>
        </div>
      </div>
    ) : standing.length > 0 && (
      <div style={{ ...card, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: '0.82rem' }}>
          <MapPin size={15} color="var(--hp-gold)" />
          <strong>Standing positions:</strong>
          {standing.map(a => <span key={a.slot} style={pill('rgba(201,168,76,0.12)', 'var(--hp-text)')}>{a.name || a.login} → {a.slot}</span>)}
          <span style={{ color: 'var(--hp-dim)' }}>auto-filled each night — drag a replacement over any night they're out.</span>
        </div>
      </div>
    )}

    {/* Operating nights — which weekdays the sorter week runs (Sun–Thu; Fri in peak). Admin only. */}
    {!draftMode && (
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
          <CalendarDays size={18} color="var(--hp-gold)" />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Operating nights</h2>
          {!opForm && <button onClick={() => setOpForm({ days: [...(operating.days || [])], peakDays: [...(operating.peakDays || [])], peakFrom: operating.peakFrom || '', peakTo: operating.peakTo || '' })} style={{ ...ghost, marginLeft: 'auto' }}><Pencil size={13} /> Edit</button>}
        </div>
        {!opForm ? (
          <p style={{ color: 'var(--hp-dim)', fontSize: '0.82rem', margin: 0 }}>
            Standard week: <strong style={{ color: 'var(--hp-text)' }}>{(operating.days || []).map(x => DOW[x]).join(', ') || 'none'}</strong>. {operating.peakFrom && operating.peakTo ? <>Peak <strong style={{ color: 'var(--hp-text)' }}>{fmtD(operating.peakFrom)}–{fmtD(operating.peakTo)}</strong> adds {(operating.peakDays || []).map(x => DOW[x]).join(', ')}.</> : 'No peak window set — Fri & Sat are no-shift.'} No-shift nights are greyed in the grid and never auto-fill.
          </p>
        ) : (
          <div>
            <label style={label}>Standard nights (every week)</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {DOW.map((d, i) => <button key={i} onClick={() => toggleDow('days', i)} style={{ ...ghost, padding: '6px 11px', color: opForm.days.includes(i) ? 'var(--hp-black)' : 'var(--hp-dim)', background: opForm.days.includes(i) ? 'var(--hp-gold)' : 'transparent', border: `1px solid ${opForm.days.includes(i) ? 'var(--hp-gold)' : 'var(--hp-border)'}`, fontWeight: 700 }}>{d}</button>)}
            </div>
            <label style={label}>Peak — extra nights, only within the window below</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {DOW.map((d, i) => <button key={i} onClick={() => toggleDow('peakDays', i)} style={{ ...ghost, padding: '6px 11px', color: opForm.peakDays.includes(i) ? 'var(--hp-black)' : 'var(--hp-dim)', background: opForm.peakDays.includes(i) ? 'var(--hp-amber, #F59E0B)' : 'transparent', border: `1px solid ${opForm.peakDays.includes(i) ? 'var(--hp-amber, #F59E0B)' : 'var(--hp-border)'}`, fontWeight: 700 }}>{d}</button>)}
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div><label style={label}>Peak from</label><input type="date" style={{ ...input, width: 160, marginBottom: 0 }} value={opForm.peakFrom} onChange={e => setOpForm(f => ({ ...f, peakFrom: e.target.value }))} /></div>
              <div><label style={label}>Peak to</label><input type="date" style={{ ...input, width: 160, marginBottom: 0 }} value={opForm.peakTo} onChange={e => setOpForm(f => ({ ...f, peakTo: e.target.value }))} /></div>
              <button onClick={saveOperating} style={btn('var(--hp-gold)')}><Save size={14} /> Save</button>
              <button onClick={() => setOpForm(null)} style={{ ...ghost }}>Cancel</button>
            </div>
            <p style={{ color: 'var(--hp-dim)', fontSize: '0.72rem', margin: '10px 0 0' }}>Peak nights only operate when their date falls between “Peak from” and “Peak to”. Leave the peak dates blank for a normal Sun–Thu week.</p>
          </div>
        )}
      </div>
    )}

    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <CalendarDays size={18} color="var(--hp-gold)" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Weekly roster</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
          <button onClick={() => shiftWeek(-1)} style={{ ...ghost, padding: '6px 8px' }}><ChevronLeft size={14} /></button>
          <span style={{ fontWeight: 700, fontSize: '0.82rem', minWidth: 168, textAlign: 'center' }}>{fmtD(days[0])} → {fmtD(days[6])} {days[6].slice(0, 4)}</span>
          <button onClick={() => shiftWeek(1)} style={{ ...ghost, padding: '6px 8px' }}><ChevronRight size={14} /></button>
        </div>
        <button onClick={publish} disabled={saving || loading} title={draftMode ? 'Submit this week for an admin to approve' : 'Publish to the live roster'} style={{ ...btn(dirty ? 'var(--hp-gold)' : 'rgba(201,168,76,0.45)'), marginLeft: 'auto' }}>{saving ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Send size={15} />} {draftMode ? 'Submit' : 'Publish'} {filledCount} shift{filledCount === 1 ? '' : 's'}{draftMode ? ' for approval' : ''}{dirty ? ' •' : ''}</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={copyLastWeek} style={ghost}><Copy size={13} /> Copy last week</button>
        {!draftMode && <button onClick={saveTemplate} style={ghost}><Save size={13} /> Save as template</button>}
        {templates.length > 0 && (<>
          <select style={{ ...input, width: 180, marginBottom: 0, padding: '7px 10px', fontSize: '0.8rem' }} value={tplSel} onChange={e => setTplSel(e.target.value)}>
            <option value="">Templates…</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button onClick={applyTemplate} disabled={!tplSel} style={ghost}><CheckCircle2 size={13} /> Apply</button>
          {!draftMode && <button onClick={deleteTemplate} disabled={!tplSel} style={{ ...ghost, color: 'var(--hp-red, #EF4444)' }}><Trash2 size={13} /></button>}
        </>)}
        <button onClick={revertWeek} disabled={saving || loading} title="Throw away unsaved changes and reload the saved roster for this week" style={ghost}><RefreshCw size={13} /> Undo changes</button>
        {!draftMode && backup && backup.count > 0 && savedCount === 0 && (
          <button onClick={restoreWeek} disabled={saving || loading} title={`Put back the ${backup.count} shifts cleared from this week`} style={{ ...ghost, color: 'var(--hp-green, #22C55E)', borderColor: 'var(--hp-green, #22C55E)' }}><RefreshCw size={13} /> Restore cleared week ({backup.count})</button>
        )}
        <button onClick={clearWeek} disabled={saving || loading} title={savedCount && !draftMode ? 'Empty this week on screen and take it off the live roster' : 'Empty every position for this week'} style={{ ...ghost, color: 'var(--hp-red, #EF4444)' }}><Trash2 size={13} /> Clear week{savedCount > 0 && !draftMode ? ` (${savedCount} live)` : ''}</button>
        {swapSrc && <span style={pill('rgba(201,168,76,0.14)', 'var(--hp-gold)')}><ArrowLeftRight size={12} /> Swap: now click the other shift (Esc to cancel)</span>}
        {clip && <span style={pill('rgba(59,130,246,0.14)', 'var(--hp-blue, #3B82F6)')}><Copy size={12} /> Copied {clip.name} — click empty positions to drop them in (Esc to clear)</span>}
      </div>


      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: '0.66rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Areas</span>
        {SLOT_CATEGORIES.map(c => (
          <span key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: c.colour, display: 'inline-block' }} />{c.label}
          </span>
        ))}
      </div>

      <div style={{ marginBottom: 12 }}>
        <button onClick={() => setShowColours(v => !v)} style={{ ...ghost, fontSize: '0.74rem', padding: '6px 11px' }}>{showColours ? '▾' : '▸'} Member colours</button>
        {showColours && (
          <div style={{ ...card, marginTop: 8, marginBottom: 0 }}>
            <p style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', margin: '0 0 12px' }}>Everyone has their own colour and a <strong>preferred name</strong> shown on the pills &amp; roster. Set <strong>♀</strong>/<strong>♂</strong> to pick a feminine or masculine palette (colour re-picks automatically); <strong>–</strong> is neutral. Click the swatch for an exact colour. New sorters get a colour on their own.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(310px, 100%), 1fr))', gap: 8 }}>
              {people.map(p => {
                const login = (p.Login || '').toUpperCase();
                const g = genderOf(login), col = colourOf(login);
                return (
                  <div key={login} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', border: '1px solid var(--hp-border)', borderRadius: 8 }}>
                    <input type="color" value={col || '#64748b'} onChange={e => setColour(login, e.target.value)} title="Click to pick this person's colour" style={{ width: 26, height: 26, padding: 0, border: '1px solid var(--hp-border)', borderRadius: 6, background: 'transparent', cursor: 'pointer', flex: '0 0 auto' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.66rem', color: 'var(--hp-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.Name || login}>{p.Name || login}</div>
                      <input defaultValue={nickOf(login)} placeholder="Preferred name…" onBlur={e => { if ((e.target.value || '').trim() !== nickOf(login)) setNick(login, e.target.value); }} onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} style={{ width: '100%', background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 6, color: 'var(--hp-text)', fontSize: '0.8rem', padding: '4px 7px', boxSizing: 'border-box', fontFamily: 'var(--font-body)' }} />
                    </div>
                    {[['F', '♀'], ['M', '♂'], ['', '–']].map(([val, lbl]) => (
                      <button key={val} onClick={() => setGender(login, val)} title={val === 'F' ? 'Feminine palette' : val === 'M' ? 'Masculine palette' : 'Neutral'} style={{ width: 26, height: 26, borderRadius: 6, cursor: 'pointer', fontFamily: 'var(--font-body)', border: `1px solid ${g === val ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: g === val ? 'rgba(201,168,76,0.15)' : 'transparent', color: g === val ? 'var(--hp-gold)' : 'var(--hp-dim)', fontWeight: 700, flex: '0 0 auto' }}>{lbl}</button>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <button onClick={() => setShowTotals(v => !v)} style={{ ...ghost, fontSize: '0.74rem', padding: '6px 11px' }}>{showTotals ? '▾' : '▸'} Week totals — {filledCount} shift{filledCount === 1 ? '' : 's'} · {Object.keys(weekTally).length} {Object.keys(weekTally).length === 1 ? 'person' : 'people'}</button>
        {showTotals && (
          <div style={{ ...card, marginTop: 8, marginBottom: 0 }}>
            <p style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', margin: '0 0 12px' }}>Shifts per person for {fmtD(days[0])} → {fmtD(days[6])} (this week as it stands on the grid).</p>
            {Object.keys(weekTally).length === 0 ? (
              <p style={{ color: 'var(--hp-dim)', fontSize: '0.82rem', margin: 0 }}>No shifts assigned yet.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: 6 }}>
                {Object.entries(weekTally).map(([login, n]) => ({ login, n, name: nickOf(login) || personName(login) }))
                  .sort((a, b) => b.n - a.n || a.name.localeCompare(b.name))
                  .map(row => (
                    <div key={row.login} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', border: '1px solid var(--hp-border)', borderRadius: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: colourOf(row.login) || 'var(--hp-border)', flex: '0 0 auto' }} />
                      <span style={{ flex: 1, fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
                      <span style={pill('rgba(201,168,76,0.14)', 'var(--hp-gold)')}>{row.n} shift{row.n === 1 ? '' : 's'}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      {toast && <div style={{ ...pill(toast.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', toast.ok ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)'), padding: '8px 12px', marginBottom: 12 }}>{toast.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {toast.text}</div>}

      {dayEdit && (
        <div style={{ background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-gold)', borderRadius: 10, padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={label}>{DOW[days.indexOf(dayEdit.date)]} {fmtD(dayEdit.date)} — sorter shift times</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="time" style={{ ...input, width: 110, marginBottom: 0 }} value={dayEdit.start} onChange={e => setDayEdit(x => ({ ...x, start: e.target.value }))} />
                <span style={{ color: 'var(--hp-dim)' }}>–</span>
                <input type="time" style={{ ...input, width: 110, marginBottom: 0 }} value={dayEdit.end} onChange={e => setDayEdit(x => ({ ...x, end: e.target.value }))} />
              </div>
            </div>
            <button onClick={applyDayTimes} style={btn('var(--hp-gold)')}><Check size={14} /> Apply to the whole night</button>
            {!draftMode && dayEdit && <button onClick={saveWeekdayDefault} title={`Remember these times as the default for every ${DOW[new Date(dayEdit.date + 'T00:00').getDay()]}`} style={{ ...btn('transparent'), color: 'var(--hp-gold)', border: '1px solid var(--hp-gold)' }}><Save size={14} /> Save as every {DOW[new Date(dayEdit.date + 'T00:00').getDay()]} default</button>}
            <button onClick={() => setDayEdit(null)} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}>Cancel</button>
          </div>
          <p style={{ color: 'var(--hp-dim)', fontSize: '0.72rem', margin: '8px 0 0' }}>You set the SORTER times; runner positions are set 1 hour later automatically (house rule), and names you add to this night afterwards get the right times too. {draftMode ? 'Submit for approval to send it in.' : 'Use “Save as … default” to remember these times for that weekday every week.'}</p>
        </div>
      )}

      {sel && !tileMenu && (
        <div style={{ background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-gold)', borderRadius: 10, padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 150 }}>
              <label style={label}>{DOW[days.indexOf(sel.split('|')[0])] || ''} {fmtD(sel.split('|')[0])} · {selSlotName}</label>
              <select autoFocus style={{ ...input, marginBottom: 0 }} value={(selCell && selCell.login) || ''}
                onChange={e => {
                  const login = e.target.value;
                  if (!login) { setGrid(g => { const n = { ...g }; delete n[sel]; return n; }); setDirty(true); }
                  else { const t = newAssignTimes(sel); setCell(sel, { ...(selCell || {}), login, name: personName(login), start: (selCell && selCell.start) || t.start, end: (selCell && selCell.end) || t.end }); }
                }}>
                <option value="">— unassigned —</option>
                {people.map(s => <option key={s.Login} value={(s.Login || '').toUpperCase()}>{s.Name || s.Login}</option>)}
              </select>
            </div>
            <div><label style={label}>Start</label><input type="time" style={{ ...input, width: 110, marginBottom: 0 }} value={(selCell && selCell.start) || (selSlot && selSlot['Default Start']) || ''} onChange={e => selCell && selCell.login && setCell(sel, { ...selCell, start: e.target.value })} disabled={!(selCell && selCell.login)} /></div>
            <div><label style={label}>End</label><input type="time" style={{ ...input, width: 110, marginBottom: 0 }} value={(selCell && selCell.end) || (selSlot && selSlot['Default End']) || ''} onChange={e => selCell && selCell.login && setCell(sel, { ...selCell, end: e.target.value })} disabled={!(selCell && selCell.login)} /></div>
            <button onClick={() => copyCell(sel)} disabled={!(selCell && selCell.login)} style={ghost} title="Copy this driver (⌘C)"><Copy size={13} /> Copy</button>
            <button onClick={() => pasteCell(sel)} disabled={!(clip && clip.login)} style={ghost} title={clip ? `Paste ${clip.name} here (⌘V)` : 'Copy a driver first'}><ClipboardPaste size={13} /> Paste{clip ? ` ${clip.name.split(' ')[0]}` : ''}</button>
            <button onClick={() => { setSwapSrc(sel); setSel(null); }} disabled={!(selCell && selCell.login)} style={ghost}><ArrowLeftRight size={13} /> Swap with…</button>
            <button onClick={() => deleteCell(sel)} disabled={!(selCell && selCell.login)} style={{ ...ghost, color: 'var(--hp-red, #EF4444)' }} title="Remove this driver (Delete)"><Trash2 size={13} /> Remove</button>
            <button onClick={() => setSel(null)} style={ghost}>Done</button>
          </div>
          <p style={{ color: selSlot && isRunnerSlot(selSlot) ? 'var(--hp-amber, #F59E0B)' : 'var(--hp-dim)', fontSize: '0.72rem', margin: '8px 0 0' }}>
            {selSlot && isRunnerSlot(selSlot)
              ? 'RUNNER RULE: this position always starts and finishes exactly 1 hour after the sorters — the cell goes amber if it drifts.'
              : 'Times start at the slot default — change them here for a one-off early/late start. Nothing is live until you Publish.'}
          </p>
        </div>
      )}

      {tileMenu && (() => {
        const a = grid[tileMenu.key];
        const who = a ? (a.name || personName(a.login)) : '';
        const left = Math.max(8, Math.min(tileMenu.x, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 190));
        return (
          <>
            <div onClick={() => { setTileMenu(null); setSel(null); }} style={{ position: 'fixed', inset: 0, zIndex: 60 }} />
            <div style={{ position: 'fixed', left, top: tileMenu.y, zIndex: 61, background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 10, boxShadow: '0 12px 34px rgba(0,0,0,0.28)', padding: 6, minWidth: 174 }}>
              <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', padding: '4px 10px 6px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{who}</div>
              <button onClick={() => { copyCell(tileMenu.key); setTileMenu(null); setSel(null); }} style={menuItem} onMouseEnter={e => e.currentTarget.style.background = 'var(--hp-surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}><Copy size={15} /> Copy</button>
              <button onClick={() => { deleteCell(tileMenu.key); setTileMenu(null); setSel(null); }} style={{ ...menuItem, color: 'var(--hp-red, #EF4444)' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.10)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}><Trash2 size={15} /> Remove</button>
            </div>
          </>
        );
      })()}

      {loading ? (
        <div style={{ color: 'var(--hp-dim)', padding: 20 }}><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>
      ) : (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          {/* Static people rail — stays put on the left while the week scrolls left↔right. */}
          <aside style={{ flex: '0 0 auto', width: 172, position: 'sticky', top: 8, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 24px)', overflowY: 'auto', background: 'var(--hp-surface)', border: '1px dashed var(--hp-border)', borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, lineHeight: 1.3 }}>People — drag onto a shift, or click then click positions to Paste</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {/* Active first, inactive sink to the bottom (stable — keeps each group's order). */}
              {[...people].sort((a, b) => ((a.Active || 'Y').toUpperCase() === 'N' ? 1 : 0) - ((b.Active || 'Y').toUpperCase() === 'N' ? 1 : 0)).map(p => {
                const login = (p.Login || '').toUpperCase();
                const inactive = (p.Active || 'Y').toUpperCase() === 'N';
                const col = colourOf(login);
                const picked = clip && clip.login === login;
                return (
                  <span key={login} draggable
                    onDragStart={e => { e.dataTransfer.setData('text/plain', 'L:' + login); e.dataTransfer.effectAllowed = 'copy'; }}
                    onClick={() => { setClip({ login, name: personName(login) || p.Name || login }); flash(`Copied ${personName(login) || p.Name || login} — click empty positions to drop them in (Esc to clear).`); }}
                    title={inactive ? `${p.Name || login} — inactive (not signed in to the portal yet); still rosterable` : `${p.Name || login} — drag onto a shift, or click then click positions to paste`}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'grab', border: `1px ${inactive ? 'dashed' : 'solid'} ${picked ? 'var(--hp-blue, #3B82F6)' : (col || 'var(--hp-border)')}`, background: picked ? 'rgba(59,130,246,0.16)' : (col ? col + '22' : 'rgba(201,168,76,0.12)'), color: inactive ? 'var(--hp-dim)' : 'var(--hp-text)', borderRadius: 8, padding: '6px 9px', fontSize: '0.75rem', fontWeight: 700, userSelect: 'none', opacity: inactive ? 0.55 : 1, boxShadow: picked ? '0 0 0 2px rgba(59,130,246,0.25)' : 'none' }}>
                    {col && <span style={{ width: 8, height: 8, borderRadius: '50%', background: col, display: 'inline-block', flex: '0 0 auto' }} />}
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pillName(login, p.Name)}</span>
                  </span>
                );
              })}
            </div>
          </aside>
          <div style={{ overflowX: 'auto', flex: 1, minWidth: 0 }} onKeyDown={e => {
          if (e.key === 'Escape') { setSwapSrc(null); setSel(null); setClip(null); setTileMenu(null); return; }
          const tag = ((e.target && e.target.tagName) || '').toLowerCase();
          if (tag === 'input' || tag === 'select' || tag === 'textarea') return; // don't hijack typing
          if (!sel) return;
          const meta = e.metaKey || e.ctrlKey;
          if (meta && (e.key === 'c' || e.key === 'C')) { copyCell(sel); e.preventDefault(); }
          else if (meta && (e.key === 'v' || e.key === 'V')) { pasteCell(sel); e.preventDefault(); }
          else if (e.key === 'Delete' || e.key === 'Backspace') { deleteCell(sel); e.preventDefault(); }
        }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', minWidth: 150 }}>Slot</th>
                {days.map((d, i) => {
                  const op = isOperatingDay(d, operating);
                  return (
                  <th key={d} style={{ ...th, opacity: op ? 1 : 0.5 }}>
                    {DOW[i]}<br />
                    <span style={{ color: 'var(--hp-dim)', fontWeight: 400 }}>{fmtD(d)}</span><br />
                    {op ? (
                      <button onClick={() => openDayEdit(d)} title="AP changed the times? Click to move every shift on this night"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '1px 3px', color: 'var(--hp-gold)', fontWeight: 600, fontSize: '0.66rem', fontFamily: 'var(--font-body)', display: 'inline-flex', alignItems: 'center', gap: 3, borderRadius: 4, borderBottom: '1px dashed rgba(201,168,76,0.5)' }}>
                        {dayTimes[d] || 'set times'} <Pencil size={9} />
                      </button>
                    ) : (
                      <span style={{ color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>No shift</span>
                    )}
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {slots.map((sl, si) => {
                const grp = slotGroup(sl);
                const prevGrp = si > 0 ? slotGroup(slots[si - 1]) : null;
                const nextGrp = si < slots.length - 1 ? slotGroup(slots[si + 1]) : null;
                const firstInGroup = !prevGrp || prevGrp.key !== grp.key;
                const lastInGroup = !nextGrp || nextGrp.key !== grp.key;
                const ncols = days.length + 1;
                const rows = [];
                if (firstInGroup) {
                  // Space divider between areas (skip above the very first area).
                  if (si > 0) rows.push(
                    <tr key={'sp-' + grp.key + si} aria-hidden="true"><td colSpan={ncols} style={{ height: 16, border: 'none', background: 'transparent', padding: 0 }} /></tr>
                  );
                  // Top of the boxed area: label + coloured cap. Border/radius live on
                  // the inner div — border-radius is ignored on cells in a collapsed table.
                  rows.push(
                    <tr key={'hd-' + grp.key + si}><td colSpan={ncols} style={{ padding: 0, border: 'none' }}><div style={{ padding: '5px 12px', borderTop: `2px solid ${grp.colour}`, borderLeft: `4px solid ${grp.colour}`, borderRight: `1px solid ${grp.colour}55`, borderTopLeftRadius: 10, borderTopRightRadius: 10, background: `${grp.colour}14`, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: grp.colour }}>{grp.label}</div></td></tr>
                  );
                }
                rows.push(
                <tr key={sl.Slot} style={{ borderTop: '1px solid var(--hp-border)' }}>
                  <td style={{ padding: '6px 8px 6px 12px', fontSize: '0.78rem', fontWeight: 600, borderLeft: `4px solid ${grp.colour}`, background: `${grp.colour}14` }} title={grp.label}>{sl.Slot}<div style={{ color: 'var(--hp-dim)', fontWeight: 400, fontSize: '0.68rem' }}>{sl['Default Start']}–{sl['Default End']}</div></td>
                  {days.map((d, di) => {
                    const lastCol = di === days.length - 1;
                    const edgeR = lastCol ? { borderRight: `1px solid ${grp.colour}55` } : {};
                    const key = d + '|' + sl.Slot;
                    // No-shift night (Fri/Sat outside peak): greyed, non-interactive.
                    if (!isOperatingDay(d, operating)) {
                      return (
                        <td key={d} style={{ padding: '3px 4px', ...edgeR }}>
                          <div style={{ minHeight: 46, borderRadius: 6, border: '1px dashed var(--hp-border)', background: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.02) 5px, rgba(255,255,255,0.02) 10px)', color: 'var(--hp-dim)', fontSize: '0.66rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>—</div>
                        </td>
                      );
                    }
                    const a = grid[key];
                    const life = a && a.login ? lifecycleOf(att, d, a.login, todayISO) : null;
                    const conflict = a && a.login && conflicts[d].has(a.login);
                    const start = (a && a.start) || sl['Default Start'] || '';
                    const end = (a && a.end) || sl['Default End'] || '';
                    const overridden = a && a.login && ((a.start && a.start !== (sl['Default Start'] || '')) || (a.end && a.end !== (sl['Default End'] || '')));
                    // HOUSE RULE check: a runner must sit exactly 1h after that night's sorters.
                    let runnerDrift = false;
                    if (isRunnerSlot(sl) && a && a.login) {
                      const nr = (dayTimes[d] || '').split('–');
                      const ns = toMin(nr[0]), ne = toMin(nr[1]);
                      if (ns != null && ne != null) runnerDrift = start !== fromMin(ns + 60) || end !== fromMin(ne + 60);
                    }
                    const isSel = sel === key, isSwap = swapSrc === key, isOver = dragOver === key;
                    const askedOff = a && a.login ? inabilityByKey[a.login + '|' + d] : null; // rostered but asked off
                    const cellCol = a && a.login ? colourOf(a.login) : ''; // that person's colour
                    return (
                      <td key={d} style={{ padding: '3px 4px', ...edgeR }}>
                        <button onClick={(e) => onCellClick(key, e)}
                          draggable={!!(a && a.login)}
                          onDragStart={e => { e.dataTransfer.setData('text/plain', 'C:' + key); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragOver={e => { e.preventDefault(); setDragOver(key); }}
                          onDragLeave={() => setDragOver(o => (o === key ? null : o))}
                          onDrop={e => onCellDrop(e, key)}
                          title={(a && a.login ? `${a.name || a.login} · ${DOW[di]} ${d} ${start}–${end}` : `Assign · ${DOW[di]} ${d}`) + (runnerDrift ? ' — RUNNER RULE: should be 1h after the sorters' : '')}
                          style={{
                            width: '100%', minHeight: 46, cursor: a && a.login ? 'grab' : 'pointer', fontFamily: 'var(--font-body)',
                            background: isOver ? 'rgba(201,168,76,0.22)' : a && a.login ? (conflict ? 'rgba(239,68,68,0.14)' : cellCol ? cellCol + '2b' : life ? life.bg : runnerDrift ? 'rgba(245,158,11,0.14)' : 'rgba(201,168,76,0.10)') : 'var(--hp-card, #1E1E24)',
                            border: `1px ${isOver ? 'dashed' : 'solid'} ${conflict ? 'var(--hp-red, #EF4444)' : isSel || isSwap || isOver ? 'var(--hp-gold)' : life ? life.bd : runnerDrift ? 'var(--hp-amber, #F59E0B)' : 'var(--hp-border)'}`,
                            borderRadius: 6, color: a && a.login ? 'var(--hp-text)' : 'var(--hp-dim)', fontSize: '0.74rem', padding: '4px 3px', lineHeight: 1.25,
                          }}>
                          {a && a.login ? (<>
                            <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={[a.standing ? 'Standing position' : '', askedOff ? 'Asked for this night off' + (askedOff.reason ? ': ' + askedOff.reason : '') : ''].filter(Boolean).join(' · ') || undefined}>{life && life.lock ? '🔒 ' : ''}{a.standing ? '★ ' : ''}{askedOff ? '⚑ ' : ''}{gridName(a.login, a.name || personName(a.login))}</div>
                            <div style={{ color: runnerDrift ? 'var(--hp-amber, #F59E0B)' : overridden ? 'var(--hp-amber, #F59E0B)' : 'var(--hp-dim)', fontSize: '0.66rem' }}>{runnerDrift ? '⚠ ' : ''}{start}–{end}</div>
                          </>) : '—'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
                );
                if (lastInGroup) rows.push(
                  <tr key={'ft-' + grp.key + si} aria-hidden="true"><td colSpan={ncols} style={{ padding: 0, border: 'none' }}><div style={{ height: 6, borderBottom: `2px solid ${grp.colour}`, borderLeft: `4px solid ${grp.colour}`, borderRight: `1px solid ${grp.colour}55`, borderBottomLeftRadius: 10, borderBottomRightRadius: 10, background: `${grp.colour}0a` }} /></td></tr>
                );
                return rows;
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginTop: 12, fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
        <span style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Shift status</span>
        {[['Accepted', LIFE.amber.bd], ['Completed', LIFE.green.bd], ['Not completed', LIFE.red.bd], ['🔒 Authorised', LIFE.blue.bd]].map(([lbl, c]) => (
          <span key={lbl} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 14, height: 14, borderRadius: 4, border: `2px solid ${c}`, background: 'transparent', display: 'inline-block' }} />{lbl}
          </span>
        ))}
      </div>
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', marginTop: 12 }}>Drag a name from the left rail onto a shift, or drag a filled shift onto another to move/swap it. <strong>Or use copy/paste:</strong> click a name in the rail — or click a filled shift and choose <strong>Copy</strong> — then click empty positions to drop them straight in (one person across a whole week in a few clicks). Clicking a filled shift opens a quick <strong>Copy · Remove</strong> menu. To change a night's times use the ✎ time editor in that day's column header; drag a filled shift onto another to swap. Amber = time changed from the slot default. Red = the same person twice in one night. <strong>Runner rule:</strong> runner positions always start and finish 1 hour after the sorters — the heading editor applies it automatically, and a runner cell that drifts shows ⚠ amber. Publish makes the week live in each sorter's “My Shifts”.</p>
    </div>
    <RosterChangeLog tick={noticeTick} />
    </>
  );
}

// ── Roster changes — what the team was told, and who has actually seen it ────
// Publishing over a live week moves people on and off nights. The backend diffs each
// publish and leaves the affected sorters a notice in their portal; this is the office's
// side of it, because "I told them" and "they read it" are different facts and only the
// second one means the night is covered. Unseen changes to the closest nights come first.
function RosterChangeLog({ tick }) {
  const [items, setItems] = useState(null);
  const [showSeen, setShowSeen] = useState(false);

  const load = useCallback(() => {
    api('listSorterNotices', {})
      .then(r => setItems(r && r.success ? (r.notices || []) : []))
      .catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load, tick]);

  if (items === null) return null;
  const unseen = items.filter(n => !n.ack);
  const seen = items.filter(n => n.ack);
  const shown = showSeen ? items : unseen;
  const when = (iso) => { try { return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '1.05rem' }}>Roster changes</h3>
        {unseen.length > 0
          ? <span style={pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)')}><AlertTriangle size={12} /> {unseen.length} not yet seen</span>
          : <span style={pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)')}><CheckCircle2 size={12} /> everyone has seen theirs</span>}
        <button onClick={load} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '6px 10px', fontSize: '0.74rem', marginLeft: 'auto' }}><RefreshCw size={12} /> Refresh</button>
      </div>
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', margin: '0 0 12px', lineHeight: 1.5 }}>
        Sent automatically when you publish over a week that was already live — nothing to compose. The sorter sees it on every tab of their portal until they tap <strong>Got it</strong>.
        {seen.length > 0 && <> <button onClick={() => setShowSeen(v => !v)} style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}>{showSeen ? 'Hide' : 'Show'} the {seen.length} already seen</button>.</>}
      </p>
      {!shown.length ? (
        <p style={{ color: 'var(--hp-dim)', fontSize: '0.82rem', margin: 0 }}>No roster changes outstanding.</p>
      ) : shown.map(n => (
        <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', padding: '10px 0', borderTop: '1px solid var(--hp-border)' }}>
          <div style={{ minWidth: 130, fontWeight: 700, fontSize: '0.85rem' }}>{n.name || n.login}</div>
          <div style={{ flex: 1, minWidth: 220, fontSize: '0.84rem', lineHeight: 1.45 }}>
            {n.text}
            <div style={{ color: 'var(--hp-dim)', fontSize: '0.72rem', marginTop: 3 }}>sent {when(n.createdAt)}{n.by ? ` by ${n.by}` : ''}</div>
          </div>
          {n.ack
            ? <span style={pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)')}><CheckCircle2 size={12} /> seen {when(n.ack)}</span>
            : <span style={pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)')}><Clock size={12} /> not seen yet</span>}
        </div>
      ))}
    </div>
  );
}

// ── Briefs — a message every sorter must read and acknowledge ─────────────────
// The office writes it here; each sorter's portal shows it on the Clock tab before they
// clock off, or before their next clock on if they weren't on shift when it went out
// (sorter-briefs-sb.js). The register below is the point of it: "we sent it" and "they
// read it" are different facts, and only the second one is worth telling AP.
const BRIEF_LANG_NAMES = { en: 'English', es: 'Spanish', zh: 'Chinese', mn: 'Mongolian', bn: 'Bengali' };
const BRIEF_TR_LANGS = ['es', 'zh', 'mn', 'bn'];
function SorterBriefsView() {
  const [briefs, setBriefs] = useState(null);
  const [err, setErr] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState('');
  const [viewLang, setViewLang] = useState('en');
  const [translating, setTranslating] = useState({}); // `${id}:${lang}` → true while waiting

  const load = useCallback(() => {
    api('listSorterBriefs', {})
      .then(r => { if (r && r.success) { setBriefs(r.briefs || []); setErr(''); } else { setBriefs(b => b || []); setErr((r && r.error) || 'Couldn’t load briefs'); } })
      .catch(e => { setBriefs(b => b || []); setErr(e.message); });
  }, []);
  useEffect(() => { load(); }, [load]);

  async function issue() {
    if (!title.trim() || !body.trim()) return;
    if (!window.confirm(`Issue “${title.trim()}” to every active sorter?\n\nAnyone on shift reads it before they clock off; everyone else before their next clock on. It can't be edited once issued — stand it down and issue a new one instead.`)) return;
    setBusy('issue');
    try {
      const r = await api('issueSorterBrief', { title, body });
      if (!r || !r.success) throw new Error((r && r.error) || 'Couldn’t issue it');
      setTitle(''); setBody(''); setPreview(false); setOpen(r.brief.id); load();
    } catch (e) { setErr(e.message); }
    setBusy('');
  }
  async function standDown(b) {
    if (!window.confirm(`Stand down “${b.title}”?\n\nSorters who haven't read it will no longer be asked to. The brief and every acknowledgement are kept.`)) return;
    setBusy(b.id);
    try {
      const r = await api('standDownSorterBrief', { id: b.id });
      if (!r || !r.success) throw new Error((r && r.error) || 'Couldn’t stand it down');
      load();
    } catch (e) { setErr(e.message); }
    setBusy('');
  }
  const when = (iso) => { try { return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

  // Translation runs as a background function (Mongolian alone takes ~30s, the whole budget
  // of a normal one), so ask for it and then watch for the row to land.
  async function translate(b, langs) {
    const keys = langs.map(l => `${b.id}:${l}`);
    setTranslating(t => ({ ...t, ...Object.fromEntries(keys.map(k => [k, true])) }));
    setErr('');
    try {
      await Promise.all(langs.map(lang => fetch('/.netlify/functions/sorter-brief-translate-background', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: b.id, lang }),
      }).then(r => { if (r.status === 403) throw new Error('Sign-in required'); })));
    } catch (e) {
      setErr(e.message);
      setTranslating(t => { const n = { ...t }; keys.forEach(k => delete n[k]); return n; });
      return;
    }
    const started = Date.now();
    let left = [...langs];
    while (left.length && Date.now() - started < 180000) {
      await new Promise(r => setTimeout(r, 6000));
      let r;
      try { r = await api('listSorterBriefs', {}); } catch { continue; }
      if (!r || !r.success) continue;
      setBriefs(r.briefs || []);
      const now = (r.briefs || []).find(x => x.id === b.id) || {};
      left = left.filter(l => {
        const done = now.i18n && now.i18n[l] && now.i18n[l].at > new Date(started - 5000).toISOString();
        const failed = now.trErrors && now.trErrors[l] && now.trErrors[l].at > new Date(started - 5000).toISOString();
        return !done && !failed;
      });
      setTranslating(t => { const n = { ...t }; langs.filter(l => !left.includes(l)).forEach(l => delete n[`${b.id}:${l}`]); return n; });
    }
    if (left.length) {
      setErr(`${left.map(l => BRIEF_LANG_NAMES[l]).join(', ')} didn\u2019t come back after 3 minutes \u2014 try again.`);
      setTranslating(t => { const n = { ...t }; left.forEach(l => delete n[`${b.id}:${l}`]); return n; });
    }
  }

  return (
    <>
      <div style={card}>
        <h3 style={{ margin: '0 0 6px', fontFamily: 'var(--font-display)', fontSize: '1.05rem' }}><Megaphone size={16} style={{ verticalAlign: -2, marginRight: 6 }} />Issue a brief</h3>
        <p style={{ color: 'var(--hp-dim)', fontSize: '0.8rem', margin: '0 0 14px', lineHeight: 1.5 }}>
          Issue it in English, then use <strong>Translate</strong> on the brief below — sorters can read it in Spanish, Chinese, Mongolian or Bengali. Every active sorter must read it and tap <strong>I&apos;ve read and understood</strong> — on the Clock tab, before they clock off, or before their next clock on if they aren&apos;t on shift. It never stops a clock-off: if their phone can&apos;t reach us the tap goes through and they&apos;re asked again next time.
          Formatting: <code>## Heading</code>, <code>- bullet</code>, <code>1. step</code>, <code>**bold**</code>.
        </p>
        <label style={label}>Title</label>
        <input style={{ ...input, marginBottom: 12 }} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Damaged parcels" />
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
          <label style={{ ...label, marginBottom: 0 }}>Brief</label>
          <button onClick={() => setPreview(v => !v)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--hp-gold)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'var(--font-body)' }}><Eye size={13} style={{ verticalAlign: -2 }} /> {preview ? 'Edit' : 'Preview as a sorter sees it'}</button>
        </div>
        {preview
          ? <div style={{ ...input, minHeight: 220, maxWidth: 440, padding: '4px 14px' }}>{title.trim() && <div style={{ fontWeight: 800, fontSize: '1.05rem', margin: '10px 0 4px' }}>{title}</div>}<BriefText text={body} /></div>
          : <textarea style={{ ...input, minHeight: 260, resize: 'vertical', lineHeight: 1.5 }} value={body} onChange={e => setBody(e.target.value)} />}
        <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={{ ...btn('var(--hp-gold)'), opacity: title.trim() && body.trim() ? 1 : 0.5 }} disabled={!title.trim() || !body.trim() || busy === 'issue'} onClick={issue}>
            {busy === 'issue' ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Send size={15} />} Issue to all sorters
          </button>
          {err && <span style={pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)')}><AlertTriangle size={12} /> {err}</span>}
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '1.05rem' }}>Issued briefs</h3>
          <button onClick={load} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '6px 10px', fontSize: '0.74rem', marginLeft: 'auto' }}><RefreshCw size={12} /> Refresh</button>
        </div>
        {briefs === null ? <p style={{ color: 'var(--hp-dim)', fontSize: '0.84rem' }}><Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite', verticalAlign: -2 }} /> Loading…</p>
          : !briefs.length ? <p style={{ color: 'var(--hp-dim)', fontSize: '0.84rem', margin: 0 }}>No briefs issued yet.</p>
          : briefs.map(b => {
            const live = b.status === 'live';
            const done = b.total > 0 && b.read === b.total;
            const outstanding = b.register.filter(r => !r.ackAt);
            const isOpen = open === b.id;
            return (
              <div key={b.id} style={{ borderTop: '1px solid var(--hp-border)', padding: '12px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button onClick={() => setOpen(isOpen ? '' : b.id)} style={{ background: 'none', border: 'none', color: 'var(--hp-text)', cursor: 'pointer', padding: 0, fontWeight: 800, fontSize: '0.92rem', fontFamily: 'var(--font-body)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <ChevronRight size={15} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} /> {b.title}
                  </button>
                  <span style={pill(done ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.14)', done ? 'var(--hp-green, #22C55E)' : 'var(--hp-amber, #F59E0B)')}>
                    {done ? <CheckCircle2 size={12} /> : <Clock size={12} />} {b.read} of {b.total} read
                  </span>
                  {!live && <span style={pill('rgba(148,163,184,0.14)', 'var(--hp-dim)')}>stood down {when(b.stoodDownAt)}</span>}
                  <span style={{ color: 'var(--hp-dim)', fontSize: '0.74rem' }}>issued {when(b.issuedAt)}{b.issuedBy ? ` by ${b.issuedBy}` : ''}</span>
                  {live && <button onClick={() => standDown(b)} disabled={busy === b.id} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '5px 10px', fontSize: '0.74rem', marginLeft: 'auto' }}>{busy === b.id ? <Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> : <X size={12} />} Stand down</button>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 8, paddingLeft: 21 }}>
                  <span style={{ color: 'var(--hp-dim)', fontSize: '0.74rem' }}>Translations:</span>
                  {BRIEF_TR_LANGS.map(l => {
                    const has = b.i18n && b.i18n[l];
                    const busyL = translating[`${b.id}:${l}`];
                    const failed = !has && b.trErrors && b.trErrors[l];
                    return (
                      <span key={l} title={failed ? failed.error : has ? `translated ${when(has.at)}` : ''}
                        style={pill(has ? 'rgba(34,197,94,0.12)' : failed ? 'rgba(239,68,68,0.12)' : 'rgba(148,163,184,0.14)', has ? 'var(--hp-green, #22C55E)' : failed ? 'var(--hp-red, #EF4444)' : 'var(--hp-dim)')}>
                        {busyL ? <Loader2 size={11} style={{ animation: 'spin 0.8s linear infinite' }} /> : has ? <CheckCircle2 size={11} /> : failed ? <AlertTriangle size={11} /> : null} {BRIEF_LANG_NAMES[l]}
                      </span>
                    );
                  })}
                  {live && (() => {
                    const missing = BRIEF_TR_LANGS.filter(l => !(b.i18n && b.i18n[l]) && !translating[`${b.id}:${l}`]);
                    const all = BRIEF_TR_LANGS.filter(l => !translating[`${b.id}:${l}`]);
                    const todo = missing.length ? missing : all;
                    if (!todo.length) return <span style={{ color: 'var(--hp-dim)', fontSize: '0.74rem' }}>translating — about 30 seconds…</span>;
                    return <button onClick={() => translate(b, todo)} style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700, fontFamily: 'var(--font-body)' }}>{missing.length ? `Translate${missing.length < BRIEF_TR_LANGS.length ? ' the rest' : ''}` : 'Re-translate all'}</button>;
                  })()}
                </div>
                {isOpen && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: 16, marginTop: 12 }}>
                    <div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        {['en', ...BRIEF_TR_LANGS.filter(l => b.i18n && b.i18n[l])].map(l => (
                          <button key={l} onClick={() => setViewLang(l)} style={{ ...btn('transparent'), padding: '4px 10px', fontSize: '0.74rem', border: `1px solid ${viewLang === l ? 'var(--hp-gold)' : 'var(--hp-border)'}`, color: viewLang === l ? 'var(--hp-gold)' : 'var(--hp-dim)' }}>{BRIEF_LANG_NAMES[l]}</button>
                        ))}
                      </div>
                      {(() => {
                        const t = viewLang !== 'en' && b.i18n && b.i18n[viewLang];
                        return <div style={{ ...input, padding: '4px 14px' }} lang={t ? viewLang : 'en'}>{t && <div style={{ fontWeight: 800, margin: '10px 0 4px' }}>{t.title}</div>}<BriefText text={t ? t.body : b.body} /></div>;
                      })()}
                    </div>
                    <div>
                      {outstanding.length > 0 && <div style={{ ...label, color: 'var(--hp-amber, #F59E0B)' }}>Not read yet ({outstanding.length})</div>}
                      {outstanding.map(r => <div key={r.login} style={{ fontSize: '0.84rem', padding: '4px 0', borderBottom: '1px solid var(--hp-border)' }}>{r.name} <span style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>{r.login}</span></div>)}
                      <div style={{ ...label, color: 'var(--hp-green, #22C55E)', marginTop: outstanding.length ? 14 : 0 }}>Read ({b.read})</div>
                      {b.register.filter(r => r.ackAt).sort((x, y) => x.ackAt.localeCompare(y.ackAt)).map(r => (
                        <div key={r.login} style={{ fontSize: '0.84rem', padding: '4px 0', borderBottom: '1px solid var(--hp-border)', display: 'flex', gap: 8 }}>
                          <span style={{ flex: 1 }}>{r.name}</span>{r.lang && r.lang !== 'en' && <span style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>in {BRIEF_LANG_NAMES[r.lang] || r.lang}</span>}<span style={{ color: 'var(--hp-dim)', fontSize: '0.74rem' }}>{when(r.ackAt)}</span>
                        </div>
                      ))}
                      {!b.read && <div style={{ color: 'var(--hp-dim)', fontSize: '0.8rem' }}>Nobody yet.</div>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </>
  );
}

// ── AP paper sign-in sheets — scan, OCR, reconcile against roster + clock ─────
function frameToJpegBase64(source, sw, sh, maxEdge = 2200, quality = 0.85) {
  const ratio = Math.min(1, maxEdge / Math.max(sw, sh));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(sw * ratio));
  canvas.height = Math.max(1, Math.round(sh * ratio));
  canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', quality).split(',')[1];
}
// Bake in EXIF rotation (iPhone) and downscale — handwriting needs more pixels than
// CameraCapture's 720px default, but the result must stay under the function body limit.
async function sheetFileToBase64(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const b64 = frameToJpegBase64(bmp, bmp.width, bmp.height);
      if (bmp.close) bmp.close();
      return b64;
    } catch (e) { /* fall through */ }
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => { const img = new Image(); img.onload = () => { try { resolve(frameToJpegBase64(img, img.naturalWidth, img.naturalHeight)); } catch (e) { reject(e); } }; img.onerror = () => reject(new Error('Could not decode image')); img.src = reader.result; };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}
const apApi = (action, payload) => fetch('/.netlify/functions/ap-signsheet-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) })
  .then(async r => {
    const text = await r.text();
    try { return JSON.parse(text); }
    catch { throw new Error(`The server took too long (${r.status}). If this was an upload, the sheet may still be stored — Refresh the list, open it and hit Re-read.`); }
  });

// ── Actual times — nightly ledger: rostered schedule vs AP's actual finish ────
// One row per shift night: rostered start & finish (the most common sorter shift
// that night, same rule as the roster grid headings), the AP finish time keyed in
// each morning, hours over/under schedule, and total man-hours (actual × shifts).
const t2m = (t) => { const m = /^(\d{1,2}):(\d{2})/.exec((t || '').trim()); return m ? (+m[1]) * 60 + (+m[2]) : null; };
// Workplace-law overtime: only time beyond 8 hours on a shift is genuine O/T.
// Running past the scheduled finish is NOT overtime while the shift stays
// under 8h (operator directive 2026-07-03 — must be consistent with the award).
const OT_THRESHOLD_MIN = 8 * 60;
// Overnight-aware duration: 23:45 → 06:15 = 6h30m (wraps past midnight).
const nightDur = (s, e) => (s == null || e == null) ? null : ((e - s) + 1440) % 1440;
const fmtDur = (min) => { if (min == null) return '—'; const neg = min < 0; const a = Math.abs(Math.round(min)); return (neg ? '−' : '') + Math.floor(a / 60) + ':' + String(a % 60).padStart(2, '0'); };
const nightLabel = (iso) => { const d = new Date(iso + 'T00:00'); return DOW[d.getDay()] + ' ' + fmtD(iso); };

function ActualTimesView({ testMode }) {
  const [to, setTo] = useState(defaultShiftDate);
  const [span, setSpan] = useState(28); // nights shown, ending at `to`
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // {date, start, schedFinish, shifts} — manual override editor
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const flash = (text, ok = true) => { setToast({ text, ok }); setTimeout(() => setToast(null), 6000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = new Date(to + 'T00:00'); d.setDate(d.getDate() - (span - 1));
      const from = isoLocal(d);
      const [s, r, f] = await Promise.all([
        api('getSorterSlots', {}),
        api('getSorterRoster', { from, to }),
        api('nsListFinishTimes', { from, to }),
      ]);
      const catBySlot = {};
      ((s && s.slots) || []).forEach(sl => { catBySlot[sl.Slot] = sl.Category || ''; });
      const byDate = {};
      ((r && r.roster) || []).forEach(row => {
        if (!(row.Login || '').trim()) return;
        const e = byDate[row.Date] || (byDate[row.Date] = { shifts: 0, ranges: {} });
        e.shifts++;
        // Runners are always +1h, so only sorter shifts vote for the night's times.
        if (catBySlot[row.Slot] === 'sorter' && row.Start && row.End) {
          const k = row.Start + '–' + row.End;
          e.ranges[k] = (e.ranges[k] || 0) + 1;
        }
      });
      const finByDate = {};
      ((f && f.finishes) || []).forEach(x => { finByDate[x.shiftDate] = x; });
      const dates = Array.from(new Set([...Object.keys(byDate), ...Object.keys(finByDate)])).sort().reverse();
      setRows(dates.map(date => {
        const e = byDate[date];
        let best = '';
        if (e) Object.entries(e.ranges).forEach(([k, n]) => { if (!best || n > e.ranges[best]) best = k; });
        const [rosterStart, rosterEnd] = best ? best.split('–') : ['', ''];
        const fin = finByDate[date];
        // Manual overrides (kept on the finish-time row) beat the roster-derived values.
        const schedStart = (fin && fin.startTime) || rosterStart;
        const schedEnd = (fin && fin.schedFinish) || rosterEnd;
        const shifts = (fin && fin.shifts != null) ? fin.shifts : (e ? e.shifts : 0);
        const s0 = t2m(schedStart), e0 = t2m(schedEnd), a0 = fin ? t2m(fin.finishTime) : null;
        const sched = nightDur(s0, e0);
        const actual = nightDur(s0, a0);
        const over = (sched != null && actual != null) ? actual - sched : null;
        return {
          date, shifts, schedStart, schedEnd,
          apFinish: (fin && fin.finishTime) || '', note: (fin && fin.note) || '',
          overridden: !!(fin && (fin.startTime || fin.schedFinish || fin.shifts != null)),
          sched, actual, over,
          overtime: actual == null ? null : Math.max(0, actual - OT_THRESHOLD_MIN),
          manHours: (actual != null && shifts) ? actual * shifts : null,
          schedManHours: (sched != null && shifts) ? sched * shifts : null,
        };
      }));
    } catch (e) {} finally { setLoading(false); }
  }, [to, span]);
  useEffect(() => { load(); }, [load]);

  const sum = (k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
  const nights = rows.filter(r => r.shifts > 0).length;
  const missing = rows.filter(r => r.shifts > 0 && !r.apFinish).length;
  const otNights = rows.filter(r => r.overtime != null);
  const totOvertime = otNights.reduce((a, r) => a + r.overtime, 0);
  const totOvertimeMan = otNights.reduce((a, r) => a + r.overtime * (r.shifts || 0), 0);

  async function saveEdit() {
    setSaving(true);
    try {
      const r = await api('nsSetActualOverride', { shiftDate: edit.date, startTime: edit.start, schedFinish: edit.schedFinish, shifts: edit.shifts });
      if (!r.success) throw new Error(r.error || 'Save failed');
      setEdit(null);
      flash(`${nightLabel(edit.date)} adjusted — the ledger below is recalculated.`);
      load();
    } catch (e) { flash(e.message, false); } finally { setSaving(false); }
  }

  const th = { padding: '8px 10px', textAlign: 'right', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-dim)', whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', textAlign: 'right', borderTop: '1px solid var(--hp-border)', fontVariantNumeric: 'tabular-nums', fontSize: '0.86rem', whiteSpace: 'nowrap' };
  const stat = (lbl, val, sub) => (
    <div style={{ background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: '10px 14px', minWidth: 120 }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{lbl}</div>
      <div style={{ fontSize: '1.15rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{val}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>{sub}</div>}
    </div>
  );

  return (<>
    <FinishTimeCard testMode={testMode} onSaved={load} />
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <Clock size={18} color="var(--hp-gold)" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Actual times — schedule vs AP finish</h2>
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginLeft: 'auto', flexWrap: 'wrap' }}>
          {[7, 14, 28].map(n => (
            <button key={n} onClick={() => setSpan(n)} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${span === n ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: span === n ? 'rgba(201,168,76,0.12)' : 'transparent', color: span === n ? 'var(--hp-gold)' : 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', fontSize: '0.78rem', fontFamily: 'var(--font-body)' }}>{n} nights</button>
          ))}
          <input type="date" style={{ ...input, width: 150, padding: '6px 10px', fontSize: '0.82rem' }} value={to} onChange={e => setTo(e.target.value)} title="Last night shown" />
          <button onClick={load} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '6px 10px', fontSize: '0.78rem' }}><RefreshCw size={13} /></button>
        </span>
      </div>

      {toast && <div style={{ ...pill(toast.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', toast.ok ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)'), padding: '8px 12px', marginBottom: 12 }}>{toast.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {toast.text}</div>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {stat('Nights', nights, missing ? `${missing} missing AP time` : 'all AP times in')}
        {stat('Shifts', sum('shifts'), `avg ${nights ? (sum('shifts') / nights).toFixed(1) : '—'}/night`)}
        {stat('Scheduled hours', fmtDur(sum('schedManHours')), 'man-hours')}
        {stat('Actual hours', fmtDur(sum('manHours')), 'man-hours, nights with AP time')}
        {stat('Overtime (>8h)', <span style={{ color: totOvertime > 0 ? 'var(--hp-red, #EF4444)' : 'var(--hp-green, #22C55E)' }}>{otNights.length ? fmtDur(totOvertime) : '—'}</span>, totOvertime > 0 ? `per night · ×shifts = ${fmtDur(totOvertimeMan)} man-hours` : 'no shift beyond 8 hours')}
      </div>

      {loading ? (
        <div style={{ color: 'var(--hp-dim)', padding: 20 }}><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--hp-dim)', padding: '16px 0' }}>No rostered nights or AP finish times in this range yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>Night</th>
                <th style={th}>Start</th>
                <th style={th}>Sched finish</th>
                <th style={th}>AP finish</th>
                <th style={th}>Sched</th>
                <th style={th}>Actual</th>
                <th style={th}>O/T &gt;8h</th>
                <th style={th}>Shifts</th>
                <th style={th}>Night man-hours</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => edit && edit.date === r.date ? (
                <tr key={r.date} style={{ background: 'rgba(201,168,76,0.07)' }}>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{nightLabel(r.date)} <span style={{ fontWeight: 400, color: 'var(--hp-gold)', fontSize: '0.72rem' }}>adjusting…</span></td>
                  <td style={td}><input type="time" style={{ ...input, width: 110, padding: '5px 8px', fontSize: '0.82rem' }} value={edit.start} onChange={e => setEdit(v => ({ ...v, start: e.target.value }))} /></td>
                  <td style={td}><input type="time" style={{ ...input, width: 110, padding: '5px 8px', fontSize: '0.82rem' }} value={edit.schedFinish} onChange={e => setEdit(v => ({ ...v, schedFinish: e.target.value }))} /></td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>{r.apFinish || '—'}</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }} colSpan={3}>AP finish is edited in the card above</td>
                  <td style={td}><input type="number" min="0" style={{ ...input, width: 70, padding: '5px 8px', fontSize: '0.82rem' }} value={edit.shifts} onChange={e => setEdit(v => ({ ...v, shifts: e.target.value }))} /></td>
                  <td style={td} colSpan={2}>
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <button onClick={saveEdit} disabled={saving} style={{ ...btn('var(--hp-gold)'), padding: '6px 12px', fontSize: '0.76rem' }}>{saving ? <Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Check size={12} />} Save</button>
                      <button onClick={() => setEdit(null)} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '6px 10px', fontSize: '0.76rem' }}><X size={12} /></button>
                    </span>
                  </td>
                </tr>
              ) : (
                <tr key={r.date}>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>
                    {nightLabel(r.date)}
                    {r.overridden && <span title="Times/shifts manually adjusted" style={{ color: 'var(--hp-gold)', fontWeight: 400 }}> ✎</span>}
                    {r.note && <span style={{ fontWeight: 400, color: 'var(--hp-dim)', fontSize: '0.74rem' }}> — {r.note}</span>}
                  </td>
                  <td style={td}>{r.schedStart || '—'}</td>
                  <td style={td}>{r.schedEnd || '—'}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{r.apFinish || (r.shifts ? warn('not in') : '—')}</td>
                  <td style={td}>{fmtDur(r.sched)}</td>
                  <td style={td}>{fmtDur(r.actual)}</td>
                  <td style={{ ...td, fontWeight: 700, color: r.overtime == null ? 'var(--hp-dim)' : r.overtime > 0 ? 'var(--hp-red, #EF4444)' : 'var(--hp-green, #22C55E)' }}
                    title={r.overtime > 0 ? `shift ran ${fmtDur(r.actual)} — ${fmtDur(r.overtime)} beyond 8h is genuine O/T`
                      : r.over != null && r.over > 0 ? `ran ${fmtDur(r.over)} past the scheduled finish — still under 8h, so no O/T`
                      : r.over != null && r.over < 0 ? `finished ${fmtDur(-r.over)} early` : undefined}>
                    {r.overtime > 0 ? '⚠ ' : ''}{fmtDur(r.overtime)}</td>
                  <td style={td}>{r.shifts ? '× ' + r.shifts : '—'}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmtDur(r.manHours)}</td>
                  <td style={td}>
                    <button title="Adjust this night's start, scheduled finish or shift count"
                      onClick={() => setEdit({ date: r.date, start: r.schedStart || '', schedFinish: r.schedEnd || '', shifts: r.shifts || '' })}
                      style={{ background: 'none', border: '1px solid var(--hp-border)', borderRadius: 6, color: 'var(--hp-dim)', cursor: 'pointer', padding: '4px 8px' }}><Pencil size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', marginTop: 12 }}>
        <strong>Start / Sched finish / Shifts</strong> come from the published roster (most common sorter shift that night; runners are +1h so they don't vote) —
        unless manually adjusted with the ✎ button, which pins that night's values permanently.
        <strong> AP finish</strong> is keyed in via the card above. <strong>O/T &gt;8h</strong> is workplace-law overtime: only time beyond 8 hours on the shift counts —
        running past the scheduled finish is <em>not</em> overtime while the shift stays under 8h (hover a value for the vs-schedule detail);
        <strong> Night man-hours</strong> = actual hours × shifts. Backfilled 15–30 Jun 2026: Sun/Mon starts 23:15, other nights 23:45, 10 shifts, scheduled to 06:15.
      </p>
    </div>
  </>);
}

function ApSheetsView({ sorters }) {
  const [sheets, setSheets] = useState(null);
  const [active, setActive] = useState(null);   // full sheet detail
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const flash = (text, ok = true) => { setToast({ text, ok }); setTimeout(() => setToast(null), 7000); };

  const load = useCallback(async () => {
    try { const r = await apApi('apSheetList', {}); setSheets(r.success ? r.sheets : []); }
    catch (e) { setSheets([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // One upload path for all three ways in: camera/file picker, drag & drop, paste.
  async function uploadSheet(file) {
    if (!file) return;
    if (!(file.type || '').startsWith('image/')) { flash('Drop or paste an image of the sheet (photo or screenshot) — PDFs aren’t supported here.', false); return; }
    setBusy('upload');
    try {
      const base64 = await sheetFileToBase64(file);
      const r = await apApi('apSheetUpload', { base64, contentType: 'image/jpeg', filename: file.name || 'sheet.jpg' });
      if (!r.success) throw new Error(r.error || 'Upload failed');
      if (r.ocrError) flash(`Image stored, but reading it failed (${r.ocrError}) — open the sheet and set the shift night by hand.`, false);
      else flash(`Sheet read — shift night ${r.shiftDate}${r.sheetDate ? ` (paper dated ${r.sheetDate})` : ''}. Check the reconciliation below.`);
      await load();
      openSheet(r.id);
    } catch (err) { flash(err.message, false); } finally { setBusy(''); }
  }

  function onFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    uploadSheet(file);
  }

  // Desktop: paste a copied photo/screenshot (⌘V on Mac / Ctrl+V on Windows)
  // anywhere on this tab. NOTE: on a Mac, Chrome only fires this for ⌘V — the
  // Paste button below covers anyone reaching for Ctrl+V.
  const uploadRef = useRef();
  uploadRef.current = { uploadSheet, busy, flash };
  // Lets the global photo-paste listener stay quiet when ⌘V is meant for a driver.
  // There is no driver copy/paste on this tab (that lives in RosterBuilder), so a
  // driver paste is never in flight here.
  const cellPasteRef = useRef();
  cellPasteRef.current = { active: false };
  useEffect(() => {
    const onPaste = (e) => {
      const { uploadSheet: up, busy: b, flash: fl } = uploadRef.current;
      if (b === 'upload') return;
      const items = Array.from((e.clipboardData && e.clipboardData.items) || []);
      const img = items.find(i => (i.type || '').startsWith('image/'));
      if (!img) {
        const tag = ((e.target && e.target.tagName) || '').toLowerCase();
        // A driver copy/paste is in flight (⌘V pastes the driver via keydown) — stay quiet.
        if (cellPasteRef.current && cellPasteRef.current.active) return;
        if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') fl('Pasted, but the clipboard had no image — copy the photo itself (right-click → Copy Image), then paste again or use the Paste button.', false);
        return;
      }
      const file = img.getAsFile();
      if (file) { e.preventDefault(); up(file); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  // The Paste button reads the clipboard directly — works no matter which
  // shortcut the operator is used to, and says so when there's no image.
  async function pasteFromClipboard() {
    if (busy === 'upload') return;
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) throw new Error('unsupported');
      const items = await navigator.clipboard.read();
      for (const it of items) {
        const type = (it.types || []).find(t => t.startsWith('image/'));
        if (type) {
          const blob = await it.getType(type);
          uploadSheet(new File([blob], 'pasted-sheet.' + (type.split('/')[1] || 'png'), { type }));
          return;
        }
      }
      flash('No image in the clipboard — copy the sheet photo itself (right-click → Copy Image), then press Paste again.', false);
    } catch (e) {
      flash('Couldn\'t read the clipboard — allow clipboard access when Chrome asks, or press ⌘V instead.', false);
    }
  }

  function onDrop(e) {
    e.preventDefault(); setDragOver(false);
    if (busy === 'upload') return;
    const file = Array.from((e.dataTransfer && e.dataTransfer.files) || []).find(f => (f.type || '').startsWith('image/'))
      || (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
    uploadSheet(file);
  }

  async function openSheet(id) {
    setBusy('open' + id);
    try { const r = await apApi('apSheetGet', { id }); if (!r.success) throw new Error(r.error || 'Failed'); setActive(r.sheet); }
    catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }

  async function patchSheet(patch, okMsg) {
    if (!active) return;
    setBusy('patch');
    try {
      const r = await apApi('apSheetUpdate', { id: active.id, ...patch });
      if (!r.success) throw new Error(r.error || 'Failed');
      if (okMsg) flash(okMsg);
      await openSheet(active.id); await load();
    } catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }

  async function deleteSheet() {
    if (!active || !window.confirm('Delete this sheet scan and its image?')) return;
    await apApi('apSheetDelete', { id: active.id });
    setActive(null); load();
  }

  // Re-run OCR on the stored image — for sheets whose first read timed out/failed.
  async function reocrSheet() {
    if (!active) return;
    setBusy('reocr');
    try {
      const r = await apApi('apSheetReocr', { id: active.id });
      if (!r.success) throw new Error(r.error || 'Re-read failed');
      flash(`Sheet re-read — shift night ${r.shiftDate}${r.sheetDate ? ` (paper dated ${r.sheetDate})` : ''}.`);
      await load(); await openSheet(active.id);
    } catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }

  const th = { padding: '7px 8px', fontSize: '0.66rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', whiteSpace: 'nowrap' };
  const td = { padding: '7px 8px', fontSize: '0.82rem', borderTop: '1px solid var(--hp-border)', verticalAlign: 'top' };
  const ghost = { ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '7px 11px', fontSize: '0.78rem' };
  const summary = active && active.reconciliation && active.reconciliation.summary;
  const entries = (active && active.reconciliation && active.reconciliation.entries) || [];

  return (
    <>
      <div
        style={{ ...card, border: dragOver ? '2px dashed var(--hp-gold)' : card.border, background: dragOver ? 'rgba(201,168,76,0.06)' : card.background }}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <ScanLine size={18} color="var(--hp-gold)" />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>AP sign-in sheets</h2>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
            <button onClick={pasteFromClipboard} disabled={busy === 'upload'}
              style={{ ...btn('transparent'), color: 'var(--hp-gold)', border: '1px solid var(--hp-gold)' }}>
              <ClipboardCheck size={15} /> Paste
            </button>
            <label style={{ ...btn('var(--hp-gold)'), cursor: busy === 'upload' ? 'wait' : 'pointer' }}>
              {busy === 'upload' ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Camera size={15} />} {busy === 'upload' ? 'Reading sheet…' : 'Scan a sheet'}
              <input type="file" hidden accept="image/*" capture="environment" disabled={busy === 'upload'} onChange={onFile} />
            </label>
          </span>
        </div>
        <p style={{ color: 'var(--hp-dim)', fontSize: '0.82rem', margin: '0 0 12px', lineHeight: 1.5 }}>
          Photograph the Botany PDC paper sheet — it's read automatically and checked against our roster and the app clock.
          On desktop: copy the photo, then click <strong>Paste</strong> (or press ⌘V), or <strong>drag &amp; drop</strong> it anywhere on this card — same result as scanning.
          The paper's date is the morning the shift ends; the shift night is worked out for you (and can be corrected).
          Every sheet is stored for 12 months — click a row below to recall it, photo included.
        </p>
        {toast && <div style={{ ...pill(toast.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', toast.ok ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)'), padding: '8px 12px', marginBottom: 12 }}>{toast.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {toast.text}</div>}

        {sheets === null ? (
          <div style={{ color: 'var(--hp-dim)', padding: 16 }}><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>
        ) : !sheets.length ? (
          <div style={{ color: 'var(--hp-dim)', padding: '18px 0', textAlign: 'center' }}>No sheets scanned yet — tap “Scan a sheet” after the shift.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead><tr><th style={th}>Shift night</th><th style={th}>Paper date</th><th style={th}>Signed on</th><th style={th}>Circled</th><th style={th}>Rostered</th><th style={th}>Status</th><th style={th}></th></tr></thead>
              <tbody>
                {sheets.map(s => {
                  const sum = s.summary || {};
                  const hcOk = sum.headcountMatches;
                  return (
                    <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => openSheet(s.id)}>
                      <td style={{ ...td, fontWeight: 700 }}>{new Date(s.shiftDate + 'T00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td style={{ ...td, color: 'var(--hp-dim)' }}>{s.sheetDate ? new Date(s.sheetDate + 'T00:00').toLocaleDateString('en-AU') : '—'}</td>
                      <td style={td}>{sum.signedOn != null ? sum.signedOn : '—'}</td>
                      <td style={td}>{s.headcount != null ? <span style={pill(hcOk === false ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)', hcOk === false ? 'var(--hp-red, #EF4444)' : 'var(--hp-green, #22C55E)')}>{s.headcount}{hcOk === false ? ' ≠' : ' ✓'}</span> : '—'}</td>
                      <td style={td}>{sum.rosteredCount != null ? sum.rosteredCount : '—'}</td>
                      <td style={td}>{s.status === 'Reviewed' ? <span style={pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)')}><CheckCircle2 size={11} /> Reviewed</span> : <span style={pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)')}>Parsed</span>}</td>
                      <td style={{ ...td, textAlign: 'right' }}><ChevronRight size={14} color="var(--hp-dim)" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {active && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', margin: 0 }}>Sheet · shift night {new Date(active.shiftDate + 'T00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</h2>
            {active.viewUrl && <a href={active.viewUrl} target="_blank" rel="noreferrer" style={{ ...ghost, textDecoration: 'none' }}><Eye size={13} /> Full size</a>}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button onClick={reocrSheet} disabled={busy === 'reocr'} title="Run the automatic read again on the stored photo" style={ghost}>
                {busy === 'reocr' ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : <ScanLine size={13} />} {busy === 'reocr' ? 'Reading…' : 'Re-read'}
              </button>
              {active.status !== 'Reviewed' && <button onClick={() => patchSheet({ status: 'Reviewed' }, 'Marked reviewed.')} style={btn('var(--hp-green, #22C55E)')}><Check size={14} /> Mark reviewed</button>}
              <button onClick={deleteSheet} style={{ ...ghost, color: 'var(--hp-red, #EF4444)' }}><Trash2 size={13} /> Delete</button>
              <button onClick={() => setActive(null)} style={ghost}><X size={13} /> Close</button>
            </div>
          </div>

          {active.viewUrl && (
            <a href={active.viewUrl} target="_blank" rel="noreferrer" title="Open the sheet full size in a new tab">
              <img src={active.viewUrl} alt="AP sign-in sheet"
                style={{ display: 'block', maxWidth: '100%', maxHeight: 440, borderRadius: 10, border: '1px solid var(--hp-border)', marginBottom: 14 }} />
            </a>
          )}

          {!((active.ocr && active.ocr.entries) || []).length && (
            <div style={{ ...pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)'), padding: '8px 12px', marginBottom: 12 }}>
              <AlertTriangle size={13} /> The automatic read didn't complete for this sheet — press <strong>Re-read</strong> above. The photo is safely stored either way.
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
            <div>
              <label style={label}>Shift night (corrected if the paper date is wrong)</label>
              <input type="date" style={{ ...input, width: 170, marginBottom: 0 }} value={active.shiftDate} onChange={e => e.target.value && patchSheet({ shiftDate: e.target.value }, 'Shift night moved — re-checked against that night\'s roster.')} />
            </div>
            {summary && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={pill('rgba(201,168,76,0.14)', 'var(--hp-gold)')}>{summary.signedOn} signed on</span>
                <span style={pill('rgba(201,168,76,0.14)', 'var(--hp-gold)')}>{summary.rosteredCount} rostered</span>
                {summary.headcountCircled != null && (
                  summary.headcountMatches
                    ? <span style={pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)')}><CheckCircle2 size={12} /> AP circled {summary.headcountCircled} — matches</span>
                    : <span style={pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)')}><AlertTriangle size={12} /> AP circled {summary.headcountCircled}, sheet shows {summary.signedOn}</span>
                )}
              </div>
            )}
          </div>

          {summary && summary.sheetMisses && summary.sheetMisses.length > 0 && (
            <div style={{ ...pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)'), padding: '8px 12px', marginBottom: 12, display: 'flex', flexWrap: 'wrap' }}>
              <AlertTriangle size={13} /> Rostered but not on the sheet at all: {summary.sheetMisses.map(m => m.name || m.login).join(', ')} — did they forget to sign?
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead><tr><th style={th}>Name on sheet</th><th style={th}>Matched to</th><th style={th}>In</th><th style={th}>Out</th><th style={th}>App clock</th><th style={th}>Checks</th></tr></thead>
              <tbody>
                {entries.map((en, i) => (
                  <tr key={i} style={{ opacity: en.notRostered ? 0.55 : 1 }}>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {en.name}{en.runner ? <span style={{ ...pill('rgba(201,168,76,0.14)', 'var(--hp-gold)'), marginLeft: 6, fontSize: '0.62rem' }}>RUNNER</span> : null}
                      {en.handwritten && <div style={{ fontSize: '0.68rem', color: 'var(--hp-amber, #F59E0B)' }}>handwritten</div>}
                    </td>
                    <td style={td}>
                      {en.matchedLogin ? en.matchedName : (
                        <select defaultValue="" style={{ ...input, width: 160, marginBottom: 0, padding: '5px 8px', fontSize: '0.76rem' }}
                          onChange={e => { const p = sorters.find(x => (x.Login || '').toUpperCase() === e.target.value); if (p) patchSheet({ entryIndex: i, entryLogin: (p.Login || '').toUpperCase(), entryName: p.Name || p.Login }, `Linked to ${p.Name || p.Login} — remembered: this name will auto-link on every future sheet.`); }}>
                          <option value="" disabled>Link to…</option>
                          {sorters.map(p => <option key={p.Login} value={(p.Login || '').toUpperCase()}>{p.Name || p.Login}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)' }}>{en.notRostered ? '—' : (en.signIn || '·')}{en.signInRaw && en.signIn !== en.signInRaw && !en.notRostered ? <span style={{ color: 'var(--hp-dim)', fontSize: '0.68rem' }}> (“{en.signInRaw}”)</span> : null}</td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)' }}>{en.notRostered ? '—' : (en.signOut || '·')}</td>
                    <td style={{ ...td, fontSize: '0.76rem', color: 'var(--hp-dim)' }}>{en.appClock ? `${en.appClock.workedHours}h · ${en.appClock.status}` : '—'}</td>
                    <td style={td}>
                      {(en.flags || []).length
                        ? en.flags.map((f, j) => <span key={j} style={{ ...pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)'), marginRight: 4, marginBottom: 3, fontSize: '0.64rem' }}><AlertTriangle size={9} />{f}</span>)
                        : <span style={{ color: 'var(--hp-green, #22C55E)', fontSize: '0.72rem' }}>✓</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {active.ocr && active.ocr.finishNote && <p style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', marginTop: 10 }}>Notes read from the sheet: “{active.ocr.finishNote}”</p>}
        </div>
      )}
    </>
  );
}

// ── Pay run (gross) — approved hours × loaded rates, per 4-weekly period ──────
const PAY_ANCHOR = '2026-05-17';
const isoDate = (d) => isoLocal(d);
function payPeriodFor(dateISO) {
  const a = new Date(PAY_ANCHOR + 'T00:00'), d = new Date(dateISO + 'T00:00');
  const idx = Math.floor((d - a) / 86400000 / 28);
  const start = new Date(a); start.setDate(a.getDate() + idx * 28);
  const end = new Date(start); end.setDate(start.getDate() + 27);
  return { start: isoDate(start), end: isoDate(end) };
}
const r1 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const fmt$ = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PayrollView() {
  const [period, setPeriod] = useState(() => payPeriodFor(isoLocal(new Date())));
  const [run, setRun] = useState(null);
  const [card, setCard] = useState(null);      // the rate card — Xero's own pay items
  const [sync, setSync] = useState(null);      // last "compare with Xero" result
  const [loading, setLoading] = useState(true);
  const [fin, setFin] = useState(false);
  const [finMsg, setFinMsg] = useState('');
  const [otForm, setOtForm] = useState(null); // overtime config editor
  const [otBusy, setOtBusy] = useState(false);
  const [showRates, setShowRates] = useState(false);   // pay-rate editor panel
  const [rateDraft, setRateDraft] = useState(null);    // { effectiveFrom, L1, L2, L3 } bases
  const [rateBusy, setRateBusy] = useState(false);
  const [slipCfg, setSlipCfg] = useState(null);        // employer identity + period pay dates
  const [slipBusy, setSlipBusy] = useState('');        // '' | 'all' | a login
  const [slipErr, setSlipErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, rc, sc] = await Promise.all([
        api('buildSorterPayRun', { from: period.start, to: period.end }),
        api('getSorterRateCard', {}),
        api('getSorterPayslipSettings', {}),
      ]);
      setRun(pr && pr.success ? pr : { run: [], totalGross: 0, count: 0 });
      if (pr && pr.otConfig) setOtForm(pr.otConfig);
      setCard(rc && rc.success ? rc.card : null);
      setSlipCfg(sc && sc.success ? sc : null);
    } catch (e) { setRun({ run: [], totalGross: 0, count: 0 }); } finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); }, [load]);
  async function saveOt() {
    setOtBusy(true);
    try { const r = await api('saveSorterOtConfig', otForm); if (!r.success) throw new Error(r.error || 'Failed'); await load(); }
    catch (e) { setFinMsg(e.message); setTimeout(() => setFinMsg(''), 5000); } finally { setOtBusy(false); }
  }
  // Xero is the source of truth for what an hour is worth, so the normal way to change a
  // rate is to read it back OUT of Xero. Dated, because Xero holds only one current value
  // per pay item — the app is the only record of when it changed, and a period already paid
  // has to keep the rate it was paid at.
  async function syncFromXero(apply, effectiveFrom) {
    setRateBusy(true); setFinMsg('');
    try {
      const r = await fetch('/.netlify/functions/xero?action=sorter-rate-sync', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apply: !!apply, effectiveFrom }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.error) throw new Error(d.error);
      setSync(d);
      if (apply) { await load(); setFinMsg(`Rates read from Xero, effective ${d.effectiveFrom}`); }
      else if (!d.changes.length) setFinMsg('Already identical to Xero');
    } catch (e) { setFinMsg(e.message); } finally { setRateBusy(false); setTimeout(() => setFinMsg(''), 7000); }
  }
  async function saveCard(next) {
    setRateBusy(true); setFinMsg('');
    try {
      const r = await api('saveSorterRateCard', { card: next, source: 'manual' });
      if (!r.success) throw new Error(r.error || 'Could not save');
      setCard(r.card); setRateDraft(null); await load();
      setFinMsg('Rate card saved');
    } catch (e) { setFinMsg(e.message); } finally { setRateBusy(false); setTimeout(() => setFinMsg(''), 6000); }
  }
  function saveDraft() {
    if (!rateDraft || !card) return;
    const eff = (rateDraft.effectiveFrom || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(eff)) { setFinMsg('Enter an effective date as YYYY-MM-DD'); setTimeout(() => setFinMsg(''), 5000); return; }
    const entry = { effectiveFrom: eff, label: rateDraft.label || '' };
    for (const L of ['L1', 'L2', 'L3']) {
      entry[L] = {};
      for (const k of ['ordinary', 'night', 'casual']) {
        const v = parseFloat((rateDraft[L] || {})[k]);
        if (!(v > 0)) { setFinMsg(`${L} ${k} must be a positive rate`); setTimeout(() => setFinMsg(''), 5000); return; }
        entry[L][k] = v;
      }
    }
    saveCard({ ...card, rates: [...card.rates.filter(r => r.effectiveFrom !== eff), entry] });
  }
  function removeEntry(effectiveFrom) {
    if (!card || card.rates.length <= 1) { setFinMsg('The card needs at least one dated set of rates'); setTimeout(() => setFinMsg(''), 5000); return; }
    if (!window.confirm(`Remove the rates effective ${effectiveFrom}?\n\nShifts from that date will be priced by whichever set applies before it.`)) return;
    saveCard({ ...card, rates: card.rates.filter(r => r.effectiveFrom !== effectiveFrom) });
  }

  // ── detailed payslips ──────────────────────────────────────────────────────
  // Built from getSorterPayslipDetail, which prices through the same priceShift the run
  // above uses — so a statement cannot say a different number from the run it came from.
  const fetchDetail = (login) => api('getSorterPayslipDetail', { login, from: period.start, to: period.end });
  async function downloadOne(login) {
    setSlipBusy(login); setSlipErr('');
    try {
      const d = await fetchDetail(login);
      if (!d || !d.success) throw new Error((d && d.error) || 'Could not build the statement');
      const { downloadSorterPayslip } = await import('../utils/sorterPayslip');
      await downloadSorterPayslip(d);
    } catch (e) { setSlipErr(e.message); setTimeout(() => setSlipErr(''), 7000); } finally { setSlipBusy(''); }
  }
  async function downloadAll() {
    if (!run || !run.run || !run.run.length) return;
    setSlipBusy('all'); setSlipErr('');
    try {
      const details = [];
      for (const p of run.run) {
        const d = await fetchDetail(p.login);
        if (d && d.success) details.push(d);
      }
      if (!details.length) throw new Error('Nothing to build');
      const { downloadSorterPayslipBundle } = await import('../utils/sorterPayslip');
      await downloadSorterPayslipBundle(details, `Payslip details ${period.start} to ${period.end}.pdf`);
    } catch (e) { setSlipErr(e.message); setTimeout(() => setSlipErr(''), 7000); } finally { setSlipBusy(''); }
  }
  async function saveSlipCfg(patch) {
    setSlipBusy('cfg'); setSlipErr('');
    try {
      const r = await api('saveSorterPayslipSettings', patch);
      if (!r.success) throw new Error(r.error || 'Could not save');
      setSlipCfg(r); setFinMsg('Payslip settings saved');
    } catch (e) { setSlipErr(e.message); } finally { setSlipBusy(''); setTimeout(() => setFinMsg(''), 5000); }
  }

  const otField = (k, label, step, suffix) => (
    <label style={{ display: 'inline-flex', flexDirection: 'column', gap: 3, fontSize: '0.64rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <input type="number" step={step} value={otForm ? otForm[k] : ''} onChange={e => setOtForm(f => ({ ...f, [k]: e.target.value }))} style={{ ...input, width: 62, padding: '5px 7px', textAlign: 'center', fontWeight: 700 }} />
        {suffix && <span style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>{suffix}</span>}
      </span>
    </label>
  );

  const shift = (n) => { const s = new Date(period.start + 'T00:00'); s.setDate(s.getDate() + n * 28); setPeriod(payPeriodFor(isoDate(s))); };
  async function finalise() {
    setFin(true); setFinMsg('');
    try { const r = await api('finaliseSorterPayRun', { from: period.start, to: period.end }); if (!r.success) throw new Error(r.error || 'Failed'); setFinMsg(`Finalised ${r.finalised} payslip${r.finalised === 1 ? '' : 's'} ✓`); }
    catch (e) { setFinMsg(e.message); } finally { setFin(false); setTimeout(() => setFinMsg(''), 6000); }
  }

  const th = { padding: '8px', fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right', whiteSpace: 'nowrap' };
  const td = { padding: '8px', fontSize: '0.84rem', borderTop: '1px solid var(--hp-border)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  // The row in effect TODAY per level — latest 'Effective From' on or before today. The old
  // first-match-per-level went stale as soon as a dated rise was added behind it.
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
  const entries = (card && card.rates) || [];
  const inForce = entries.filter(r => r.effectiveFrom <= todayStr).slice(-1)[0] || entries[0] || null;

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <DollarSign size={18} color="var(--hp-gold)" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Pay run</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
          <button onClick={() => shift(-1)} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '6px 8px' }}><ChevronLeft size={14} /></button>
          <span style={{ fontWeight: 700, fontSize: '0.82rem', minWidth: 184, textAlign: 'center' }}>{period.start} → {period.end}</span>
          <button onClick={() => shift(1)} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '6px 8px' }}><ChevronRight size={14} /></button>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {finMsg && <span style={{ fontSize: '0.78rem', color: 'var(--hp-green, #22C55E)' }}>{finMsg}</span>}
          <button onClick={load} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}><RefreshCw size={14} /> Refresh</button>
          <button onClick={finalise} disabled={fin || !run || !run.run || !run.run.length} style={btn('var(--hp-gold)')}>{fin ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Check size={14} />} Finalise</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        {inForce && ['L1', 'L2', 'L3'].map(L => (
          <div key={L} style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: '7px 11px', fontSize: '0.74rem' }}>
            <strong style={{ color: 'var(--hp-gold)' }}>{L}</strong> · {fmt$(inForce[L].total)}/h
            <span style={{ color: 'var(--hp-dim)', marginLeft: 6 }}>
              {fmt$(inForce[L].ordinary)} + {fmt$(inForce[L].night)} + {fmt$(inForce[L].casual)} · from {inForce.effectiveFrom}
            </span>
          </div>
        ))}
        <button onClick={() => setShowRates(v => !v)} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '7px 12px', fontSize: '0.74rem' }}>
          <Pencil size={13} /> {showRates ? 'Hide rate card' : 'Rate card'}
        </button>
      </div>

      {showRates && card && (
        <div style={{ padding: '14px 16px', marginBottom: 16, background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-gold)' }}>Rate card</div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button onClick={() => syncFromXero(false)} disabled={rateBusy} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '6px 11px', fontSize: '0.74rem' }}>
                <RefreshCw size={13} /> Compare with Xero
              </button>
            </div>
          </div>
          <p style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', margin: '0 0 12px' }}>
            <strong>Xero is the source of truth.</strong> These are its three pay items per level, and a shift is paid all three added
            together — <em>every day of the week, Sunday included</em>. Read them out of Xero rather than typing them: deriving a penalty
            from the base drifts cents, because each penalty dollar is uplifted on its own. Rates are <strong>effective-dated</strong>, so
            a rise never re-prices shifts already paid — add a date instead of editing the set in force.
          </p>

          {sync && (
            <div style={{ background: sync.changes.length ? 'rgba(245,158,11,.08)' : 'rgba(34,197,94,.08)', border: `1px solid ${sync.changes.length ? 'rgba(245,158,11,.28)' : 'rgba(34,197,94,.28)'}`, borderRadius: 8, padding: '9px 13px', marginBottom: 12, fontSize: '0.79rem' }}>
              {sync.changes.length ? <>
                <strong>Xero differs from the card:</strong>
                {sync.changes.map((c, i) => <div key={i} style={{ marginTop: 3 }}>{c.level} {c.component}: {c.was == null ? '—' : fmt$(c.was)} → <strong>{fmt$(c.now)}</strong></div>)}
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--hp-dim)' }}>Apply from</span>
                  <input type="date" defaultValue={period.start} id="rateSyncFrom" style={{ ...input, width: 160, padding: '6px 9px' }} />
                  <button onClick={() => syncFromXero(true, document.getElementById('rateSyncFrom').value)} disabled={rateBusy} style={{ ...btn('var(--hp-gold)'), padding: '7px 13px', fontSize: '0.78rem' }}>
                    <Save size={13} /> Take Xero&apos;s rates
                  </button>
                  <span style={{ color: 'var(--hp-dim)', fontSize: '0.74rem' }}>Xero prices a whole period at the rate in force when the run is posted — dating this to the period start is what makes the two agree.</span>
                </div>
              </> : <>Every rate matches Xero exactly.{sync.org ? ` (${sync.org})` : ''}</>}
              {!!(sync.problems || []).length && <div style={{ color: 'var(--hp-red, #EF4444)', marginTop: 6 }}>{sync.problems.join(' · ')}</div>}
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>Effective from</th><th style={{ ...th, textAlign: 'left' }}>Level</th>
              <th style={th}>Ordinary</th><th style={th}>Night penalty</th><th style={th}>Casual loading</th><th style={th}>Paid per hour</th><th style={th} />
            </tr></thead>
            <tbody>
              {entries.map(r => ['L1', 'L2', 'L3'].map((L, i) => {
                const live = inForce && r.effectiveFrom === inForce.effectiveFrom;
                const future = r.effectiveFrom > todayStr;
                return (
                  <tr key={r.effectiveFrom + L} style={{ opacity: live || future ? 1 : 0.5 }}>
                    <td style={{ ...td, textAlign: 'left' }}>
                      {i === 0 && <>{r.effectiveFrom}
                        {r.label && <span style={{ color: 'var(--hp-dim)', marginLeft: 6, fontSize: '0.75rem' }}>{r.label}</span>}
                        {live && <span style={{ marginLeft: 7, fontSize: '0.66rem', color: 'var(--hp-green, #22C55E)', fontWeight: 800 }}>IN FORCE</span>}
                        {future && <span style={{ marginLeft: 7, fontSize: '0.66rem', color: 'var(--hp-gold)', fontWeight: 800 }}>SCHEDULED</span>}
                      </>}
                    </td>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{L}</td>
                    <td style={td}>{fmt$(r[L].ordinary)}</td>
                    <td style={td}>{fmt$(r[L].night)}</td>
                    <td style={td}>{fmt$(r[L].casual)}</td>
                    <td style={{ ...td, fontWeight: 800 }}>{fmt$(r[L].total)}</td>
                    <td style={{ ...td, width: 40 }}>
                      {i === 0 && <button onClick={() => removeEntry(r.effectiveFrom)} disabled={rateBusy} title="Remove this dated set"
                        style={{ background: 'transparent', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 4 }}><Trash2 size={13} /></button>}
                    </td>
                  </tr>
                );
              }))}
            </tbody>
          </table>

          {rateDraft ? (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.64rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Effective from
                <input type="date" value={rateDraft.effectiveFrom} onChange={e => setRateDraft(d => ({ ...d, effectiveFrom: e.target.value }))} style={{ ...input, width: 156, padding: '7px 9px' }} />
              </label>
              {['L1', 'L2', 'L3'].map(L => (
                <div key={L} style={{ display: 'flex', gap: 5, alignItems: 'flex-end' }}>
                  {['ordinary', 'night', 'casual'].map(k => (
                    <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.62rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{L} {k.slice(0, 4)}
                      <input type="number" step="0.01" value={(rateDraft[L] || {})[k] || ''} onChange={e => setRateDraft(d => ({ ...d, [L]: { ...(d[L] || {}), [k]: e.target.value } }))}
                        style={{ ...input, width: 76, padding: '7px 8px', textAlign: 'right', fontWeight: 700 }} />
                    </label>
                  ))}
                </div>
              ))}
              <button onClick={saveDraft} disabled={rateBusy} style={btn('var(--hp-gold)')}>
                {rateBusy ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Save size={14} />} Save rates
              </button>
              <button onClick={() => setRateDraft(null)} disabled={rateBusy} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => setRateDraft({ effectiveFrom: todayStr, L1: {}, L2: {}, L3: {} })} style={{ ...btn('transparent'), color: 'var(--hp-gold)', border: '1px solid var(--hp-gold)' }}>
              <DollarSign size={14} /> New dated rates
            </button>
          )}
        </div>
      )}

      {/* Overtime rules — casual/shift/weekend penalties are already in the rates above */}
      {otForm && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', padding: '12px 14px', marginBottom: 16, background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-gold)', alignSelf: 'center' }}>Overtime</div>
          {otField('threshold', 'Over / shift', '0.5', 'h')}
          {otField('tier1Hours', 'First tier', '0.5', 'h')}
          {otField('tier1', 'Tier 1 ×', '0.05', '×')}
          {otField('tier2', 'Tier 2 ×', '0.05', '×')}
          {otField('casualLoading', 'Casual load', '0.01', '')}
          <button onClick={saveOt} disabled={otBusy} style={{ ...btn('var(--hp-gold)'), padding: '8px 14px' }}>{otBusy ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Check size={13} />} Save</button>
          <span style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', flex: 1, minWidth: 200, alignSelf: 'center' }}>OT applies only to hours beyond {otForm.threshold}h/shift, on the ordinary rate incl {Math.round((otForm.casualLoading || 0) * 100)}% casual loading (weekend/PH penalty not compounded). Sorter shifts are ~6h so this rarely fires.</span>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--hp-dim)', padding: 20 }}><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>
      ) : !run || !run.run || !run.run.length ? (
        <div style={{ color: 'var(--hp-dim)', padding: '24px 0', textAlign: 'center' }}>No approved hours in this period yet — approve timesheets first.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>Sorter</th><th style={{ ...th, textAlign: 'left' }}>Lvl</th>
              <th style={th}>Hrs</th><th style={th} title="Overtime hours (beyond the threshold)">OT</th><th style={th} title="Before tax and other deductions">Before tax</th><th style={th}>Super</th><th style={th} />
            </tr></thead>
            <tbody>
              {run.run.map(p => (
                <tr key={p.login}>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{p.name}</td>
                  <td style={{ ...td, textAlign: 'left' }}>{p.level}</td>
                  <td style={td}>{r1(p.totalHours)}</td>
                  <td style={{ ...td, color: p.otHours > 0 ? 'var(--hp-amber, #F59E0B)' : 'var(--hp-dim)', fontWeight: p.otHours > 0 ? 700 : 400 }}>{p.otHours > 0 ? r1(p.otHours) : '—'}</td>
                  <td style={{ ...td, fontWeight: 700, fontFamily: 'var(--font-mono, monospace)' }}>{fmt$(p.gross)}</td>
                  <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)', color: 'var(--hp-dim)' }}>{fmt$(p.superAmt)}</td>
                  <td style={{ ...td, padding: '4px 8px' }}>
                    <button onClick={() => downloadOne(p.login)} disabled={!!slipBusy}
                      title={`Detailed payslip for ${p.name} — every shift in this period`}
                      style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '4px 9px', fontSize: '0.72rem' }}>
                      {slipBusy === p.login ? <Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> : <FileText size={12} />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr style={{ borderTop: '2px solid var(--hp-border)' }}>
              <td style={{ ...td, textAlign: 'left', fontWeight: 700 }} colSpan={2}>{run.count} sorter{run.count === 1 ? '' : 's'}</td>
              <td style={td}></td><td style={td}></td>
              <td style={{ ...td, fontWeight: 800, color: 'var(--hp-gold)', fontFamily: 'var(--font-mono, monospace)' }}>{fmt$(run.totalGross)}</td>
              <td style={{ ...td, fontWeight: 700, fontFamily: 'var(--font-mono, monospace)' }}>{fmt$(run.totalSuper)}</td>
              <td style={td} />
            </tr></tfoot>
          </table>

          {/* Pay categories — hours + $ per earnings rate (the Xero-ready breakdown) */}
          {run.categoryTotals && run.categoryTotals.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-dim)', marginBottom: 8 }}>Pay categories (maps to Xero earnings rates)</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {run.categoryTotals.map(c => (
                  <div key={c.key} style={{ border: '1px solid var(--hp-border)', borderRadius: 10, padding: '10px 14px', minWidth: 150 }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{c.label}</div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 800, fontFamily: 'var(--font-mono, monospace)' }}>{fmt$(c.amount)}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{r1(c.hours)} h</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* ── Detailed payslips ─────────────────────────────────────────────────
          The statement that shows a sorter the twenty nights behind the one number on
          their Xero payslip. Tax and net print only once Xero has POSTED the run — a
          draft is still editable, so its figures stay here for the office to check
          against and never reach a sorter as though they were final. */}
      <div style={{ marginTop: 18, padding: '14px 16px', background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-gold)' }}>Detailed payslips</div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            {slipErr && <span style={{ fontSize: '0.76rem', color: 'var(--hp-red, #EF4444)' }}>{slipErr}</span>}
            <button onClick={downloadAll} disabled={!!slipBusy || !run || !run.run || !run.run.length} style={{ ...btn('var(--hp-gold)'), padding: '7px 13px', fontSize: '0.78rem' }}>
              {slipBusy === 'all' ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : <FileText size={13} />}
              {slipBusy === 'all' ? 'Building…' : `Download all (${(run && run.count) || 0})`}
            </button>
          </div>
        </div>
        <p style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', margin: '0 0 12px' }}>
          One branded statement per sorter: every shift in the period with its start, finish, break, hours and rate,
          subtotalled by week, reconciling to the pay item lines above. <strong>Download all</strong> gives one PDF
          holding every statement; the icon on each row gives just that person&apos;s. Sorters can pull their own from
          the portal. <strong>Tax and net appear only once the Xero run is posted</strong> and pulled back on the
          <em> Push to Xero</em> tab.
        </p>

        {slipCfg && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {[['name', 'Employer name', 300], ['abn', 'ABN', 150], ['tradingLine', 'Header line', 130]].map(([k, label, w]) => (
              <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.64rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}
                <input value={(slipCfg.employer || {})[k] || ''} onChange={e => setSlipCfg(c => ({ ...c, employer: { ...c.employer, [k]: e.target.value } }))}
                  style={{ ...input, width: w, padding: '6px 9px' }} />
              </label>
            ))}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.64rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Payment date for this period
              <input type="date" value={(slipCfg.payDates || {})[period.start] || ''}
                onChange={e => setSlipCfg(c => ({ ...c, payDates: { ...c.payDates, [period.start]: e.target.value } }))}
                style={{ ...input, width: 160, padding: '6px 9px' }} />
            </label>
            <button onClick={() => saveSlipCfg({ employer: slipCfg.employer, payDate: { from: period.start, date: (slipCfg.payDates || {})[period.start] || '' } })}
              disabled={slipBusy === 'cfg'} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '7px 13px', fontSize: '0.76rem' }}>
              {slipBusy === 'cfg' ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Save size={13} />} Save
            </button>
          </div>
        )}
      </div>

      <p style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', marginTop: 12 }}>Approved hours × <strong>Xero&apos;s own pay items</strong> — ordinary + night penalty + casual loading, the same three every day of the week. Overtime is the exception: hours past the threshold pay ordinary + casual at the tier multipliers, with no night penalty. The <strong>pay categories</strong> above are the lines that appear on the Xero payslip. Figures here are <strong>before tax and other deductions</strong> — the app does not calculate tax. Xero withholds it, and its posted payslips come back through <strong>Pull payslips back</strong> on the Push to Xero tab.</p>
    </div>
  );
}

// ── Timesheet approvals — actual vs rostered, breaks, rounding, approve/amend ──
const BLUE = 'var(--hp-blue, #3B82F6)';
// Break badge: who recorded it — sorter self-tapped (green), admin-added (blue), missing (red).
function breakBadge(r) {
  const mins = r.breakMins === '' ? 0 : Number(r.breakMins);
  if (r.breakSource === 'sorter') return <span style={pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)')} title={`Sorter took the break themselves${r.breakStart ? ` (${r.breakStart}–${r.breakEnd})` : ''}`}>{mins}m · self ✓</span>;
  if (r.breakSource === 'auto') return <span style={pill('rgba(201,162,39,0.16)', 'var(--hp-gold, #C9A227)')} title="Required 30-min break auto-applied at clock-off (Sun–Thu)">{mins}m · auto</span>;
  if (r.breakSource === 'admin') return <span style={pill('rgba(59,130,246,0.14)', BLUE)} title="Break added by admin">{mins}m · admin</span>;
  return <span style={pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)')} title="No break recorded by the sorter">none ⚠</span>;
}


// ── Push to Xero — approved hours → Xero Payroll timesheets ──────────────────
// A timesheet carries HOURS against an earnings rate; Xero's own pay items hold the
// dollars, so PAYG, super and STP stay Xero-native and each penalty is its own payslip
// line. Everything lands as DRAFT: a person still opens the pay run in Xero and posts it.
function XeroPushView() {
  const [period, setPeriod] = useState(() => payPeriodFor(isoLocal(new Date())));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [openLogin, setOpenLogin] = useState('');
  const [slips, setSlips] = useState(null);   // "pull payslips back" result
  const [slipBusy, setSlipBusy] = useState(false);

  const call = useCallback(async (mode) => {
    const r = await fetch('/.netlify/functions/xero?action=sorter-timesheets', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ from: period.start, to: period.end, mode }),
    });
    const d = await r.json().catch(() => ({}));
    if (d.error) throw new Error(d.error);
    return d;
  }, [period]);

  const load = useCallback(async () => {
    setLoading(true); setErr(''); setResult(null);
    try { setData(await call('preview')); }
    catch (e) { setErr(e.message); setData(null); } finally { setLoading(false); }
  }, [call]);
  useEffect(() => { load(); }, [load]);

  async function push() {
    const t = data && data.totals;
    if (!window.confirm(`Send ${t.sorters} timesheet(s) — ${t.hours} hours — to Xero as DRAFT for ${fmtD(period.start)} – ${fmtD(period.end)}?\n\nNothing is paid until someone opens the pay run in Xero and posts it. A draft already sitting there for this period is replaced; one that has been through a pay run is left alone.`)) return;
    setBusy(true); setErr('');
    try { const r = await call('push'); setResult(r); await load(); setResult(r); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  const shift = (n) => {
    const d = new Date(period.start + 'T00:00'); d.setDate(d.getDate() + n * 28);
    setPeriod(payPeriodFor(isoLocal(d)));
  };
  // The other direction: once a pay run is POSTED, bring Xero's real payslips back so the
  // portal shows what was actually paid — gross, tax withheld, deductions, net — instead of
  // the app's own arithmetic.
  async function pullPayslips() {
    setSlipBusy(true); setErr('');
    try {
      const r = await fetch('/.netlify/functions/xero?action=sorter-payslip-sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const d = await r.json().catch(() => ({}));
      if (d.error) throw new Error(d.error);
      setSlips(d);
    } catch (e) { setErr(e.message); } finally { setSlipBusy(false); }
  }
  const th2 = { padding: '8px', fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' };
  const td2 = { padding: '7px 8px', fontSize: '0.84rem', borderTop: '1px solid var(--hp-border)' };
  const num = { ...td2, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const t = data && data.totals;

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <Send size={18} color="var(--hp-gold)" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Push to Xero</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 12 }}>
          <button onClick={() => shift(-1)} style={{ ...btn('transparent'), padding: 7, color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}><ChevronLeft size={15} /></button>
          <span style={{ fontSize: '0.85rem', fontWeight: 700, minWidth: 168, textAlign: 'center' }}>{fmtD(period.start)} – {fmtD(period.end)}</span>
          <button onClick={() => shift(1)} style={{ ...btn('transparent'), padding: 7, color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}><ChevronRight size={15} /></button>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={load} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}><RefreshCw size={14} /> Refresh</button>
          <button onClick={pullPayslips} disabled={slipBusy} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}>
            {slipBusy ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Inbox size={14} />} Pull payslips back
          </button>
          <button onClick={push} disabled={busy || loading || !t || !t.sorters} style={btn(t && t.sorters ? 'var(--hp-gold)' : 'var(--hp-border)')}>
            {busy ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Send size={14} />} Send as draft
          </button>
        </div>
      </div>
      <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 14px' }}>
        Approved hours for this period, split into the earnings rates that make up each shift&apos;s loaded rate —
        ordinary, night penalty, casual loading — so every penalty is visible on the Xero payslip.
        {data && data.org ? <> Posting to <strong>{data.org}</strong>.</> : null}
      </p>

      {err && <div style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, padding: '9px 13px', marginBottom: 14, fontSize: '0.8rem' }}>
        <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} />{err}
      </div>}
      {loading && <div><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Building the timesheets and reading your Xero pay items…</div>}

      {slips && (
        <div style={{ background: 'rgba(34,197,94,.07)', border: '1px solid rgba(34,197,94,.28)', borderRadius: 8, padding: '10px 13px', marginBottom: 14, fontSize: '0.8rem' }}>
          <strong>{slips.payslips} payslips</strong> across {slips.periods} periods came back from {slips.org} — the portal now shows what was actually paid, tax and all.
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead><tr><th style={th2}>Period</th><th style={{ ...th2, textAlign: 'right' }}>People</th><th style={{ ...th2, textAlign: 'right' }}>Gross</th><th style={{ ...th2, textAlign: 'right' }}>Tax</th><th style={th2}>Paid</th></tr></thead>
            <tbody>
              {slips.summary.map(p => (
                <tr key={p.from}>
                  <td style={td2}>{fmtD(p.from)} – {fmtD(p.to)}</td>
                  <td style={num}>{p.people}</td><td style={num}>{fmt$(p.gross)}</td><td style={num}>{fmt$(p.tax)}</td>
                  <td style={{ ...td2, color: 'var(--hp-dim)' }}>{p.paymentDate ? fmtD(p.paymentDate) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!!(slips.unmappedEmployees || []).length && <div style={{ color: 'var(--hp-dim)', marginTop: 8, fontSize: '0.75rem' }}>
            Not filed against anyone — {slips.unmappedEmployees.length} Xero {slips.unmappedEmployees.length === 1 ? 'employee' : 'employees'} on the sorter calendar with no mapping (past staff, mostly): {slips.unmappedEmployees.slice(0, 6).join(', ')}{slips.unmappedEmployees.length > 6 ? '…' : ''}
          </div>}
        </div>
      )}

      {/* A period that has been PAID and no longer matches our hours. Someone amended a
          shift after the run was posted — resolving a dispute, usually — so the difference
          is owed and can only reach them in a later period. Nothing else in the app would
          notice; a paid period is otherwise never looked at again. */}
      {(() => {
        const posted = (slips && (slips.summary || []).find(x => x.from === period.start && x.slips));
        if (!posted || !data || !data.timesheets) return null;
        const off = data.timesheets.map(ts => {
          const x = (posted.slips || {})[ts.login];
          return x ? { name: ts.name, login: ts.login, ours: ts.appGross, paid: x.gross, diff: +(ts.appGross - x.gross).toFixed(2) } : null;
        }).filter(r => r && Math.abs(r.diff) >= 0.01);
        if (!off.length) return null;
        return (
          <div style={{ background: 'rgba(239,68,68,.07)', border: '1px solid rgba(239,68,68,.32)', borderRadius: 8, padding: '10px 13px', marginBottom: 14, fontSize: '0.8rem' }}>
            <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6, color: 'var(--hp-red, #EF4444)' }} />
            <strong>{off.length} {off.length === 1 ? 'person has' : 'people have'} had hours changed since this period was PAID.</strong> The
            run cannot be re-cut — carry the difference into a later period, and tell them, or they are simply short.
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
              <thead><tr><th style={th2}>Sorter</th><th style={{ ...th2, textAlign: 'right' }}>Hours now worth</th><th style={{ ...th2, textAlign: 'right' }}>Actually paid</th><th style={{ ...th2, textAlign: 'right' }}>Owed</th></tr></thead>
              <tbody>{off.map(r => (
                <tr key={r.login}>
                  <td style={td2}>{r.name}</td><td style={num}>{fmt$(r.ours)}</td><td style={num}>{fmt$(r.paid)}</td>
                  <td style={{ ...num, fontWeight: 800, color: r.diff > 0 ? 'var(--hp-red, #EF4444)' : 'var(--hp-dim)' }}>{r.diff > 0 ? fmt$(r.diff) : `(${fmt$(-r.diff)} over)`}</td>
                </tr>))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* ── The run that is about to happen ────────────────────────────────────
          A DRAFT pay run already carries Xero's tax, days before the money moves —
          which is the only useful moment to check it. Amber, not green, and per-person
          against what we expect Xero to pay: a difference here is a wrong payslip you can
          still stop. Nothing from a draft reaches a sorter; the statement prints tax only
          from a posted run. */}
      {slips && !!(slips.draftSummary || []).length && (
        <div style={{ background: 'rgba(245,158,11,.07)', border: '1px solid rgba(245,158,11,.30)', borderRadius: 8, padding: '10px 13px', marginBottom: 14, fontSize: '0.8rem' }}>
          <strong>Scheduled in Xero, not yet paid.</strong> These figures are real but still editable, so they stay here —
          a sorter&apos;s statement shows tax only once the run is posted. Check them now: after Monday it is a correction, not a fix.
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead><tr><th style={th2}>Period</th><th style={{ ...th2, textAlign: 'right' }}>People</th><th style={{ ...th2, textAlign: 'right' }}>Gross</th><th style={{ ...th2, textAlign: 'right' }}>Tax</th><th style={th2}>Pays</th></tr></thead>
            <tbody>
              {slips.draftSummary.map(p => (
                <tr key={p.from}>
                  <td style={td2}>{fmtD(p.from)} – {fmtD(p.to)}</td>
                  <td style={num}>{p.people}</td><td style={num}>{fmt$(p.gross)}</td><td style={num}>{fmt$(p.tax)}</td>
                  <td style={{ ...td2, color: 'var(--hp-dim)' }}>{p.paymentDate ? fmtD(p.paymentDate) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(() => {
            // Per-person, but only for the period on screen — anything else is comparing
            // this period's hours against another period's pay run.
            const draft = (slips.draftSummary || []).find(x => x.from === period.start);
            if (!draft || !data || !data.timesheets) return null;
            const rows = data.timesheets.map(ts => {
              const x = (draft.slips || {})[ts.login];
              return { name: ts.name, ours: ts.xeroGross, theirs: x ? x.gross : null, tax: x ? x.tax : null, net: x ? x.net : null };
            });
            const off = rows.filter(r => r.theirs != null && Math.abs(r.theirs - r.ours) >= 0.02);
            return (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: '0.75rem', color: off.length ? 'var(--hp-red, #EF4444)' : 'var(--hp-green, #22C55E)', fontWeight: 700, marginBottom: 4 }}>
                  {off.length
                    ? `${off.length} of ${rows.length} differ from the hours we sent — check these before it posts`
                    : `All ${rows.length} match the hours we sent, to the cent`}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th2}>Sorter</th><th style={{ ...th2, textAlign: 'right' }}>We expect</th><th style={{ ...th2, textAlign: 'right' }}>Xero draft</th><th style={{ ...th2, textAlign: 'right' }}>Tax</th><th style={{ ...th2, textAlign: 'right' }}>Net</th></tr></thead>
                  <tbody>
                    {rows.map(r => {
                      const bad = r.theirs != null && Math.abs(r.theirs - r.ours) >= 0.02;
                      return (
                        <tr key={r.name}>
                          <td style={td2}>{r.name}</td>
                          <td style={num}>{fmt$(r.ours)}</td>
                          <td style={{ ...num, color: r.theirs == null ? 'var(--hp-dim)' : bad ? 'var(--hp-red, #EF4444)' : undefined, fontWeight: bad ? 800 : 400 }}>
                            {r.theirs == null ? 'not in the run' : fmt$(r.theirs)}
                          </td>
                          <td style={num}>{r.tax == null ? '—' : fmt$(r.tax)}</td>
                          <td style={num}>{r.net == null ? '—' : fmt$(r.net)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {!loading && data && (
        <>
          {(data.notes || []).map((n, i) => (
            <div key={i} style={{ background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.28)', borderRadius: 8, padding: '9px 13px', marginBottom: 8, fontSize: '0.79rem' }}>
              <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6, color: 'var(--hp-amber, #F59E0B)' }} />{n}
            </div>
          ))}

          {!!(data.blocked || []).length && (
            <table style={{ width: '100%', borderCollapse: 'collapse', margin: '10px 0 18px' }}>
              <thead><tr><th style={th2}>Held back</th><th style={th2}>Hours</th><th style={th2}>Why</th></tr></thead>
              <tbody>
                {data.blocked.map(b => (
                  <tr key={b.login}>
                    <td style={td2}>{b.name}<div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{b.login}</div></td>
                    <td style={num}>{b.hours}</td>
                    <td style={{ ...td2, color: 'var(--hp-red, #EF4444)', fontSize: '0.78rem' }}>{b.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th2}>Sorter</th><th style={{ ...th2, textAlign: 'right' }}>Shifts</th><th style={{ ...th2, textAlign: 'right' }}>Hours</th>
              <th style={{ ...th2, textAlign: 'right' }}>App ledger</th><th style={{ ...th2, textAlign: 'right' }}>Xero will pay</th>
              <th style={{ ...th2, textAlign: 'right' }}>Difference</th><th style={th2}></th>
            </tr></thead>
            <tbody>
              {(data.timesheets || []).map(ts => {
                const pushed = (result && (result.results || []).find(r => r.login === ts.login)) || null;
                const diff = r1(ts.xeroGross - ts.appGross);
                return (
                  <Fragment key={ts.login}>
                    <tr>
                      <td style={td2}>
                        {ts.name}
                        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{ts.login} · {ts.levels.join('/')}</div>
                      </td>
                      <td style={num}>{ts.shifts}</td>
                      <td style={num}>{ts.hours}</td>
                      <td style={num}>{fmt$(ts.appGross)}</td>
                      <td style={num}>{fmt$(ts.xeroGross)}</td>
                      <td style={{ ...num, color: diff ? 'var(--hp-amber, #F59E0B)' : 'var(--hp-dim)' }}>{diff ? (diff > 0 ? '+' : '') + fmt$(diff) : '—'}</td>
                      <td style={{ ...td2, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {pushed && (pushed.ok ? ok(pushed.updated ? 'draft updated' : 'draft created') : warn(pushed.skipped ? pushed.status : 'failed'))}
                        <button onClick={() => setOpenLogin(openLogin === ts.login ? '' : ts.login)} style={{ ...btn('transparent'), padding: '4px 8px', marginLeft: 8, fontSize: '0.74rem', color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}>
                          <Eye size={12} /> lines
                        </button>
                      </td>
                    </tr>
                    {openLogin === ts.login && (
                      <tr><td colSpan={7} style={{ ...td2, background: 'rgba(255,255,255,0.02)' }}>
                        {ts.lines.map(l => (
                          <div key={l.key + l.level} style={{ display: 'flex', gap: 10, fontSize: '0.78rem', padding: '2px 0' }}>
                            <span style={{ flex: 1 }}>{l.rateName || l.label}</span>
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{l.hours} h × {fmt$(l.unitRate || 0)} = {fmt$(l.amount || 0)}</span>
                          </div>
                        ))}
                        {ts.dayHours && !!ts.dayHours.sunday && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 6 }}>
                          Includes {ts.dayHours.sunday} h on a Sunday — paid at the same rate as any other shift, which is what Xero pays.
                        </div>}
                        {!!ts.excluded.length && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 6 }}>
                          Not included: {ts.excluded.map(x => `${fmtD(x.date)} ${x.hours}h (${x.status})`).join(', ')}
                        </div>}
                        {pushed && pushed.error && <div style={{ fontSize: '0.76rem', color: 'var(--hp-red, #EF4444)', marginTop: 6 }}>{pushed.error}</div>}
                      </td></tr>
                    )}
                  </Fragment>
                );
              })}
              {t && (
                <tr style={{ fontWeight: 800 }}>
                  <td style={{ ...td2, borderTop: '2px solid var(--hp-border)' }}>{t.sorters} sorter{t.sorters === 1 ? '' : 's'}</td>
                  <td style={{ ...num, borderTop: '2px solid var(--hp-border)' }} />
                  <td style={{ ...num, borderTop: '2px solid var(--hp-border)' }}>{t.hours}</td>
                  <td style={{ ...num, borderTop: '2px solid var(--hp-border)' }}>{fmt$(t.appGross)}</td>
                  <td style={{ ...num, borderTop: '2px solid var(--hp-border)' }}>{fmt$(t.xeroGross)}</td>
                  <td style={{ ...num, borderTop: '2px solid var(--hp-border)', color: 'var(--hp-amber, #F59E0B)' }}>{fmt$(r1(t.xeroGross - t.appGross))}</td>
                  <td style={{ ...td2, borderTop: '2px solid var(--hp-border)' }} />
                </tr>
              )}
            </tbody>
          </table>

          {result && <div style={{ marginTop: 14, fontSize: '0.82rem' }}>
            {result.pushed} timesheet(s) sent as draft{result.failed ? `, ${result.failed} failed` : ''} — open the pay run in Xero to review and post it.
          </div>}
          <p style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', marginTop: 12 }}>
            <strong>App ledger</strong> is what the pay run on the Payroll tab computes; <strong>Xero will pay</strong> prices the same
            hours at Xero&apos;s own pay items. They differ wherever Xero has no rate for the day type, or a base rate moved mid-period —
            a timesheet carries hours, never dollars.
          </p>
        </>
      )}
    </div>
  );
}

// ── Bank + super, read back out of Xero Payroll ───────────────────────────────
// Xero already holds every mapped sorter's bank account and super fund. The registry
// only knew what had been typed into the portal, so the tax & super chase asked people
// for details the office had. This copies Xero's into blanks (never over what's there)
// and names any sorter whose portal details differ from what Xero pays. The Monday
// sweep does the same before it chases anyone.
const PROFILE_LABEL = {
  fill: ['Copy from Xero', 'var(--hp-gold)'], same: ['Matches Xero', 'var(--hp-green, #22C55E)'],
  differs: ['Differs from Xero', 'var(--hp-red, #EF4444)'], none: ['Not in Xero', 'var(--hp-amber, #F59E0B)'],
  unmapped: ['Not mapped', 'var(--hp-dim)'], unreadable: ['Could not read', 'var(--hp-red, #EF4444)'],
};
function XeroProfileSyncCard() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  async function run(apply) {
    setBusy(apply ? 'apply' : 'preview'); setErr('');
    try {
      const r = await fetch('/.netlify/functions/xero?action=sorter-profile-sync', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ apply }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.error) throw new Error(d.error);
      setRes(d);
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  }

  const pending = res ? res.rows.filter(r => r.bank === 'fill' || r.super === 'fill').length : 0;
  const pill = (k) => { const [t, c] = PROFILE_LABEL[k] || [k, 'var(--hp-dim)']; return <span style={{ fontSize: '0.74rem', fontWeight: 700, color: c }}>{t}</span>; };
  const th2 = { padding: '8px', fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' };
  const td2 = { padding: '7px 8px', fontSize: '0.84rem', borderTop: '1px solid var(--hp-border)', verticalAlign: 'top' };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <ShieldCheck size={18} color="var(--hp-gold)" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Bank &amp; super from Xero</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => run(false)} disabled={!!busy} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}>
            {busy === 'preview' ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Eye size={14} />} Compare with Xero
          </button>
          {res && !res.applied && pending > 0 && (
            <button onClick={() => run(true)} disabled={!!busy} style={btn('var(--hp-gold)')}>
              {busy === 'apply' ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Save size={14} />} Copy {pending} into the app
            </button>
          )}
        </div>
      </div>
      <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 12px' }}>
        Fills a sorter&apos;s blank bank account or super fund from their Xero Payroll record, so the tax &amp; super chase
        doesn&apos;t ask them for it. Nothing already in the app is overwritten — a difference is listed for you to check in Xero.
        The Monday sweep runs this before it chases anyone.
      </p>
      {err && <div style={{ color: 'var(--hp-red, #EF4444)', fontSize: '0.8rem', marginBottom: 10 }}><AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} />{err}</div>}
      {res && (
        <>
          {res.applied && <div style={{ fontSize: '0.82rem', marginBottom: 10, color: res.failed.length ? 'var(--hp-red, #EF4444)' : 'var(--hp-green, #22C55E)' }}>
            Copied {res.bankFilled} bank account(s) and {res.superFilled} super fund(s) from Xero.{res.failed.length ? ` ${res.failed.length} could not be saved: ${res.failed.map(f => f.login).join(', ')}.` : ''}
          </div>}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th2}>Sorter</th><th style={th2}>Bank</th><th style={th2}>Super</th><th style={th2}>Notes</th></tr></thead>
              <tbody>
                {res.rows.slice().sort((a, b) => a.name.localeCompare(b.name)).map(r => (
                  <tr key={r.login}>
                    <td style={td2}>{r.name}<div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>{r.login}</div></td>
                    <td style={td2}>{res.applied && r.bank === 'fill' ? pill('same') : pill(r.bank)}{r.xeroAccLast3 && <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>Xero ••{r.xeroAccLast3}</div>}</td>
                    <td style={td2}>{res.applied && r.super === 'fill' ? pill('same') : pill(r.super)}{r.xeroFund && <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>{r.xeroFund}</div>}</td>
                    <td style={{ ...td2, fontSize: '0.76rem', color: 'var(--hp-dim)' }}>{r.notes.join(' · ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Xero mapping — login → EmployeeID, category/level → EarningsRateID ────────
// Lists come live from Xero (payroll-discover) so the dropdowns can only ever offer real
// IDs. Matching by NAME is not an option: the org has two earnings rates both called
// "Night Shift Penalty Sorters Level 1", and two employee records for Jowher Sayem.
function XeroMapView({ sorters }) {
  const [map, setMap] = useState(null);
  const [xero, setXero] = useState(null);      // { employees, earningsRates, payrollCalendars }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const m = await api('getSorterXeroMap', {});
      if (m && m.success) setMap(m.map);
      // Same-origin and admin-gated, so the browser session authorises it directly.
      const r = await fetch('/.netlify/functions/xero?action=payroll-discover');
      const d = await r.json().catch(() => ({}));
      if (d && d.error) setErr(d.error);
      else if (d && d.employees) setXero(d);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save() {
    setBusy(true); setMsg(''); setErr('');
    try {
      const r = await api('saveSorterXeroMap', { map });
      if (!r.success) throw new Error(r.error || 'Could not save');
      setMap(r.map); setMsg('Mapping saved');
    } catch (e) { setErr(e.message); } finally { setBusy(false); setTimeout(() => { setMsg(''); }, 5000); }
  }

  if (loading) return <div style={card}><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Reading the mapping and your Xero org…</div>;

  const calId = (map && map.payrollCalendarId) || '';
  // Only employees on the sorter pay calendar can hold sorter timesheets.
  const pool = ((xero && xero.employees) || []).filter(e => !calId || e.calendarId === calId);
  const rateOpts = (xero && xero.earningsRates) || [];
  const dupNames = {};
  rateOpts.forEach(r => { const k = (r.name || '').trim().replace(/\.$/, '').toLowerCase(); dupNames[k] = (dupNames[k] || 0) + 1; });
  const rateLabel = (r) => `${r.name} — ${r.rateType === 'MULTIPLE' ? '×' + r.multiplier : (r.ratePerUnit == null ? 'no rate set' : fmt$(r.ratePerUnit))}${rateProblem(r) ? ' — ' + rateProblem(r) : ''}`;
  // Xero will only put timesheet units against a live, per-hour rate. It rejects anything
  // else with "does not exist or is not valid for timesheets" — which is what the two
  // archived Level 1 pay items did on the first real push — so say so in the dropdown
  // rather than letting someone pick one and meet a 400 at push time.
  const rateProblem = (r) => (r.current === false ? 'ARCHIVED, cannot be used'
    : r.rateType && r.rateType !== 'RATEPERUNIT' ? `${r.rateType}, cannot carry hours`
      : r.unit && String(r.unit).toLowerCase() !== 'hours' ? `measured in ${r.unit}` : '');
  const usedBy = {};
  Object.entries((map && map.employees) || {}).forEach(([lg, id]) => { if (id) usedBy[id] = lg; });

  // Same convention as the rest of the page: a blank Active means active. Test logins are
  // never mapped — they have no Xero record and must not reserve one.
  const active = sorters.filter(s => {
    const lg = (s.Login || s.login || '').toUpperCase();
    return (s.Active || 'Y').toUpperCase() === 'Y' && !lg.startsWith('TEST') && !lg.startsWith('ZZ');
  }).sort((a, b) => (a.Name || a.name || '').localeCompare(b.Name || b.name || ''));
  const CATS = [
    { key: 'ordinary', label: 'Ordinary hours', hint: 'The base rate. Xero “Sorter Ordinary Hours – Level n”.' },
    { key: 'night', label: 'Night shift penalty 30%', hint: 'Applies to every night sort.' },
    { key: 'casual', label: 'Casual loading 25%', hint: 'Applies to every shift.' },
    { key: 'saturday', label: 'Saturday (peak)', hint: 'Only the peak-Christmas Saturdays. Xero “Peak Period Shift Level n” is ×1.5.' },
    { key: 'sunday', label: 'Sunday penalty', hint: 'No Sunday rate exists in Xero — leave unset unless one is added.' },
    { key: 'publicHoliday', label: 'Public holiday penalty', hint: 'No public-holiday rate exists in Xero.' },
  ];
  const setEmp = (login, id) => setMap(m => ({ ...m, employees: { ...m.employees, [login]: id } }));
  const setRate = (cat, lvl, id) => setMap(m => ({ ...m, earningsRates: { ...m.earningsRates, [cat]: { ...(m.earningsRates[cat] || {}), [lvl]: id } } }));

  const th2 = { padding: '8px', fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' };
  const td2 = { padding: '7px 8px', fontSize: '0.84rem', borderTop: '1px solid var(--hp-border)' };
  const sel = { ...input, padding: '7px 9px', fontSize: '0.8rem' };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <ArrowLeftRight size={18} color="var(--hp-gold)" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Xero mapping</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {msg && <span style={{ fontSize: '0.78rem', color: 'var(--hp-green, #22C55E)' }}>{msg}</span>}
          <button onClick={load} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}><RefreshCw size={14} /> Reload from Xero</button>
          <button onClick={save} disabled={busy} style={btn('var(--hp-gold)')}>{busy ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Save size={14} />} Save mapping</button>
        </div>
      </div>
      <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 14px' }}>
        Which Xero employee and which Xero earnings rate each sorter&apos;s hours are posted against.
        Options are read live from {xero && xero.org ? <strong>{xero.org}</strong> : 'Xero'}, so they are always real IDs.
      </p>
      {err && <div style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, padding: '9px 13px', marginBottom: 14, fontSize: '0.8rem' }}>
        <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} />{err}
      </div>}

      <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-gold)', margin: '4px 0 8px' }}>People</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 22 }}>
        <thead><tr><th style={th2}>Sorter</th><th style={th2}>Login</th><th style={th2}>Xero employee</th></tr></thead>
        <tbody>
          {active.map(s => {
            const login = s.Login || s.login;
            const cur = (map.employees || {})[login] || '';
            const clash = cur && usedBy[cur] && usedBy[cur] !== login;
            return (
              <tr key={login}>
                <td style={td2}>{s.Name || s.name}</td>
                <td style={{ ...td2, color: 'var(--hp-dim)', fontSize: '0.78rem' }}>{login}</td>
                <td style={td2}>
                  <select value={cur} onChange={e => setEmp(login, e.target.value)} style={{ ...sel, width: '100%', maxWidth: 380, borderColor: clash ? 'var(--hp-red, #EF4444)' : (cur ? 'var(--hp-border)' : 'var(--hp-gold)') }}>
                    <option value="">— not mapped: hours for this person will be refused —</option>
                    {pool.map(e => <option key={e.id} value={e.id}>{e.first} {e.last}</option>)}
                  </select>
                  {clash && <div style={{ fontSize: '0.72rem', color: 'var(--hp-red, #EF4444)', marginTop: 3 }}>Also mapped to {usedBy[cur]} — one person&apos;s hours would land on the other&apos;s payslip.</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-gold)', margin: '4px 0 8px' }}>Earnings rates</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th2}>Category</th><th style={th2}>Level 1</th><th style={th2}>Level 2</th><th style={th2}>Level 3</th></tr></thead>
        <tbody>
          {CATS.map(c => (
            <tr key={c.key}>
              <td style={{ ...td2, maxWidth: 200 }}>
                <div style={{ fontWeight: 700 }}>{c.label}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{c.hint}</div>
              </td>
              {['L1', 'L2', 'L3'].map(lvl => {
                const cur = ((map.earningsRates || {})[c.key] || {})[lvl] || '';
                const chosen = rateOpts.find(r => r.id === cur);
                const isDup = chosen && dupNames[(chosen.name || '').trim().replace(/\.$/, '').toLowerCase()] > 1;
                return (
                  <td key={lvl} style={td2}>
                    <select value={cur} onChange={e => setRate(c.key, lvl, e.target.value)} style={{ ...sel, width: '100%' }}>
                      <option value="">— none —</option>
                      {rateOpts.map(r => <option key={r.id} value={r.id}>{rateLabel(r)}</option>)}
                    </select>
                    {chosen && rateProblem(chosen)
                      ? <div style={{ fontSize: '0.7rem', color: 'var(--hp-red, #EF4444)', marginTop: 3 }}>Xero will refuse this one — {rateProblem(chosen)}. Pick the live rate of the same name.</div>
                      : isDup && <div style={{ fontSize: '0.7rem', color: 'var(--hp-gold)', marginTop: 3 }}>Xero has more than one rate with this name — mapped by ID, so this is the right one.</div>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 12 }}>
        A category left as <strong>none</strong> blocks the push for any shift that needs it, rather than quietly posting those hours at the ordinary rate.
      </p>
    </div>
  );
}

// What the sorter said about their own shift, on the approvals row. Small on purpose —
// most rows are 'confirmed' or unanswered, and only a dispute needs the eye.
const SORTER_SAYS = {
  confirmed: { text: 'sorter ✓', bg: 'rgba(34,197,94,0.12)', fg: 'var(--hp-green, #22C55E)', tip: 'The sorter checked this shift and confirmed the hours.' },
  disputed:  { text: 'sorter ⚠ disputed', bg: 'rgba(239,68,68,0.14)', fg: 'var(--hp-red, #EF4444)', tip: 'The sorter says this shift is wrong.' },
  resolved:  { text: 'dispute answered', bg: 'rgba(212,175,55,0.14)', fg: 'var(--hp-gold)', tip: 'The sorter disputed this shift and you answered.' },
  changed:   { text: 'edited since ✓', bg: 'rgba(245,158,11,0.14)', fg: 'var(--hp-amber, #F59E0B)', tip: 'The sorter confirmed this shift, then the times changed — they are being asked again.' },
};
function SorterSaysChip({ entry }) {
  const look = entry && SORTER_SAYS[entry.state];
  if (!look) return null;
  return (
    <div style={{ marginTop: 4 }} title={(look.tip + (entry.disputeNote ? `\n\n“${entry.disputeNote}”` : '')).trim()}>
      <span style={{ ...pill(look.bg, look.fg), fontSize: '0.66rem' }}>{look.text}</span>
    </div>
  );
}

// Open disputes for the pay period, and how ready the period is to be paid. Sits above the
// approvals table because a disputed shift is the one thing here that should stop a pay run.
function DisputesPanel({ disputes, counts, period, sorters, busy, onResolve, onGoTo, flash }) {
  const [showWho, setShowWho] = useState(false);
  if (!period) return null;
  const checked = (counts.confirmed || 0) + (counts.resolved || 0);
  const waiting = (sorters || []).filter(s => !s.done);
  const done = (sorters || []).filter(s => s.done);
  // The chase list as text, for the WhatsApp group. Names only — the group is not the
  // place for anyone's hours.
  const copyWaiting = async () => {
    const txt = waiting.length
      ? `Still to check their shifts for ${fmtD(period.from)} – ${fmtD(period.to)}:\n`
        + waiting.map(s => `• ${s.name} (${s.toCheck} of ${s.total})`).join('\n')
      : `Everyone has checked their shifts for ${fmtD(period.from)} – ${fmtD(period.to)}.`;
    try { await navigator.clipboard.writeText(txt); flash && flash('Chase list copied to the clipboard.'); }
    catch (_) { flash && flash('Could not copy — select the names manually.', false); }
  };
  const row = (s) => (
    <div key={s.login} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid var(--hp-border)' }}>
      <span style={{ flex: 1, fontSize: '0.82rem', fontWeight: 600 }}>{s.name}</span>
      {!!s.disputed && <span style={{ ...pill('rgba(239,68,68,0.14)', 'var(--hp-red, #EF4444)'), fontSize: '0.64rem' }}>{s.disputed} disputed</span>}
      {!!s.changed && <span style={{ ...pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)'), fontSize: '0.64rem' }} title="Confirmed, then the shift was edited — they are being asked again.">{s.changed} re-check</span>}
      <span style={{ fontSize: '0.76rem', color: s.done ? 'var(--hp-green, #22C55E)' : 'var(--hp-dim)', fontFamily: 'var(--font-mono, monospace)', minWidth: 92, textAlign: 'right' }}>
        {s.done ? `all ${s.total} checked` : `${s.toCheck} of ${s.total} to go`}
      </span>
    </div>
  );
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
        <span>Sorters&apos; own check, period {fmtD(period.from)} – {fmtD(period.to)}:</span>
        <span style={{ ...pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)'), fontSize: '0.7rem' }}>{checked} of {counts.total || 0} checked</span>
        {!!counts.awaiting && <span style={{ ...pill('rgba(148,163,184,0.16)', 'var(--hp-dim)'), fontSize: '0.7rem' }}>{counts.awaiting} not answered</span>}
        {!!counts.disputed && <span style={{ ...pill('rgba(239,68,68,0.14)', 'var(--hp-red, #EF4444)'), fontSize: '0.7rem' }}>{counts.disputed} disputed</span>}
        {/* Not a chase number — these shifts are still on OUR side. Approve them and they
            go to the sorter to check; until then nobody has been asked anything. */}
        {!!counts.notReady && (
          <span style={{ ...pill('rgba(59,130,246,0.14)', BLUE), fontSize: '0.7rem' }}
            title="Shifts in this period that aren't approved yet. They are not shown to the sorter and nobody is being chased for them — approve them and they go across to be checked.">
            {counts.notReady} not approved yet
          </span>
        )}
        {!!(sorters || []).length && (
          <button onClick={() => setShowWho(v => !v)} style={{ ...btn('transparent'), color: 'var(--hp-gold)', border: '1px solid var(--hp-gold)', padding: '4px 10px', fontSize: '0.72rem' }}>
            <Users size={12} /> {showWho ? 'Hide' : `Who's checked (${done.length}/${sorters.length})`}
          </button>
        )}
      </div>

      {showWho && (
        <div style={{ marginTop: 10, border: '1px solid var(--hp-border)', borderRadius: 8, padding: '12px 14px', background: 'var(--hp-card, #1E1E24)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 800, fontSize: '0.84rem', color: 'var(--hp-amber, #F59E0B)' }}>Waiting on {waiting.length}</span>
            {!!waiting.length && (
              <button onClick={copyWaiting} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '4px 9px', fontSize: '0.7rem', marginLeft: 'auto' }}>
                <Copy size={11} /> Copy the chase list
              </button>
            )}
          </div>
          {waiting.length ? waiting.map(row)
            : <p style={{ fontSize: '0.8rem', color: 'var(--hp-green, #22C55E)', margin: '4px 0 0' }}>Everyone has checked their shifts for this period.</p>}

          <div style={{ fontWeight: 800, fontSize: '0.84rem', color: 'var(--hp-green, #22C55E)', margin: '16px 0 6px' }}>Checked {done.length}</div>
          {done.length ? done.map(row)
            : <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', margin: '4px 0 0' }}>Nobody has checked this period yet.</p>}

          <p style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', margin: '12px 0 0' }}>
            Sorters are only asked about a shift once it is <strong>Approved</strong> — an unapproved night is not on their phone and does not count against them here.
            A shift someone has reported as wrong counts as answered — it is with you, not with them, so it does not put them on the chase list.
          </p>
        </div>
      )}
      {!!disputes.length && (
        <div style={{ marginTop: 10, border: '1px solid var(--hp-red, #EF4444)', borderRadius: 8, padding: 12, background: 'rgba(239,68,68,0.06)' }}>
          <div style={{ fontWeight: 800, fontSize: '0.86rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} color="var(--hp-red, #EF4444)" /> {disputes.length} shift{disputes.length === 1 ? '' : 's'} the sorter says {disputes.length === 1 ? 'is' : 'are'} wrong
          </div>
          {disputes.map(d => (
            <div key={d.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px solid var(--hp-border)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem' }}>
                  {d.name || d.login} · {fmtD(d.date)} · {d.hours}h
                  {/* An older dispute nobody answered still shows here — it doesn't stop
                      mattering because the screen moved on to the next period. */}
                  {d.otherPeriod && <span style={{ ...pill('rgba(148,163,184,0.16)', 'var(--hp-dim)'), fontSize: '0.62rem', marginLeft: 6 }}>another period</span>}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--hp-text)', marginTop: 2 }}>“{d.note}”</div>
              </div>
              <button onClick={() => onGoTo(d.date)} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '5px 9px', fontSize: '0.72rem' }}>Open night</button>
              <button onClick={() => onResolve(d)} disabled={!!busy} style={{ ...btn('var(--hp-gold)'), padding: '5px 10px', fontSize: '0.72rem' }}>Answer</button>
            </div>
          ))}
          <p style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', margin: '8px 0 0' }}>Fix the times first if they&apos;re wrong, then answer — your reply is shown to the sorter in their portal.</p>
        </div>
      )}
    </div>
  );
}

function ApprovalsView({ testMode, sorters }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editHrs, setEditHrs] = useState('');
  const [timesId, setTimesId] = useState(null);   // row whose actual in/out is being edited
  const [tIn, setTIn] = useState(''); const [tOut, setTOut] = useState('');
  const [slots, setSlots] = useState([]);       // active slot templates — options for the "covering for" picker
  const [seedWeek, setSeedWeek] = useState(() => sundayOf(isoLocal(new Date())));
  // Filters + sort
  const [statusFilter, setStatusFilter] = useState('Pending'); // Pending | Approved | All
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [sortKey, setSortKey] = useState('date'); const [sortDir, setSortDir] = useState('desc');
  const [apFinish, setApFinish] = useState({}); // shiftDate → AP-reported finish HH:mm
  const [rosterCount, setRosterCount] = useState(0); // rostered sorters for the picked night
  const [capWatch, setCapWatch] = useState({});   // login → visa-cap fortnight tally (capped people only)
  const [attempts, setAttempts] = useState([]);   // the picked night's REJECTED clock attempts (QR/geofence refusals)
  // Add-a-shift by hand — the catch-all for anyone who isn't on this page at all.
  const [addForm, setAddForm] = useState(null);   // null = closed · {login,date,slot,start,end,breakMins,reason,force}
  const [addBusy, setAddBusy] = useState(false);
  const [addDup, setAddDup] = useState('');       // duplicate warning from the server
  // The "No clock-in" row being written down as an absence — {ID,login,date,name,slot,reason,note}
  const [absentFor, setAbsentFor] = useState(null);
  // What the sorters themselves said about these shifts — id → {state, disputeNote, …}.
  // Their sign-off never gates a pay run (you pay someone whether or not they tapped a
  // button); an OPEN DISPUTE is the thing that should stop a line going out unchecked.
  const [review, setReview] = useState({ review: {}, counts: {}, disputes: [] });

  const load = useCallback(async () => {
    setRows(null);
    try {
      // On a single picked night (not the Approved-only view) also pull the roster so
      // sorters who never scanned the QR show as "No clock-in" rows and can be authorised.
      const rosterNight = (from && from === to && statusFilter !== 'Approved') ? from : '';
      const r = await api('getSorterTimesheetsAdmin', { status: statusFilter, from, to, limit: 800, rosterNight });
      let out = r && r.success ? r.timesheets : [];
      if (statusFilter === 'All') out = out.filter(x => x.Status !== 'Discarded'); // hide discarded from the combined view
      setRows(out);
      // Visa-cap watch (monitoring only): fortnight hours (night sort + driving
      // combined) vs the 48h cap, for any visa-capped sorter on screen.
      try {
        const logins = [...new Set(out.map(x => (x.Login || '').toUpperCase()).filter(Boolean))];
        if (logins.length) {
          const cw = await fetch('/.netlify/functions/driver-dms-sb', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'capWatch', logins }),
          }).then(x => x.json());
          setCapWatch((cw && cw.capWatch) || {});
        } else setCapWatch({});
      } catch (_) { /* best-effort — approvals work without the watch */ }
      setRosterCount(r && r.success ? (r.rosterCount || 0) : 0);
      setSlots(r && r.success ? (r.slots || []) : []);
      // AP-reported finish times for the covered nights (so we don't toggle to Actual times)
      const ds = out.map(x => x.Date).filter(Boolean).sort();
      const f = from || ds[0], t = to || ds[ds.length - 1];
      if (f && t) { const fr = await api('nsListFinishTimes', { from: f, to: t }); const m = {}; if (fr && fr.success) (fr.finishes || []).forEach(x => { if (x.finishTime) m[x.shiftDate] = x.finishTime; }); setApFinish(m); }
      else setApFinish({});
      // Rejected clock attempts for the night — proof of who TRIED but was refused
      // (outside fence, no GPS, wrong/old QR), so a "the app wouldn't let me" claim
      // is checkable against data instead of taken on faith.
      try {
        if (rosterNight) { const ar = await api('getSorterClockAttempts', { night: rosterNight }); setAttempts((ar && ar.success && ar.attempts) || []); }
        else setAttempts([]);
      } catch (_) { setAttempts([]); }
      // The sorters' own answers for the pay period these rows sit in.
      try {
        const rv = await api('getSorterReviewAdmin', { from: from || (out[0] && out[0].Date) || isoLocal(new Date()) });
        setReview(rv && rv.success ? rv : { review: {}, counts: {}, disputes: [] });
      } catch (_) { /* approvals still work without it */ }
    } catch (e) { setRows([]); }
  }, [statusFilter, from, to]);
  useEffect(() => { load(); }, [load]);

  // AP finish per row, offset by role: runners (rostered end later) finish that much
  // after the AP time; the AP time anchors to the earliest rostered end each night.
  const hm = (s) => { const p = (s || '').split(':'); return p.length < 2 ? null : (+p[0]) * 60 + (+p[1]); };
  const mh = (m) => { const x = ((m % 1440) + 1440) % 1440; return String(Math.floor(x / 60)).padStart(2, '0') + ':' + String(x % 60).padStart(2, '0'); };
  const baseEndByNight = {};
  (rows || []).forEach(r => { const e = hm(r.rosteredEnd); if (e == null) return; if (baseEndByNight[r.Date] == null || e < baseEndByNight[r.Date]) baseEndByNight[r.Date] = e; });
  const apFinishFor = (r) => {
    const ap = apFinish[r.Date]; if (!ap) return null;
    const rowEnd = hm(r.rosteredEnd), base = baseEndByNight[r.Date];
    const offset = (rowEnd != null && base != null) ? rowEnd - base : 0;
    return mh((hm(ap) || 0) + offset);
  };

  // Date-range presets (Sydney-local today). Populate From/To.
  const setPreset = (p) => {
    const t = new Date();
    if (p === 'all') { setFrom(''); setTo(''); return; }
    if (p === 'day') { const s = isoLocal(t); setFrom(s); setTo(s); return; }
    if (p === 'week') { const s = new Date(t); s.setDate(t.getDate() - t.getDay()); const e = new Date(s); e.setDate(s.getDate() + 6); setFrom(isoLocal(s)); setTo(isoLocal(e)); return; }
    if (p === 'month') { setFrom(isoLocal(new Date(t.getFullYear(), t.getMonth(), 1))); setTo(isoLocal(new Date(t.getFullYear(), t.getMonth() + 1, 0))); return; }
    if (p === 'period') { const s = new Date(t); s.setDate(t.getDate() - 27); setFrom(isoLocal(s)); setTo(isoLocal(t)); return; }
  };
  const toggleSort = (k) => { if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(k); setSortDir(k === 'date' ? 'desc' : 'asc'); } };
  const sorted = (rows || []).slice().sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'sorter') return dir * (a.Name || a.Login || '').localeCompare(b.Name || b.Login || '');
    if (sortKey === 'slot') return dir * (a.slot || '').localeCompare(b.slot || '');
    return dir * ((a.Date || '').localeCompare(b.Date || '') || (a.Name || '').localeCompare(b.Name || '')); // date
  });
  const pendingRows = (rows || []).filter(r => r.Status === 'Pending');
  // Shift-night picked → roster merge is live. Rostered no-shows (never scanned the QR),
  // and the QR login/logout snapshot for the night.
  const nightPicked = from && from === to ? from : '';
  const missingRows = (rows || []).filter(r => r.Status === 'Missing');
  // An authorised-to-scheduled row carries a synthetic clock-in — the sorter did NOT scan
  // the QR, so keep those out of the real "used the app" tally.
  const isAuthorised = (r) => (r.flags || []).some(f => (f || '').includes('authorised to scheduled'));
  // Sorters recorded as AWAY for the night — sick, on leave, a no-show or not needed. Not
  // a shift and never paid: they're here so the night reads "9 worked, 1 off sick" instead
  // of a roster of 10 with one gap nobody wrote down.
  const absentRows = (rows || []).filter(r => r.Status === 'Absent');
  const awayReason = (r) => ((r.flags || []).find(f => /^Away — /.test(f || '')) || '').replace(/^Away — /, '') || 'Away';
  // Who was actually expected to turn up: the roster, less the people we know were away.
  const expectedCount = Math.max(0, rosterCount - absentRows.length);
  // Rejected-attempt helpers: Sydney HH:mm + a plain-English reason per gate code.
  const attHHmm = (iso) => { try { return new Intl.DateTimeFormat('en-GB', { timeZone: 'Australia/Sydney', hourCycle: 'h23', hour: '2-digit', minute: '2-digit' }).format(new Date(iso)); } catch (_) { return ''; } };
  const attReason = (a) => a.gate === 'OUTSIDE_FENCE' ? `${a.distance != null ? a.distance + 'm ' : ''}outside the fence`
    : a.gate === 'GPS_REQUIRED' ? 'no location' : a.gate === 'WRONG_QR' ? 'scanned the wrong code'
    : a.gate === 'QR_REQUIRED' ? 'missing/old QR code' : a.gate === 'NO_OPEN_SHIFT' ? 'no open shift'
    // Not the sorter's doing — the server couldn't read or save. The portal retries these on
    // its own and records the time they tapped, so a run of them is one tap, not many.
    : a.gate === 'LOOKUP_FAILED' ? 'server couldn\'t read (app retried)' : a.gate === 'WRITE_FAILED' ? 'server couldn\'t save (app retried)'
    : (a.message || 'refused');
  const attLine = (a) => `${attHHmm(a.ts)} clock-${a.dir === 'out' ? 'off' : 'on'} — ${attReason(a)}`;
  const attemptsFor = (login) => attempts.filter(a => (a.login || '').toUpperCase() === (login || '').toUpperCase());
  const nightRows = nightPicked ? (rows || []) : [];
  const realClockedIn = nightRows.filter(r => r.Status !== 'Missing' && r.actualIn && !isAuthorised(r));
  const clockedInOut = realClockedIn.filter(r => r.actualOut);
  const inNoOut = realClockedIn.filter(r => !r.actualOut);
  const authorisedNoQr = nightRows.filter(isAuthorised);
  const noApp = missingRows.length + authorisedNoQr.length; // rostered sorters who never used the app
  const snapshotText = () => {
    const line = (r) => `${r.Name || r.Login} — ${r.slot || 'no slot'}`;
    const L = [];
    L.push(`Night Sort — QR clock snapshot for ${fmtDayDate(nightPicked)}`);
    L.push(`${realClockedIn.length} of ${expectedCount} expected sorters used the QR to clock in; ${noApp} did not.`);
    if (absentRows.length) L.push(`${rosterCount} were rostered — ${absentRows.length} recorded as away.`);
    if (clockedInOut.length) { L.push('', `Clocked in & out (${clockedInOut.length}):`); clockedInOut.forEach(r => L.push(`  ✓ ${line(r)} (${r.actualIn}–${r.actualOut})`)); }
    if (inNoOut.length) { L.push('', `Clocked in, NO clock-out (${inNoOut.length}):`); inNoOut.forEach(r => L.push(`  ⚠ ${line(r)} (in ${r.actualIn})`)); }
    if (authorisedNoQr.length) { L.push('', `Authorised without the QR (${authorisedNoQr.length}):`); authorisedNoQr.forEach(r => L.push(`  ● ${line(r)}`)); }
    if (missingRows.length) { L.push('', `No clock-in, not yet authorised (${missingRows.length}):`); missingRows.forEach(r => L.push(`  ✗ ${line(r)}`)); }
    if (absentRows.length) { L.push('', `Away, did not work (${absentRows.length}):`); absentRows.forEach(r => L.push(`  — ${line(r)} · ${awayReason(r)}`)); }
    if (attempts.length) { L.push('', `Rejected scans (${attempts.length}):`); attempts.forEach(a => L.push(`  ⊘ ${a.name || a.login} · ${attLine(a)}`)); }
    return L.join('\n');
  };

  const flash = (text, ok = true) => { setToast({ text, ok }); setTimeout(() => setToast(null), 5000); };
  async function act(action, id, extra) {
    setBusy(id + action);
    try { const r = await api(action, { id, ...extra }); if (!r.success) throw new Error(r.error || 'Failed'); await load(); }
    catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }
  async function bulk(action, payload, label) {
    setBusy(action);
    try { const r = await api(action, payload); if (!r.success) throw new Error(r.error || 'Failed'); flash(label(r)); await load(); }
    catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }
  async function approveAll() {
    if (!pendingRows.length) return;
    setBusy('all');
    try { const r = await api('approveAllSorterTimesheets', { from, to }); if (!r.success) throw new Error(r.error || 'Failed'); flash(`Approved ${r.approved} timesheet${r.approved === 1 ? '' : 's'}.` + (r.held ? ` ${r.held} held back — impossible hours (${(r.heldRows || []).map(x => `${x.login} ${x.hours}h`).join(', ')}). Fix the times, then approve.` : '')); await load(); }
    catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }
  async function saveAmend(id) { await act('amendSorterTimesheet', id, { workedHours: editHrs }); setEditId(null); setEditHrs(''); }
  async function saveTimes(id) { await act('editSorterTimes', id, { clockIn: tIn, clockOut: tOut }); setTimesId(null); }
  function roundAll() {
    const ds = pendingRows.map(r => r.Date).filter(Boolean).sort();
    if (!ds.length) return;
    bulk('roundSorterTimesheets', { from: ds[0], to: ds[ds.length - 1] }, r => `Rounded ${r.rounded} timesheet${r.rounded === 1 ? '' : 's'} to the nearest 15 min.`);
  }
  // One click: set every pending sorter to their SCHEDULED shift — rostered start + the
  // saved finish time for that night (runners kept +1h automatically). Covering sorters
  // inherit the slot they're marked as covering. Only the exceptions then need a pencil.
  function setNightScheduled() {
    const ds = pendingRows.map(r => r.Date).filter(Boolean).sort();
    if (!ds.length) return;
    bulk('setSorterScheduled', { from: ds[0], to: ds[ds.length - 1] },
      r => `Set ${r.fixed} timesheet${r.fixed === 1 ? '' : 's'} to scheduled${r.noSched ? ` · ${r.noSched} need a slot (use "Covering for…")` : ''}.`);
  }
  // One click: authorise every rostered sorter who never scanned the QR — insert a
  // scheduled Pending row for each (23:15-start, saved AP close, 30-min break), then the
  // operator reviews and Approves all. Nothing is auto-approved.
  function authoriseAllNoShows() {
    if (!nightPicked || !missingRows.length) return;
    bulk('authoriseRosterNoShows', { date: nightPicked },
      r => `Authorised ${r.created} no-show${r.created === 1 ? '' : 's'} to scheduled${r.noSched ? ` · ${r.noSched} had no roster schedule` : ''}. Review then Approve all.`);
  }
  function authoriseOne(r) { act('authoriseScheduledShift', r.ID, { login: r.Login, date: r.Date, name: r.Name, slot: r.slot }); }

  // The third answer to a "No clock-in" row. The first two were "they worked it"
  // (Authorise scheduled, which pays them) and silence — so a sorter who was off sick sat
  // on the page as an open item for ever, and the only way to clear it was to authorise a
  // shift they never worked. This records the absence instead: nought hours, a reason, and
  // the row leaves the outstanding list without anyone being paid for a night off.
  function openAbsent(r) { setAbsentFor({ ID: r.ID, login: r.Login, date: r.Date, name: r.Name || r.Login, slot: r.slot || '', reason: 'Sick', note: '' }); }
  async function saveAbsent() {
    const a = absentFor; if (!a) return;
    setBusy(a.ID + 'markSorterAbsent');
    try {
      const r = await api('markSorterAbsent', { login: a.login, date: a.date, name: a.name, slot: a.slot, reason: a.reason, note: a.note });
      if (!r.success) throw new Error(r.error || 'Failed');
      setAbsentFor(null);
      flash(`${a.name} recorded as away on ${fmtD(a.date)} — ${a.reason.toLowerCase()}. No hours, nothing to approve.`);
      await load();
    } catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }
  // Undo: the absence is discarded and the night goes back to being a "No clock-in" to
  // answer — marked away by mistake must never be a one-way door.
  function undoAbsent(r) { act('clearSorterAbsent', r.ID); }

  // ── Add a shift by hand ────────────────────────────────────────────────────
  // "Authorise no-shows" only reaches people the roster says should have been there. This
  // reaches ANYONE, for any reason — someone who worked unrostered, a replacement nobody
  // logged, or a sorter the app locked out so their shift left no trace at all.
  function openAdd(prefill) {
    setAddDup('');
    setAddForm({ login: '', date: nightPicked || isoLocal(new Date()), slot: '', start: '', end: '', breakMins: '', reason: '', force: false, ...(prefill || {}) });
  }
  const setAdd = (patch) => setAddForm(f => ({ ...f, ...patch }));
  async function submitAdd() {
    if (!addForm || !addForm.login || !addForm.date) { flash('Pick a sorter and a shift night.', false); return; }
    setAddBusy(true);
    try {
      const person = (sorters || []).find(s => (s.Login || '').toUpperCase() === addForm.login);
      const r = await api('addSorterShift', {
        login: addForm.login, name: (person && person.Name) || '', date: addForm.date, slot: addForm.slot,
        start: addForm.start, end: addForm.end, breakMins: addForm.breakMins, reason: addForm.reason, force: !!addForm.force,
      });
      if (!r.success) { if (r.duplicate) setAddDup(r.error); throw new Error(r.error || 'Could not add the shift.'); }
      flash(`Shift added for ${(person && person.Name) || addForm.login} on ${fmtD(addForm.date)} — ${r.workedHours}h, pending your approval.`);
      setAddForm(null); setAddDup('');
      // Jump the view to that night so the new row is on screen to review.
      if (addForm.date !== nightPicked) pickNight(addForm.date); else load();
    } catch (e) { flash(e.message, false); } finally { setAddBusy(false); }
  }
  async function copySnapshot() {
    try { await navigator.clipboard.writeText(snapshotText()); flash('QR snapshot copied to the clipboard.'); }
    catch (_) { flash('Could not copy — select the text manually.', false); }
  }

  // Shift-night picker: pick one night (from == to) and show its Pending sheets.
  // `status` lets a caller land on a night WITHOUT the Pending filter — jumping to a
  // disputed shift needs 'All', because a disputed shift is usually already approved and
  // the Pending view would open on an empty table.
  function pickNight(d, status) { if (!d) { setFrom(''); setTo(''); return; } setFrom(d); setTo(d); setStatusFilter(status || 'Pending'); }
  function stepNight(delta) {
    const base = nightPicked || isoLocal(new Date());
    const dt = new Date(base + 'T00:00:00'); dt.setDate(dt.getDate() + delta);
    pickNight(isoLocal(dt));
  }
  // Mark (or clear) the rostered slot a replacement covered, so they read as rostered.
  function setCovering(id, slot) { act('setSorterCoveringSlot', id, { slot }); }
  // Answer a sorter's dispute. Whatever the outcome, it gets written down and shown back to
  // them in the portal — a dispute that just vanishes off the screen is one nobody can show
  // they dealt with. Fix the times FIRST (pencil), then say what you did.
  function resolveDispute(d) {
    const what = window.prompt(`What did you do about ${d.name || d.login}'s ${fmtD(d.date)} shift?\n\nThey said: "${d.note}"\n\nThis reply is shown to them in the portal.`, '');
    if (what == null || !what.trim()) return;
    act('resolveSorterDispute', d.id, { resolution: what.trim() });
  }

  const th = { padding: '8px', fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', whiteSpace: 'nowrap' };
  const td = { padding: '8px', fontSize: '0.84rem', borderTop: '1px solid var(--hp-border)', verticalAlign: 'top' };
  const iconBtn = { ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '6px 9px', marginRight: 4 };

  return (
    <>
    <FinishTimeCard testMode={testMode} onSaved={load} />
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <ClipboardCheck size={18} color="var(--hp-gold)" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}>Timesheet approvals</h2>
        <span style={pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)')}>{rows ? pendingRows.length : '…'} pending{rows && statusFilter !== 'Pending' ? ` · ${rows.length} shown` : ''}</span>
        {!!missingRows.length && <span style={pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)')}>{missingRows.length} no clock-in</span>}
        {!!absentRows.length && <span style={pill('rgba(148,163,184,0.16)', 'var(--hp-dim)')}>{absentRows.length} away</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={load} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}><RefreshCw size={14} /> Refresh</button>
          <button onClick={() => (addForm ? setAddForm(null) : openAdd())} title="Add a shift by hand for anyone who isn't on this page — worked unrostered, covered for someone, or the app wouldn't let them clock on or off." style={btn(BLUE)}><UserPlus size={14} /> Add shift</button>
          <button onClick={setNightScheduled} disabled={!!busy || !pendingRows.length} title="Set every pending sorter to their scheduled shift — rostered start time and the saved finish time for that night (runners +1h). Then pencil-edit only the exceptions (late in / early out)." style={btn('var(--hp-gold)')}>{busy === 'setSorterScheduled' ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <CalendarDays size={14} />} Set to scheduled</button>
          {!!missingRows.length && <button onClick={authoriseAllNoShows} disabled={!!busy} title="For every rostered sorter who never scanned the QR, insert a scheduled shift (rostered start, saved AP close, 30-min break). They become pending rows to review — nothing is auto-approved." style={btn('var(--hp-amber, #F59E0B)')}>{busy === 'authoriseRosterNoShows' ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <UserPlus size={14} />} Authorise no-shows ({missingRows.length})</button>}
          <button onClick={roundAll} disabled={!!busy || !pendingRows.length} title="Round every pending clock in/out to the nearest 15 minutes" style={{ ...btn('transparent'), color: 'var(--hp-text)', border: '1px solid var(--hp-border)' }}>{busy === 'roundSorterTimesheets' ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Clock size={14} />} Round all to 15</button>
          <button onClick={approveAll} disabled={busy === 'all' || !pendingRows.length} style={btn('var(--hp-green, #22C55E)')}>{busy === 'all' ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Check size={14} />} Approve all{pendingRows.length ? ` (${pendingRows.length})` : ''}</button>
        </div>
      </div>

      {/* Add a shift by hand — anyone, any night, any reason */}
      {addForm && (
        <div style={{ border: `1px solid ${BLUE}`, borderRadius: 10, padding: 14, marginBottom: 12, background: 'rgba(59,130,246,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <UserPlus size={16} color={BLUE} />
            <b style={{ fontSize: '0.92rem' }}>Add a shift</b>
            <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>For anyone who isn't on this page — rostered or not. It lands as pending for you to review and approve.</span>
            <button onClick={() => { setAddForm(null); setAddDup(''); }} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '5px 9px', marginLeft: 'auto' }}><X size={13} /></button>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 210 }}>
              <label style={label}>Sorter</label>
              <select value={addForm.login} onChange={e => { setAdd({ login: e.target.value }); setAddDup(''); }} style={{ ...input, marginBottom: 0 }}>
                <option value="">— pick a sorter —</option>
                {[...(sorters || [])]
                  .sort((a, b) => (a.Name || a.Login || '').localeCompare(b.Name || b.Login || ''))
                  .map(s => <option key={s.Login} value={(s.Login || '').toUpperCase()}>{s.Name || s.Login}{(s.Active || 'Y').toUpperCase() === 'N' ? ' (inactive)' : ''}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Shift night</label>
              <input type="date" value={addForm.date} onChange={e => { setAdd({ date: e.target.value }); setAddDup(''); }} style={{ ...input, width: 160, marginBottom: 0 }} />
            </div>
            <div style={{ minWidth: 180 }}>
              <label style={label}>Position (optional)</label>
              <select value={addForm.slot} onChange={e => setAdd({ slot: e.target.value })} style={{ ...input, marginBottom: 0 }}>
                <option value="">— from their roster —</option>
                {(slots || []).map(s => <option key={s.Slot || s.slot} value={s.Slot || s.slot}>{s.Slot || s.slot}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Start</label>
              <input type="time" value={addForm.start} onChange={e => setAdd({ start: e.target.value })} style={{ ...input, width: 118, marginBottom: 0 }} />
            </div>
            <div>
              <label style={label}>Finish</label>
              <input type="time" value={addForm.end} onChange={e => setAdd({ end: e.target.value })} style={{ ...input, width: 118, marginBottom: 0 }} />
            </div>
            <div>
              <label style={label}>Break (min)</label>
              <input type="number" min="0" placeholder="30" value={addForm.breakMins} onChange={e => setAdd({ breakMins: e.target.value })} style={{ ...input, width: 96, marginBottom: 0 }} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={label}>Reason (kept on the row)</label>
              <input value={addForm.reason} onChange={e => setAdd({ reason: e.target.value })} placeholder="e.g. app wouldn't let her clock on" style={{ ...input, marginBottom: 0 }} />
            </div>
            <button onClick={submitAdd} disabled={addBusy || !addForm.login} style={btn(BLUE)}>{addBusy ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Check size={14} />} Add shift</button>
          </div>
          {addDup && (
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 10px', borderRadius: 8, background: 'rgba(245,158,11,0.10)', border: '1px solid var(--hp-amber, #F59E0B)' }}>
              <AlertTriangle size={14} color="var(--hp-amber, #F59E0B)" />
              <span style={{ fontSize: '0.8rem' }}>{addDup}</span>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!addForm.force} onChange={e => setAdd({ force: e.target.checked })} /> Add anyway
              </label>
            </div>
          )}
          <p style={{ color: 'var(--hp-dim)', fontSize: '0.76rem', margin: '10px 0 0' }}>
            Leave <b>Start</b> and <b>Finish</b> blank to use that night's schedule — their rostered times, or the position's times, with the AP close applied (the same maths as “Set to scheduled”). Leave <b>Break</b> blank for the required 30 minutes on a Sun–Thu night. The row is flagged <b>Added by the office</b> with your reason, and still has to be approved like any other.
          </p>
        </div>
      )}

      {/* Shift night — the primary, one-night-at-a-time flow */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12, padding: '10px 12px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
        <CalendarDays size={16} color="var(--hp-gold)" />
        <span style={{ fontWeight: 700, fontSize: '0.82rem' }}>Shift night</span>
        <button onClick={() => stepNight(-1)} title="Previous night" style={{ ...btn('transparent'), color: 'var(--hp-text)', border: '1px solid var(--hp-border)', padding: '6px 8px' }}><ChevronLeft size={14} /></button>
        <input type="date" value={nightPicked} onChange={e => pickNight(e.target.value)} aria-label="Shift night" style={{ ...input, width: 156 }} />
        <button onClick={() => stepNight(1)} title="Next night" style={{ ...btn('transparent'), color: 'var(--hp-text)', border: '1px solid var(--hp-border)', padding: '6px 8px' }}><ChevronRight size={14} /></button>
        {nightPicked
          ? <button onClick={() => { setFrom(''); setTo(''); }} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', fontSize: '0.75rem' }}>All nights</button>
          : <button onClick={() => pickNight(isoLocal(new Date()))} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', fontSize: '0.75rem' }}>Pick tonight</button>}
        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--hp-dim)' }}>Pick a night → <b style={{ color: 'var(--hp-gold)' }}>Set to scheduled</b> → pencil the exceptions → Approve all.</span>
      </div>

      {/* Filters: status · date range · presets */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'inline-flex', border: '1px solid var(--hp-border)', borderRadius: 8, overflow: 'hidden' }}>
          {[['Pending', 'Unapproved'], ['Approved', 'Approved'], ['All', 'All']].map(([v, lbl]) => (
            <button key={v} onClick={() => setStatusFilter(v)} style={{ padding: '7px 14px', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.78rem', background: statusFilter === v ? 'var(--hp-gold)' : 'transparent', color: statusFilter === v ? 'var(--hp-black)' : 'var(--hp-dim)' }}>{lbl}</button>
          ))}
        </div>
        <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
          {[['all', 'All dates'], ['day', 'Day'], ['week', 'Week'], ['month', 'Month'], ['period', 'Period (28d)']].map(([p, lbl]) => (
            <button key={p} onClick={() => setPreset(p)} style={{ padding: '7px 11px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.75rem' }}>{lbl}</button>
          ))}
        </div>
        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: '0.75rem', color: 'var(--hp-dim)' }}>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...input, width: 140, padding: '6px 8px' }} />
          <span>→</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...input, width: 140, padding: '6px 8px' }} />
          {(from || to) && <button onClick={() => { setFrom(''); setTo(''); }} title="Clear date range" style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '5px 8px' }}><X size={12} /></button>}
        </div>
      </div>

      {testMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 12px', marginBottom: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid var(--hp-amber, #F59E0B)', borderRadius: 10 }}>
          <span style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--hp-amber, #F59E0B)' }}>TEST</span>
          <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>Seed a week's roster as pending timesheets to walk the approval flow:</span>
          <input type="date" value={seedWeek} onChange={e => setSeedWeek(e.target.value)} style={{ ...input, width: 150, padding: '6px 8px' }} />
          <button onClick={() => bulk('seedSorterTestWeek', { weekStart: seedWeek }, r => `Seeded ${r.seeded} shifts (${r.badSignoffs} bad sign-offs, ${r.forgotBreaks} missing breaks).`)} disabled={!!busy} style={{ ...btn('var(--hp-amber, #F59E0B)'), padding: '7px 12px' }}>{busy === 'seedSorterTestWeek' ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Seed test week'}</button>
          <button onClick={() => bulk('clearSorterTestWeek', { weekStart: seedWeek }, r => `Removed ${r.removed} seeded timesheets.`)} disabled={!!busy} style={{ ...btn('transparent'), color: 'var(--hp-red, #EF4444)', border: '1px solid var(--hp-border)', padding: '7px 12px' }}><Trash2 size={13} /> Clear</button>
        </div>
      )}

      {toast && <div style={{ ...pill(toast.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', toast.ok ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)'), padding: '8px 12px', marginBottom: 12 }}>{toast.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {toast.text}</div>}

      {/* QR clock snapshot — who scanned the LOG IN / LOG OUT codes this night, and who didn't */}
      {nightPicked && rows && rosterCount > 0 && (
        <div style={{ marginBottom: 12, padding: '12px 14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <QrCode size={16} color="var(--hp-gold)" />
            <b style={{ fontSize: '0.9rem' }}>QR clock snapshot</b>
            <span style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>
              <b style={{ color: realClockedIn.length === expectedCount ? 'var(--hp-green, #22C55E)' : 'var(--hp-amber, #F59E0B)' }}>{realClockedIn.length} of {expectedCount}</b> expected sorters clocked in with the QR
              {absentRows.length ? <> · <b style={{ color: 'var(--hp-dim)' }}>{absentRows.length} away</b> of {rosterCount} rostered</> : null}
              {noApp ? <> · <b style={{ color: 'var(--hp-red, #EF4444)' }}>{noApp} did not use the app</b></> : null}
              {inNoOut.length ? <> · <b style={{ color: 'var(--hp-amber, #F59E0B)' }}>{inNoOut.length} no clock-out</b></> : null}
            </span>
            <button onClick={copySnapshot} title="Copy the snapshot as text to share" style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '5px 10px', marginLeft: 'auto', fontSize: '0.75rem' }}><Copy size={12} /> Copy</button>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {[
              ['Clocked in & out', clockedInOut, 'var(--hp-green, #22C55E)', '✓'],
              ['In, no clock-out', inNoOut, 'var(--hp-amber, #F59E0B)', '⚠'],
              ['Authorised, no QR', authorisedNoQr, 'var(--hp-dim)', '●'],
              ['No clock-in', missingRows, 'var(--hp-red, #EF4444)', '✗'],
              ['Away, did not work', absentRows, 'var(--hp-dim)', '—'],
            ].map(([label, list, colour, mark]) => (
              <div key={label} style={{ flex: '1 1 200px', minWidth: 180 }}>
                <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: colour, fontWeight: 700, marginBottom: 6 }}>{mark} {label} ({list.length})</div>
                {list.length ? list.map(r => (
                  <div key={r.ID} style={{ fontSize: '0.8rem', padding: '2px 0', color: 'var(--hp-text)' }}>
                    {r.Name || r.Login}
                    <span style={{ color: 'var(--hp-dim)' }}>{r.Status === 'Absent' ? ` · ${awayReason(r)}` : r.actualIn ? ` · ${r.actualIn}${r.actualOut ? '–' + r.actualOut : '–…'}` : ''}</span>
                  </div>
                )) : <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>—</div>}
              </div>
            ))}
          </div>
          {/* Refused clock attempts — the sorter's phone reached the server but a gate
              said no (outside fence / no GPS / wrong or stale QR). Distinct from "No
              clock-in", which means the server never heard from them at all. */}
          {attempts.length > 0 && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--hp-border)' }}>
              <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-amber, #F59E0B)', fontWeight: 700, marginBottom: 6 }}>⊘ Rejected scans ({attempts.length})</div>
              {attempts.map((a, i) => (
                <div key={i} style={{ fontSize: '0.8rem', padding: '2px 0', color: 'var(--hp-text)' }}>
                  {a.name || a.login}<span style={{ color: 'var(--hp-dim)' }}> · {attLine(a)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* OUTSIDE the rows branch on purpose. This describes the PAY PERIOD — who has
          checked, and what they have disputed — not the rows the filters happen to be
          showing. It used to sit inside, so picking a date range whose rows were all
          approved emptied the table and took the whole panel (and its Who's checked
          button) with it, exactly when you most wanted to know who was outstanding. */}
      <DisputesPanel disputes={review.disputes || []} counts={review.counts || {}} period={review.period}
        sorters={review.sorters || []} flash={flash}
        busy={busy} onResolve={resolveDispute} onGoTo={(d) => pickNight(d, 'All')} />
      {rows === null ? (
        <div style={{ color: 'var(--hp-dim)', padding: 20 }}><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>
      ) : !rows.length ? (
        <div style={{ color: 'var(--hp-dim)', padding: '24px 0', textAlign: 'center' }}><CheckCircle2 size={26} color="var(--hp-green, #22C55E)" /><div style={{ marginTop: 8 }}>{statusFilter === 'Pending' ? 'All caught up — nothing pending' : statusFilter === 'Approved' ? 'No approved timesheets' : 'No timesheets'}{(from || to) ? ' in this date range' : ''}.</div></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <thead><tr>
              {[['date', 'Date'], ['sorter', 'Sorter'], ['slot', 'Slot']].map(([k, lbl]) => (
                <th key={k} style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(k)} title={`Sort by ${lbl.toLowerCase()}`}>
                  {lbl}{sortKey === k ? <span style={{ color: 'var(--hp-gold)' }}> {sortDir === 'asc' ? '▲' : '▼'}</span> : <span style={{ opacity: 0.3 }}> ↕</span>}
                </th>
              ))}
              <th style={th}>Rostered</th><th style={th}>Actual</th><th style={th} title="AP-reported finish for that night (runners +1h)">AP finish</th><th style={th}>Break</th><th style={th}>Hrs</th>
              <th style={th}>Flags</th><th style={{ ...th, textAlign: 'right' }}>Action</th>
            </tr></thead>
            <tbody>
              {sorted.map(r => r.Status === 'Absent' ? (
                /* Away — not a timesheet. No times, no hours, nothing to approve: it is
                   here so the night's headcount is explained rather than short. */
                <tr key={r.ID} style={{ background: 'rgba(148,163,184,0.06)' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDayDate(r.Date)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{r.Name || r.Login}</td>
                  <td style={td}>{r.slot || '—'}</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>{r.rosteredStart ? `${r.rosteredStart}–${r.rosteredEnd}` : '—'}</td>
                  <td style={{ ...td, color: 'var(--hp-dim)', fontWeight: 600 }}>Away — did not work</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>—</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>—</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>0</td>
                  <td style={td}>
                    <span style={{ ...pill('rgba(148,163,184,0.16)', 'var(--hp-dim)'), fontSize: '0.66rem' }}>{awayReason(r)}</span>
                    <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', marginTop: 4 }}>Not paid · not counted as a shift</div>
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => undoAbsent(r)} disabled={!!busy} title="Undo — put this back to a 'No clock-in' row to answer." style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '6px 12px' }}>{busy === r.ID + 'clearSorterAbsent' ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Undo'}</button>
                  </td>
                </tr>
              ) : r.Status === 'Missing' ? (
                <Fragment key={r.ID}>
                <tr style={{ background: 'rgba(245,158,11,0.05)' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDayDate(r.Date)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>
                    {r.Name || r.Login}
                    {/* Visa-cap watch: fortnight hours (sort + drive) vs the 48h cap. */}
                    {(() => {
                      const cw = capWatch[(r.Login || '').toUpperCase()];
                      if (!cw) return null;
                      const c = cw.over ? 'var(--hp-red, #EF4444)' : cw.warn ? 'var(--hp-amber, #F59E0B)' : 'var(--hp-dim)';
                      const tip = (cw.windows || []).map(w =>
                        `${w.start} – ${w.end}: ${(w.counted != null ? w.counted : w.total).toFixed(1)}h counted${w.breakHours ? ` + ${w.breakHours.toFixed(1)}h course break` : ''} (sort ${w.sorter.toFixed(1)} + drive ${w.driver.toFixed(1)})`).join('\n');
                      return (
                        <div style={{ fontSize: '0.64rem', fontWeight: 800, color: c, marginTop: 2 }}
                          title={`Visa work cap — hours this fortnight (both Monday windows)\n${tip}`}>
                          {cw.onBreak ? `Course break · unlimited (visa cap resumes after ${cw.currentBreak.end})` : `Visa cap ${cw.maxTotal.toFixed(1)}h / ${cw.capHours}h`}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={td}>{r.slot || '—'}</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>{r.rosteredStart ? `${r.rosteredStart}–${r.rosteredEnd}` : '—'}</td>
                  <td style={{ ...td, color: 'var(--hp-red, #EF4444)', fontWeight: 600 }}>No clock-in</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>—</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>—</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>—</td>
                  <td style={td}>
                    <span style={{ ...pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)'), fontSize: '0.66rem' }}><AlertTriangle size={10} />Never scanned the QR</span>
                    {/* They DID try: show the refused attempts so the claim is checkable. */}
                    {(() => {
                      const list = attemptsFor(r.Login);
                      if (!list.length) return null;
                      return (
                        <div style={{ marginTop: 4 }} title={list.map(attLine).join('\n')}>
                          <span style={{ ...pill('rgba(245,158,11,0.12)', 'var(--hp-amber, #F59E0B)'), fontSize: '0.66rem' }}>
                            {list.length} rejected scan{list.length === 1 ? '' : 's'} · last {attLine(list[list.length - 1])}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {/* Two honest answers to "no clock-in": they worked it, or they weren't here. */}
                    <button onClick={() => (absentFor && absentFor.ID === r.ID ? setAbsentFor(null) : openAbsent(r))} disabled={!!busy}
                      title="They weren't here — off sick, on leave, a no-show or not needed. Records a nought-hour absence with the reason: no pay, and the row stops asking to be answered."
                      style={{ ...btn('transparent'), color: absentFor && absentFor.ID === r.ID ? 'var(--hp-gold)' : 'var(--hp-text)', border: `1px solid ${absentFor && absentFor.ID === r.ID ? 'var(--hp-gold)' : 'var(--hp-border)'}`, padding: '6px 12px', marginRight: 6 }}>Sick / away</button>
                    <button onClick={() => authoriseOne(r)} disabled={!!busy} title="They DID work — insert a scheduled shift for this sorter (rostered start, saved AP close, 30-min break), then review and approve it." style={{ ...btn('var(--hp-amber, #F59E0B)'), padding: '6px 12px' }}>{busy === r.ID + 'authoriseScheduledShift' ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Authorise scheduled'}</button>
                  </td>
                </tr>
                {/* Why they weren't here. The reason is the whole point — "away" with no
                    reason is the same blank the row already was. */}
                {absentFor && absentFor.ID === r.ID && (
                  <tr style={{ background: 'rgba(245,158,11,0.09)' }}>
                    <td colSpan={10} style={{ ...td, borderTop: 'none' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}><b style={{ color: 'var(--hp-text)' }}>{absentFor.name}</b> was away on {fmtD(absentFor.date)} —</span>
                        <select value={absentFor.reason} onChange={e => setAbsentFor(f => ({ ...f, reason: e.target.value }))} style={{ ...input, width: 150, padding: '5px 7px', marginBottom: 0, fontSize: '0.8rem' }}>
                          {['Sick', 'Leave', 'Did not show', 'Not needed'].map(x => <option key={x} value={x}>{x}</option>)}
                        </select>
                        <input value={absentFor.note} onChange={e => setAbsentFor(f => ({ ...f, note: e.target.value }))} placeholder="Note (optional) — e.g. not replaced, no cover" style={{ ...input, flex: '1 1 260px', maxWidth: 380, padding: '5px 7px', marginBottom: 0, fontSize: '0.8rem' }} />
                        <button onClick={saveAbsent} disabled={!!busy} style={{ ...btn('var(--hp-gold)'), padding: '6px 14px' }}>{busy === r.ID + 'markSorterAbsent' ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Record away'}</button>
                        <button onClick={() => setAbsentFor(null)} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '6px 9px' }}><X size={13} /></button>
                        <span style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>Nought hours — never paid, never put to the sorter to confirm.</span>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ) : (
                <tr key={r.ID}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDayDate(r.Date)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>
                    {r.Name || r.Login}
                    {/* Visa-cap watch: fortnight hours (sort + drive) vs the 48h cap. */}
                    {(() => {
                      const cw = capWatch[(r.Login || '').toUpperCase()];
                      if (!cw) return null;
                      const c = cw.over ? 'var(--hp-red, #EF4444)' : cw.warn ? 'var(--hp-amber, #F59E0B)' : 'var(--hp-dim)';
                      const tip = (cw.windows || []).map(w =>
                        `${w.start} – ${w.end}: ${(w.counted != null ? w.counted : w.total).toFixed(1)}h counted${w.breakHours ? ` + ${w.breakHours.toFixed(1)}h course break` : ''} (sort ${w.sorter.toFixed(1)} + drive ${w.driver.toFixed(1)})`).join('\n');
                      return (
                        <div style={{ fontSize: '0.64rem', fontWeight: 800, color: c, marginTop: 2 }}
                          title={`Visa work cap — hours this fortnight (both Monday windows)\n${tip}`}>
                          {cw.onBreak ? `Course break · unlimited (visa cap resumes after ${cw.currentBreak.end})` : `Visa cap ${cw.maxTotal.toFixed(1)}h / ${cw.capHours}h`}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={td}>
                    {((r.flags || []).includes('Not rostered') || r.covering) ? (
                      <select value={r.rosterSlot || ''} onChange={e => setCovering(r.ID, e.target.value)} disabled={!!busy}
                        title="Which rostered slot did this sorter cover? Picking one gives them that slot's scheduled times and clears 'Not rostered'."
                        style={{ ...input, padding: '4px 6px', maxWidth: 190, fontSize: '0.78rem', borderColor: r.covering ? 'var(--hp-green, #22C55E)' : 'var(--hp-amber, #F59E0B)' }}>
                        <option value="">{r.covering ? '— not covering —' : 'Covering for…'}</option>
                        {slots.map(s => <option key={s.slot} value={s.slot}>{s.slot}</option>)}
                      </select>
                    ) : (r.slot || '—')}
                  </td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>{r.rosteredStart ? `${r.rosteredStart}–${r.rosteredEnd}` : '—'}</td>
                  <td style={td}>
                    {timesId === r.ID ? (
                      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        <input type="time" value={tIn} onChange={e => setTIn(e.target.value)} style={{ ...input, width: 92, padding: '4px 6px' }} />
                        <input type="time" value={tOut} onChange={e => setTOut(e.target.value)} style={{ ...input, width: 92, padding: '4px 6px' }} />
                        <button onClick={() => saveTimes(r.ID)} style={{ ...btn('var(--hp-gold)'), padding: '5px 9px' }}>Save</button>
                        <button onClick={() => setTimesId(null)} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '5px 9px' }}>✕</button>
                      </span>
                    ) : (
                      <button title="Edit the actual sign-on / sign-off time" onClick={() => { setTimesId(r.ID); setTIn(r.actualIn || ''); setTOut(r.actualOut || ''); }} style={{ background: 'transparent', border: 'none', color: 'var(--hp-text)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.84rem', padding: 0 }}>
                        {r.actualIn}–{r.actualOut || '…'} <Pencil size={11} style={{ opacity: 0.5, verticalAlign: 'middle' }} />
                      </button>
                    )}
                  </td>
                  <td style={td}>
                    {(() => { const ap = apFinishFor(r); if (!ap) return <span style={{ color: 'var(--hp-dim)' }}>—</span>; const match = ap === r.actualOut; return <span style={{ fontWeight: 700, color: match ? 'var(--hp-green, #22C55E)' : 'var(--hp-amber, #F59E0B)' }} title={match ? 'Actual matches the AP finish' : `Actual (${r.actualOut || '—'}) differs from the AP finish`}>{ap}</span>; })()}
                  </td>
                  <td style={td}>
                    {breakBadge(r)}
                    {r.breakSource === 'none'
                      ? <button title="Add a 30-minute break the sorter forgot to record" onClick={() => act('setSorterBreak', r.ID, { breakMins: 30 })} disabled={!!busy} style={{ ...btn('transparent'), color: BLUE, border: `1px solid ${BLUE}`, padding: '3px 8px', marginLeft: 6, fontSize: '0.68rem' }}>+ 30m</button>
                      : <button title="Change the break minutes" onClick={() => { const v = window.prompt('Break minutes for ' + (r.Name || r.Login), String(r.breakMins || 30)); if (v != null && v !== '') act('setSorterBreak', r.ID, { breakMins: v }); }} disabled={!!busy} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '3px 7px', marginLeft: 6, fontSize: '0.68rem' }}><Pencil size={10} /></button>}
                  </td>
                  <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, color: r.overLong ? 'var(--hp-red, #EF4444)' : undefined }}>
                    {editId === r.ID
                      ? <input value={editHrs} onChange={e => setEditHrs(e.target.value)} style={{ ...input, width: 56, padding: '4px 6px' }} />
                      : <span title={r.overLong ? 'Longer than any shift — the start or finish is on the wrong day. Fix the times before approving.' : undefined}>{r.workedHours}{r.overLong ? ' ⚠' : ''}</span>}
                  </td>
                  <td style={td}>{(r.flags || []).map((f, i) => <span key={i} style={{ ...pill(f === 'SEEDED' ? 'rgba(148,163,184,0.16)' : 'rgba(245,158,11,0.14)', f === 'SEEDED' ? 'var(--hp-dim)' : 'var(--hp-amber, #F59E0B)'), marginRight: 4, marginBottom: 4, fontSize: '0.66rem' }}>{f !== 'SEEDED' && <AlertTriangle size={10} />}{f}</span>)}{!(r.flags || []).length && <span style={{ color: 'var(--hp-green, #22C55E)', fontSize: '0.72rem' }}>✓ clean</span>}
                    <SorterSaysChip entry={(review.review || {})[r.ID]} /></td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {r.Status === 'Approved' ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                        <span style={pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)')}><CheckCircle2 size={12} /> Approved</span>
                        <button title="Move back to pending" onClick={() => act('amendSorterTimesheet', r.ID, { status: 'Pending' })} disabled={!!busy} style={{ ...iconBtn, marginRight: 0, fontSize: '0.68rem', padding: '5px 9px' }}>Reopen</button>
                      </span>
                    ) : editId === r.ID ? (
                      <>
                        <button onClick={() => saveAmend(r.ID)} style={{ ...btn('var(--hp-gold)'), padding: '6px 10px', marginRight: 4 }}>Save</button>
                        <button onClick={() => { setEditId(null); setEditHrs(''); }} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '6px 10px' }}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button title="Round this row's clock in/out to nearest 15 min" onClick={() => act('editSorterTimes', r.ID, { round15: true })} disabled={!!busy} style={iconBtn}>{busy === r.ID + 'editSorterTimes' ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Clock size={13} />}</button>
                        <button title="Adjust hours" onClick={() => { setEditId(r.ID); setEditHrs(String(r.workedHours)); }} style={iconBtn}><Pencil size={13} /></button>
                        <button title="Discard" onClick={() => act('discardSorterTimesheet', r.ID)} disabled={!!busy} style={{ ...iconBtn, color: 'var(--hp-red, #EF4444)' }}><X size={13} /></button>
                        <button onClick={() => act('approveSorterTimesheet', r.ID)} disabled={!!busy} style={{ ...btn('var(--hp-green, #22C55E)'), padding: '6px 12px' }}>{busy === r.ID + 'approveSorterTimesheet' ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Approve'}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', marginTop: 12 }}>Approved hours feed the payroll engine (P2). Hours = actual span − break. <b style={{ color: 'var(--hp-green, #22C55E)' }}>self ✓</b> = the sorter took the break themselves; <b style={{ color: BLUE }}>admin</b> = you added it; <b style={{ color: 'var(--hp-red, #EF4444)' }}>none ⚠</b> = no break — add one or it pays the full span. <b>AP finish</b> is the closing time AP reported for that night (runners +1h); green = the sorter's actual matches it. <b style={{ color: 'var(--hp-gold)' }}>Set to scheduled</b> sets every pending sorter to their rostered start and the saved finish for the night in one click — then just pencil-edit anyone who came late or left early. If someone <b>covered</b> a shift they weren't rostered for, pick the slot they covered in the <b>Slot</b> column to give them that slot's scheduled times. On a single picked night the <b style={{ color: 'var(--hp-red, #EF4444)' }}>No clock-in</b> rows are rostered sorters who never scanned the QR — <b style={{ color: 'var(--hp-amber, #F59E0B)' }}>Authorise scheduled</b> (per row) or <b style={{ color: 'var(--hp-amber, #F59E0B)' }}>Authorise no-shows</b> (all) inserts their scheduled shift so you can cross-check the AP Sheet, then Approve. If they simply weren&apos;t there, <b>Sick / away</b> records the absence with a reason instead — nought hours, never paid, never put to the sorter to confirm, and the night then reads as the smaller number who were actually expected. Undo puts it back. Nothing is auto-approved. <b>Approving is what sends a shift to the sorter to check</b> — until then it is not shown in their portal and they are not chased for it, so a night part-way through being pencilled never goes out as a question.</p>
    </div>
    </>
  );
}

// ── Clock QR & depot geofence setup ───────────────────────────────────────────
// Generates the printable depot QR (opens /sorters?clock=<token>) and manages the
// hard-geofence pin. Scanning the QR + being inside the fence is what lets a sorter
// clock on/off — both enforced server-side in sorter-shift-sb.js.
function adminPosition() {
  return new Promise((res, rej) => {
    if (!navigator.geolocation) return rej(new Error('No GPS on this device.'));
    navigator.geolocation.getCurrentPosition(
      p => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => rej(new Error('Location permission is needed to drop the pin.')),
      { enableHighAccuracy: true, timeout: 12000 });
  });
}
// ── Interactive depot map: drop the LOG IN + LOG OUT pins, with address search ─
// Plain Leaflet, keyless: Esri satellite + OpenStreetMap street tiles, Nominatim
// geocoding. Two draggable pins with live radius circles — lets an admin set the
// geofence from off-site, and the two clock points can be separate spots on site.
const DEFAULT_CENTER = { lat: -33.95026, lng: 151.1945 }; // Botany depot area
function pinDivIcon(color, label) {
  return L.divIcon({
    className: 'hp-depot-pin',
    html: `<div style="transform:translate(-50%,-100%);display:flex;flex-direction:column;align-items:center;pointer-events:none">
      <div style="background:${color};color:#fff;font:700 10px system-ui,sans-serif;padding:2px 7px;border-radius:6px;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.45)">${label}</div>
      <div style="width:16px;height:16px;background:${color};border:3px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.45);margin-top:3px"></div>
    </div>`,
    iconSize: [0, 0], iconAnchor: [0, 0],
  });
}
function DepotPinMap({ inPin, outPin, radius, active, setActive, onChange }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markers = useRef({ in: null, out: null });
  const circles = useRef({ in: null, out: null });
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  // Refs so the map's mount-time handlers always see the latest active pin / callbacks.
  const activeRef = useRef(active); useEffect(() => { activeRef.current = active; }, [active]);
  const onChangeRef = useRef(onChange); useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  const setActiveRef = useRef(setActive); useEffect(() => { setActiveRef.current = setActive; }, [setActive]);

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const start = inPin || outPin || DEFAULT_CENTER;
    const map = L.map(elRef.current, { center: [start.lat, start.lng], zoom: (inPin || outPin) ? 18 : 16 });
    const sat = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { maxZoom: 21, maxNativeZoom: 19, attribution: 'Tiles © Esri' });
    const street = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' });
    sat.addTo(map);
    L.control.layers({ Satellite: sat, Street: street }, {}, { collapsed: true }).addTo(map);
    map.on('click', (e) => onChangeRef.current(activeRef.current, { lat: e.latlng.lat, lng: e.latlng.lng }));
    mapRef.current = map;
    setTimeout(() => { try { map.invalidateSize(); } catch {} }, 250);
    return () => { try { map.remove(); } catch {} mapRef.current = null; };
  }, []);// eslint-disable-line

  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    const specs = [['in', inPin, '#16A34A', 'LOG IN'], ['out', outPin, '#DC2626', 'LOG OUT']];
    for (const [key, pin, color, label] of specs) {
      const valid = pin && !isNaN(pin.lat) && !isNaN(pin.lng);
      if (valid) {
        const ll = [pin.lat, pin.lng];
        if (!markers.current[key]) {
          const m = L.marker(ll, { icon: pinDivIcon(color, label), draggable: true }).addTo(map);
          m.on('dragend', () => { const p = m.getLatLng(); onChangeRef.current(key, { lat: p.lat, lng: p.lng }); });
          m.on('click', () => setActiveRef.current(key));
          markers.current[key] = m;
        } else markers.current[key].setLatLng(ll);
        if (!circles.current[key]) circles.current[key] = L.circle(ll, { radius: radius || 100, color, weight: 1, fillOpacity: 0.08 }).addTo(map);
        else { circles.current[key].setLatLng(ll); circles.current[key].setRadius(radius || 100); }
      } else {
        if (markers.current[key]) { map.removeLayer(markers.current[key]); markers.current[key] = null; }
        if (circles.current[key]) { map.removeLayer(circles.current[key]); circles.current[key] = null; }
      }
    }
  }, [inPin, outPin, radius]);

  async function search(e) {
    if (e) e.preventDefault();
    if (!q.trim()) return;
    setSearching(true); setResults(null);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(q)}`, { headers: { Accept: 'application/json' } }).then(x => x.json());
      setResults(Array.isArray(r) ? r : []);
      if (r && r[0] && mapRef.current) mapRef.current.setView([parseFloat(r[0].lat), parseFloat(r[0].lon)], 18);
    } catch { setResults([]); } finally { setSearching(false); }
  }
  function goTo(res) {
    if (mapRef.current) mapRef.current.setView([parseFloat(res.lat), parseFloat(res.lon)], 18);
    setResults(null); setQ(res.display_name.split(',').slice(0, 2).join(','));
  }

  const chip = (key, color, lbl) => (
    <button type="button" onClick={() => setActive(key)} style={{ padding: '6px 12px', borderRadius: 999, border: active === key ? `2px solid ${color}` : '1px solid var(--hp-border)', background: active === key ? color : 'transparent', color: active === key ? '#fff' : 'var(--hp-text)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <MapPin size={12} /> {lbl}
    </button>
  );

  return (
    <div>
      <form onSubmit={search} style={{ display: 'flex', gap: 8, marginBottom: 10, position: 'relative' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search an address or place…" style={{ ...input, flex: 1, margin: 0 }} />
        <button type="submit" style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--hp-gold)', color: '#0B0B0F', fontWeight: 700, cursor: 'pointer' }}>{searching ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Search'}</button>
        {results && (
          <div style={{ position: 'absolute', top: '105%', left: 0, right: 0, zIndex: 1200, background: 'var(--hp-card, #16161c)', border: '1px solid var(--hp-border)', borderRadius: 8, overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}>
            {results.length === 0
              ? <div style={{ padding: 10, color: 'var(--hp-dim)', fontSize: '0.82rem' }}>No matches.</div>
              : results.map((r, i) => <button key={i} type="button" onClick={() => goTo(r)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', borderBottom: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-text)', fontSize: '0.8rem', cursor: 'pointer' }}>{r.display_name}</button>)}
          </div>
        )}
      </form>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--hp-dim)', fontSize: '0.78rem' }}>Tap the map to drop the:</span>
        {chip('in', '#16A34A', 'LOG IN pin')}
        {chip('out', '#DC2626', 'LOG OUT pin')}
      </div>
      <div ref={elRef} style={{ height: 400, width: '100%', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--hp-border)' }} />
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.75rem', margin: '8px 0 0' }}>Tap to place the selected pin, or drag a pin to fine-tune. The shaded circle is the {radius} m clock radius. Use the layers control (top-right) to switch Satellite / Street.</p>
    </div>
  );
}

function ClockQrView({ flash }) {
  const [settings, setSettings] = useState(null);
  const [qr, setQr] = useState(null);        // { in:{token,url}, out:{token,url} }
  const [img, setImg] = useState({ in: '', out: '' });   // QR data URLs
  const [radius, setRadius] = useState(100);
  const [inPin, setInPin] = useState(null);   // LOG IN (clock on) geofence pin
  const [outPin, setOutPin] = useState(null); // LOG OUT (clock off) geofence pin
  const [active, setActive] = useState('in'); // which pin a map tap drops
  const [busy, setBusy] = useState('');

  const loadSettings = useCallback(async () => {
    try {
      const g = await fetch('/.netlify/functions/geofence-settings').then(r => r.json());
      if (g && g.settings) {
        setSettings(g.settings); setRadius(g.settings.sorterRadiusM || 100);
        const pins = g.settings.depotPins || {};
        setInPin(pins.sorter || null); setOutPin(pins.sorter_out || null);
      }
    } catch {}
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);
  const onPinChange = useCallback((which, ll) => { (which === 'out' ? setOutPin : setInPin)(ll); }, []);

  useEffect(() => {
    if (!qr) { setImg({ in: '', out: '' }); return; }
    let alive = true;
    const gen = (u) => (u ? QRCode.toDataURL(u, { width: 360, margin: 2, errorCorrectionLevel: 'M' }).catch(() => '') : Promise.resolve(''));
    Promise.all([gen(qr.in && qr.in.url), gen(qr.out && qr.out.url)]).then(([i, o]) => { if (alive) setImg({ in: i, out: o }); });
    return () => { alive = false; };
  }, [qr]);

  async function showQr() {
    setBusy('qr');
    try { const r = await api('getSorterClockQr', { base: window.location.origin }); if (!r.success) throw new Error(r.error || 'Could not load QR.'); setQr({ in: r.in, out: r.out }); }
    catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }
  async function rotateQr(which) {
    const lbl = which === 'in' ? 'LOG IN' : 'LOG OUT';
    if (!window.confirm(`Generate a NEW ${lbl} QR? The current printed ${lbl} poster will stop working and must be reprinted. The other poster is unaffected.`)) return;
    setBusy('rotate-' + which);
    try { const r = await api('rotateSorterClockQr', { base: window.location.origin, which }); if (!r.success) throw new Error(r.error || 'Could not rotate.'); setQr({ in: r.in, out: r.out }); flash(`New ${lbl} QR generated — reprint that poster.`); }
    catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }
  async function saveGeofence() {
    if (!settings) return;
    setBusy('pin');
    try {
      const body = {
        action: 'saveGeofenceSettings',
        radiusM: settings.geofenceRadiusM,
        geofenceEnabled: settings.geofenceEnabled,
        sorterQrRequired: settings.sorterQrRequired ?? settings.qrRequired,
        sorterRadiusM: radius,
        finishZone: settings.finishZone,
        depotPins: { ...(settings.depotPins || {}), ...(inPin ? { sorter: inPin } : {}), ...(outPin ? { sorter_out: outPin } : {}) },
      };
      const r = await fetch('/.netlify/functions/geofence-settings', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(x => x.json());
      if (!r.success) throw new Error(r.error || 'Could not save.');
      setSettings(r.settings);
      if (r.settings) { setRadius(r.settings.sorterRadiusM || 100); const p = r.settings.depotPins || {}; setInPin(p.sorter || null); setOutPin(p.sorter_out || null); }
      flash('Geofence saved — both clock points set.');
    } catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }
  // On-site convenience: set the currently-selected pin to this device's GPS fix.
  async function dropPin() {
    setBusy('gps');
    try { const pos = await adminPosition(); onPinChange(active, pos); flash(`${active === 'out' ? 'LOG OUT' : 'LOG IN'} pin set to your location — press Save.`); }
    catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }
  async function toggleQrRequired() {
    if (!settings) return;
    setBusy('pin');
    try {
      const cur = settings.sorterQrRequired ?? settings.qrRequired;
      const body = { action: 'saveGeofenceSettings', radiusM: settings.geofenceRadiusM, geofenceEnabled: settings.geofenceEnabled, sorterQrRequired: !cur, sorterRadiusM: radius, finishZone: settings.finishZone, depotPins: settings.depotPins || {} };
      const r = await fetch('/.netlify/functions/geofence-settings', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(x => x.json());
      if (!r.success) throw new Error(r.error || 'Could not save.');
      setSettings(r.settings); flash(`QR ${(r.settings.sorterQrRequired ?? r.settings.qrRequired) ? 'now required' : 'no longer required'} to clock on/off.`);
    } catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }
  // Professional gold/black "City Sorters" poster (Hollypaul Recruitment Pty Ltd).
  // One .page per code — green CLOCK ON / red CLOCK OFF — printed on its own A4 sheet.
  const GOLD = '#C9A227', INK = '#0B0B0F';
  const POSTER_STYLE = `
      @page { size: A4 portrait; margin: 0; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      html,body { margin:0; padding:0; font-family: 'Helvetica Neue', Arial, sans-serif; color:${INK}; }
      .page { width:210mm; min-height:297mm; margin:0 auto 22px; padding:14mm; background:#fff; }
      .frame { border:3px solid ${INK}; border-radius:16px; overflow:hidden; height:100%; display:flex; flex-direction:column; }
      .head { background:${INK}; color:#fff; text-align:center; padding:26px 20px 22px; border-bottom:5px solid ${GOLD}; }
      .brand { font-size:58px; font-weight:800; letter-spacing:3px; color:${GOLD}; line-height:1; }
      .brand span { color:#fff; }
      .sub { margin-top:10px; font-size:15px; letter-spacing:3px; text-transform:uppercase; color:#d8d8d8; }
      .action { text-align:center; padding:22px 20px 6px; }
      .pill { display:inline-flex; align-items:center; gap:12px; color:#fff; font-size:34px; font-weight:800; letter-spacing:2px; padding:12px 34px; border-radius:999px; }
      .dot { width:16px; height:16px; border-radius:50%; background:#fff; }
      .when { margin:14px 0 0; font-size:19px; color:#333; }
      .qrwrap { text-align:center; padding:14px 20px 6px; }
      .qr { width:300px; height:300px; padding:14px; background:#fff; border:6px solid ${GOLD}; border-radius:18px; }
      .steps { margin:6px 30px 0; padding:16px 22px; border:2px solid #eee; border-radius:14px; background:#faf7ec; }
      .steps h3 { margin:0 0 10px; font-size:16px; letter-spacing:2px; text-transform:uppercase; color:${INK}; }
      .steps ol { margin:0; padding-left:22px; }
      .steps li { font-size:17px; line-height:1.7; color:#222; }
      .note { text-align:center; margin:16px 30px 0; font-size:14px; color:#666; }
      .foot { margin-top:auto; background:${INK}; color:${GOLD}; text-align:center; padding:14px; font-size:17px; letter-spacing:2px; font-weight:700; }
      .foot span { color:#fff; letter-spacing:1px; font-weight:400; }
      .bar { position:fixed; top:0; left:0; right:0; height:52px; display:flex; align-items:center; gap:14px; padding:0 18px; background:${INK}; color:#fff; z-index:9999; font-size:14px; box-shadow:0 2px 8px rgba(0,0,0,.3); }
      .bar button { background:${GOLD}; color:${INK}; border:none; font-weight:800; font-size:14px; padding:10px 18px; border-radius:8px; cursor:pointer; }
      .bar .hint { color:#bbb; }
      @media screen { body { background:#5a5a5a; padding-top:66px; } }
      @media print { .bar { display:none !important; } body { background:#fff !important; padding-top:0 !important; } .page { margin:0; break-after:page; page-break-after:always; } .page:last-of-type { break-after:auto; page-break-after:auto; } }`;
  // Build one poster's <div class="page">…</div> (accent inline so one stylesheet serves both).
  function posterMarkup(which) {
    const src = img[which];
    const isIn = which === 'in';
    const accent = isIn ? '#1F9D4D' : '#D0342C';
    const action = isIn ? 'CLOCK ON' : 'CLOCK OFF';
    const when = isIn ? 'at the START of your shift' : 'at the END of your shift';
    const btnName = isIn ? 'green <b>Clock On</b>' : 'red <b>Clock Off</b>';
    return `<div class="page"><div class="frame">
      <div class="head">
        <div class="brand">CITY <span>SORTERS</span></div>
        <div class="sub">Hollypaul Recruitment Pty Ltd</div>
      </div>
      <div class="action">
        <div class="pill" style="background:${accent}"><span class="dot"></span> ${action}</div>
        <p class="when">Scan this code <b>${when}</b>.</p>
      </div>
      <div class="qrwrap"><img class="qr" src="${src}" alt="${action} QR" /></div>
      <div class="steps">
        <h3>How to ${isIn ? 'log in' : 'log off'}</h3>
        <ol>
          <li>Open the <b>Camera</b> app on your phone.</li>
          <li>Point it at this code and tap the link that appears.</li>
          <li>Sign in with your PIN if asked.</li>
          <li>Press the ${btnName} button to ${isIn ? 'start' : 'end'} your shift.</li>
        </ol>
      </div>
      <p class="note">You must be on site — your location is checked when you ${isIn ? 'clock on' : 'clock off'}. ${isIn ? 'Clock off at the LOG OUT poster at the end of your shift.' : 'This is the LOG OUT point only.'}</p>
      <div class="foot">hollypaul.com/sorters &nbsp;·&nbsp; <span>Night sort team portal</span></div>
    </div></div>`;
  }
  // Open a viewable document (screen toolbar prints; hidden on paper). `whichList` is
  // one or both codes — each renders on its own A4 sheet.
  function openPosters(whichList, title, hint) {
    if (whichList.some((w) => !img[w])) return;
    const w = window.open('', '_blank', 'width=840,height=1180');
    if (!w) { flash('Allow pop-ups to open the poster.', false); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>${POSTER_STYLE}</style></head>
    <body>
    <div class="bar"><button onclick="window.print()">🖨 ${whichList.length > 1 ? 'Print both posters' : 'Print this poster'}</button><span class="hint">${hint}</span></div>
    ${whichList.map(posterMarkup).join('\n')}
    </body></html>`);
    w.document.close(); // viewable document; user prints from the toolbar
  }
  function printPoster(which) {
    const action = which === 'in' ? 'CLOCK ON' : 'CLOCK OFF';
    openPosters([which], `City Sorters — ${action}`, `${action} · print on A4, colour, 100% scale — or choose “Save as PDF”. Close this tab when done.`);
  }
  function printBoth() {
    openPosters(['in', 'out'], 'City Sorters — both posters', 'Prints as 2 pages — page 1 CLOCK ON, page 2 CLOCK OFF. A4, colour, 100% scale.');
  }

  const btn = (bg) => ({ padding: '10px 16px', borderRadius: 8, border: 'none', background: bg, color: '#0B0B0F', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)' });
  const ghost = { padding: '10px 16px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)' };

  return (
    <>
      <div style={card}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: '0 0 6px' }}><QrCode size={18} style={{ verticalAlign: -3, marginRight: 6 }} />Depot clock QR — two codes</h2>
        <p style={{ color: 'var(--hp-dim)', fontSize: '0.82rem', margin: '0 0 16px' }}>Two separate posters: the <strong style={{ color: 'var(--hp-green, #22C55E)' }}>green LOG IN</strong> code only clocks on, the <strong style={{ color: 'var(--hp-red, #EF4444)' }}>red LOG OUT</strong> code only clocks off. Post them at the start / end points. Combined with the geofence below, sorters can only clock on and off <strong>on site, from the physical poster</strong>. Scanning the wrong code is rejected.</p>
        {!qr && <button style={btn('var(--hp-gold)')} disabled={busy === 'qr'} onClick={showQr}>{busy === 'qr' ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : <QrCode size={15} />} Show / generate both codes</button>}
        {qr && img.in && img.out && (
          <div style={{ marginBottom: 14 }}>
            <button style={btn('var(--hp-gold)')} onClick={printBoth}><Printer size={15} /> Print both posters</button>
            <span style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', marginLeft: 10 }}>Opens both on one page — prints as 2 A4 sheets (CLOCK ON + CLOCK OFF).</span>
          </div>
        )}
        {qr && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {['in', 'out'].map((which) => {
              const isIn = which === 'in';
              const accent = isIn ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)';
              const lbl = isIn ? 'LOG IN' : 'LOG OUT';
              const sub = isIn ? 'Scan at the START of the shift to clock on.' : 'Scan at the END of the shift to clock off.';
              const d = qr[which]; const src = img[which];
              return (
                <div key={which} style={{ flex: 1, minWidth: 260, border: '1px solid var(--hp-border)', borderRadius: 12, padding: 16 }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 999, background: accent, color: '#0B0B0F', fontWeight: 800, fontSize: '0.78rem', letterSpacing: 0.5 }}>{isIn ? <ChevronRight size={13} /> : <ChevronLeft size={13} />} {lbl}</div>
                  <p style={{ color: 'var(--hp-dim)', fontSize: '0.78rem', margin: '10px 0 12px' }}>{sub}</p>
                  <div style={{ textAlign: 'center' }}>
                    {src
                      ? <img src={src} alt={`${lbl} QR`} style={{ width: 200, height: 200, background: '#fff', padding: 10, borderRadius: 12, border: `3px solid ${accent}` }} />
                      : <div style={{ width: 200, height: 200, margin: '0 auto', border: '1px dashed var(--hp-border)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--hp-dim)' }}><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /></div>}
                  </div>
                  {d && (<>
                    <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.72rem', wordBreak: 'break-all', color: 'var(--hp-dim)', margin: '10px 0' }}>{d.url}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button style={btn(accent)} onClick={() => printPoster(which)}><Eye size={15} /> View / print</button>
                      <button style={ghost} onClick={() => { navigator.clipboard?.writeText(d.url); flash('Link copied.'); }}><Copy size={14} /> Copy</button>
                      <button style={ghost} disabled={busy === 'rotate-' + which} onClick={() => rotateQr(which)}>{busy === 'rotate-' + which ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <RefreshCw size={14} />} Rotate</button>
                    </div>
                  </>)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={card}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: '0 0 6px' }}><MapPin size={18} style={{ verticalAlign: -3, marginRight: 6 }} />Depot geofence — two clock points</h2>
        <p style={{ color: 'var(--hp-dim)', fontSize: '0.82rem', margin: '0 0 14px' }}>Search for the facility, then tap the map to drop the <strong style={{ color: '#16A34A' }}>LOG IN</strong> and <strong style={{ color: '#DC2626' }}>LOG OUT</strong> pins — they can be separate spots inside the building. Clocking on is checked against the LOG IN pin, clocking off against the LOG OUT pin. Until a pin is set, location is recorded but not enforced.</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {[['in', inPin, '#16A34A', 'LOG IN'], ['out', outPin, '#DC2626', 'LOG OUT']].map(([k, p, c, l]) => (
            <div key={k} style={{ ...pill(p ? 'rgba(34,197,94,0.10)' : 'rgba(245,158,11,0.12)', p ? c : 'var(--hp-amber, #F59E0B)'), flex: 1, minWidth: 220 }}>
              {p ? <><CheckCircle2 size={13} /> {l} pin set — {p.lat.toFixed(5)}, {p.lng.toFixed(5)}</> : <><AlertTriangle size={13} /> No {l} pin (soft)</>}
            </div>
          ))}
        </div>

        <DepotPinMap inPin={inPin} outPin={outPin} radius={radius} active={active} setActive={setActive} onChange={onPinChange} />

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 16 }}>
          <div>
            <label style={label}>Radius (m)</label>
            <input style={{ ...input, width: 110 }} type="number" value={radius} onChange={e => setRadius(parseInt(e.target.value) || 0)} />
          </div>
          <button style={ghost} disabled={busy === 'gps'} onClick={dropPin} title="On-site only: set the selected pin to this device's location">{busy === 'gps' ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : <MapPin size={15} />} Use my location ({active === 'out' ? 'LOG OUT' : 'LOG IN'})</button>
          <button style={btn('var(--hp-gold)')} disabled={busy === 'pin'} onClick={saveGeofence}>{busy === 'pin' ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Save size={14} />} Save geofence</button>
        </div>
        {settings && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, cursor: 'pointer', fontSize: '0.85rem', color: 'var(--hp-text)' }}>
            <input type="checkbox" checked={!!(settings.sorterQrRequired ?? settings.qrRequired)} onChange={toggleQrRequired} disabled={busy === 'pin'} />
            Require the QR scan to clock on/off {settings.qrConfigured ? '' : '(generate a QR above first)'}
          </label>
        )}
      </div>
    </>
  );
}

// ── Admin TFN / document upload into the encrypted vault ──────────────────────
// storeTfn/storeDoc/getStatus accept a site-admin session acting on any login
// (sorter-vault.js), so the admin can load the docs already held for the current
// team. Reveal/download additionally needs HP_VAULT_ADMIN_SECRET and isn't done here.
function fileToB64(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(',')[1] || ''); r.onerror = rej; r.readAsDataURL(file); });
}
const VAULT_DOC_TYPES = ['TFN Declaration', 'Super Choice Form', 'Photo ID', 'Bank Details', 'Other'];
function VaultDocsPanel({ login, name, flash, onChanged }) {
  const [status, setStatus] = useState(undefined); // undefined=loading
  const [tfn, setTfn] = useState('');
  const [replaceTfn, setReplaceTfn] = useState(false);
  const [docType, setDocType] = useState('TFN Declaration');
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);

  const vault = useCallback((op, extra) => fetch('/.netlify/functions/sorter-vault', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op, login, by: 'admin', ...extra }),
  }).then(r => r.json()), [login]);

  const loadStatus = useCallback(async () => {
    try { const r = await vault('getStatus', {}); setStatus(r && r.success ? r : { tfn: { has: false }, docs: [] }); }
    catch { setStatus({ tfn: { has: false }, docs: [] }); }
  }, [vault]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function saveTfn() {
    const digits = tfn.replace(/\s+/g, '');
    if (!/^\d{8,9}$/.test(digits)) { flash('A TFN must be 8 or 9 digits.', false); return; }
    setBusy('tfn');
    try {
      const r = await vault('storeTfn', { tfn: digits, declaration: { source: 'Entered by the office — signed declaration held on file' } });
      if (!r.success) throw new Error(r.error || 'Could not store TFN.');
      await api('updateSorter', { sorter: { login, tfnStatus: 'Provided' } });
      setTfn(''); setReplaceTfn(false); flash(`TFN stored for ${login} (••• ${r.tfnLast3}).`);
      loadStatus(); onChanged && onChanged();
    } catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }

  async function uploadDoc(file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { flash('File too large (max ~5 MB).', false); return; }
    setBusy('doc');
    try {
      const dataBase64 = await fileToB64(file);
      const r = await vault('storeDoc', { docType, filename: file.name, mime: file.type || 'application/octet-stream', dataBase64 });
      if (!r.success) throw new Error(r.error || 'Could not upload.');
      if (docType === 'TFN Declaration') await api('updateSorter', { sorter: { login, tfnStatus: 'Provided' } });
      if (docType === 'Super Choice Form') await api('updateSorter', { sorter: { login, superDeclStatus: 'Provided' } });
      flash(`Uploaded ${file.name} for ${login}.`);
      if (fileRef.current) fileRef.current.value = '';
      loadStatus(); onChanged && onChanged();
    } catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }

  const has = status && status.tfn && status.tfn.has;
  const docs = (status && status.docs) || [];
  const btn = (bg) => ({ padding: '9px 14px', borderRadius: 8, border: 'none', background: bg, color: '#0B0B0F', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)', fontSize: '0.82rem' });
  const ghost = { padding: '8px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.8rem' };

  return (
    <div style={{ background: 'var(--hp-card, #1A1A20)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: 16, margin: '4px 0' }}>
      <div style={{ fontWeight: 700, marginBottom: 12 }}>Documents · {name} <span style={{ color: 'var(--hp-dim)', fontFamily: 'var(--font-mono, monospace)', fontWeight: 400 }}>{login}</span></div>
      {status === undefined ? (
        <div style={{ color: 'var(--hp-dim)' }}><Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 20 }}>
          {/* TFN */}
          <div>
            <label style={label}>Tax File Number</label>
            {has && !replaceTfn ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ ...pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)') }}><ShieldCheck size={13} /> On file ••• {status.tfn.last3}</span>
                <button style={ghost} onClick={() => setReplaceTfn(true)}>Replace</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...input, fontFamily: 'var(--font-mono, monospace)' }} value={tfn} onChange={e => setTfn(e.target.value)} inputMode="numeric" placeholder="8–9 digits" />
                <button style={btn('var(--hp-gold)')} disabled={busy === 'tfn'} onClick={saveTfn}>{busy === 'tfn' ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Save size={14} />} Save</button>
                {has && <button style={ghost} onClick={() => { setReplaceTfn(false); setTfn(''); }}>Cancel</button>}
              </div>
            )}
            <p style={{ color: 'var(--hp-dim)', fontSize: '0.72rem', marginTop: 6 }}>Encrypted at rest. Only a masked last-3 is ever shown here. Needs <code>HP_VAULT_KEY</code> set.</p>
            <p style={{ color: 'var(--hp-dim)', fontSize: '0.72rem', marginTop: 4 }}>
              Typing a number here records it as <b>entered by the office</b> — use it when you hold their paper form or ATO summary, and file that with their documents. A sorter completing the declaration in their portal signs it themselves.
            </p>
          </div>
          {/* Doc upload */}
          <div>
            <label style={label}>Upload a document</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select style={{ ...input, maxWidth: 180 }} value={docType} onChange={e => setDocType(e.target.value)}>
                {VAULT_DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => uploadDoc(e.target.files && e.target.files[0])} />
              <button style={btn('var(--hp-gold)')} disabled={busy === 'doc'} onClick={() => fileRef.current && fileRef.current.click()}>{busy === 'doc' ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <UserPlus size={14} />} Choose file…</button>
            </div>
            {docs.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {docs.map(d => (
                  <div key={d.docId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderTop: '1px solid var(--hp-border)', fontSize: '0.8rem' }}>
                    <span>{d.filename}</span>
                    <span style={{ color: 'var(--hp-dim)' }}>{d.docType} · {new Date(d.uploadedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span>
                  </div>
                ))}
              </div>
            )}
            <p style={{ color: 'var(--hp-dim)', fontSize: '0.72rem', marginTop: 6 }}>PDF or image, ~5 MB max. Secure viewing/download needs the vault admin secret (not done here).</p>
          </div>
        </div>
      )}
      {/* Ask the sorter for the three things only they can supply. */}
      {status !== undefined && (
        <div style={{ marginTop: 14 }}>
          <SorterTaxSuperRequest login={login} name={name} />
        </div>
      )}
      {status !== undefined && <RevealBankSuper login={login} flash={flash} />}
      {status !== undefined && <VaultPasswordAdmin login={login} flash={flash} />}
    </div>
  );
}

// Admin view + reset of a sorter's personal vault password (the step-up lock on
// their portal Documents tab). Reset just clears the hash — the sorter sets a new
// one on their next visit, and no data is lost (docs are encrypted with the vault
// key, not the password). Sorters can also self-reset with their PIN in the portal.
function VaultPasswordAdmin({ login, flash }) {
  const [has, setHas] = useState(null);   // null = loading
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch('/.netlify/functions/sorter-vault', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ op: 'pwdStatus', login }),
    }).then(r => r.json()).then(d => setHas(d && d.success ? !!d.hasPassword : false)).catch(() => setHas(false));
  }, [login]);
  useEffect(() => { load(); }, [load]);

  async function reset() {
    if (!window.confirm(`Reset ${login}'s personal vault password? They'll set a new one the next time they open their Documents tab. Nothing they've saved is lost.`)) return;
    setBusy(true);
    try {
      const r = await fetch('/.netlify/functions/sorter-vault', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ op: 'pwdReset', login, by: 'admin' }),
      }).then(x => x.json());
      if (!r.success) throw new Error(r.error || 'Could not reset.');
      flash(`Vault password reset for ${login}.`);
      load();
    } catch (e) { flash(e.message, false); } finally { setBusy(false); }
  }

  const miniBtn = { padding: '6px 11px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 5 };

  return (
    <div style={{ marginTop: 10, padding: '11px 13px', borderRadius: 10, border: '1px solid var(--hp-border)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <Lock size={14} color="var(--hp-gold)" />
      <span style={{ fontWeight: 700, fontSize: '0.86rem' }}>Vault password</span>
      {has === null
        ? <span style={{ color: 'var(--hp-dim)', fontSize: '0.8rem' }}>…</span>
        : has
          ? <span style={{ ...pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)') }}><CheckCircle2 size={12} /> Set by sorter</span>
          : <span style={{ ...pill('rgba(148,163,184,0.14)', 'var(--hp-dim)') }}>Not set yet</span>}
      <button onClick={reset} disabled={busy || !has} title={has ? 'Clear it so the sorter can set a new one' : 'Nothing to reset yet'}
        style={{ ...miniBtn, marginLeft: 'auto', opacity: (busy || !has) ? 0.5 : 1 }}>
        {busy ? '…' : <><RefreshCw size={12} /> Reset</>}
      </button>
    </div>
  );
}

// Super fund + bank details are redacted from the sorters list feed; the office
// reveals them here one sorter at a time behind the vault admin secret. Mirrors the
// TFN reveal in CompliancePage: secret-gated, audited, auto-hides after 30s.
function RevealBankSuper({ login, flash }) {
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  async function reveal() {
    setBusy(true);
    try {
      const r = await fetch('/.netlify/functions/sorter-vault', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Hp-Vault-Admin': secret },
        body: JSON.stringify({ op: 'revealRegistry', login, by: 'admin' }),
      }).then(x => x.json());
      if (r && r.success) { setDetail(r.detail || {}); setSecret(''); setOpen(false); setTimeout(() => setDetail(null), 30000); }
      else flash(r && r.error === 'Not authorised.'
        ? 'Reveal needs the vault admin secret (HP_VAULT_ADMIN_SECRET) — set in Netlify and entered here correctly.'
        : ((r && r.error) || 'Could not reveal bank & super details.'), false);
    } catch (e) { flash(e.message, false); } finally { setBusy(false); }
  }

  const miniBtn = { padding: '6px 11px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 5 };
  const row = (k, v) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0', fontSize: '0.84rem' }}>
      <span style={{ color: 'var(--hp-dim)' }}>{k}</span>
      <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--hp-text)' }}>{v || '—'}</span>
    </div>
  );

  return (
    <div style={{ marginTop: 14, padding: '11px 13px', borderRadius: 10, border: '1px solid var(--hp-border)', background: 'rgba(201,168,76,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <ShieldCheck size={14} color="var(--hp-gold)" />
        <span style={{ fontWeight: 700, fontSize: '0.86rem' }}>Bank &amp; super details</span>
        {!detail && !open && <button onClick={() => setOpen(true)} style={{ ...miniBtn, marginLeft: 'auto' }}><Eye size={13} /> Reveal</button>}
      </div>
      {!detail && !open && <p style={{ color: 'var(--hp-dim)', fontSize: '0.72rem', margin: '6px 0 0' }}>Hidden by default. Revealing is logged to the vault audit and needs <code>HP_VAULT_ADMIN_SECRET</code>.</p>}
      {open && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="password" value={secret} onChange={e => setSecret(e.target.value)} placeholder="Vault admin secret" autoComplete="off"
            style={{ ...input, maxWidth: 220, padding: '6px 9px', marginTop: 0 }} />
          <button onClick={reveal} disabled={busy || !secret} style={{ ...miniBtn, background: 'var(--hp-gold)', color: '#1a1a2e', borderColor: 'transparent', opacity: (busy || !secret) ? 0.5 : 1 }}>{busy ? '…' : 'Show details'}</button>
          <button onClick={() => { setOpen(false); setSecret(''); }} style={miniBtn}>Cancel</button>
        </div>
      )}
      {detail && (
        <div style={{ marginTop: 10 }}>
          {row('Super fund', detail.superFund)}
          {row('Super USI', detail.superUsi)}
          {row('Member no.', detail.superMemberNo)}
          {row('Institution', detail.bankName)}
          {row('Account name', detail.accountName)}
          {row('BSB', detail.bsb)}
          {row('Account no.', detail.accNumber)}
          <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginTop: 6 }}>Hidden again shortly · this view is logged to the vault audit.</div>
        </div>
      )}
    </div>
  );
}

// rosterOnly = mounted by the scoped /sort-roster page for a night-sort roster manager:
// render ONLY the roster builder (draftMode) — no tabs, no test-mode flip, no onboarding
// table, and a PII-free people list (listSortersForRoster, not the admin /sorters feed).
// ── Outlet mis-route alerts ───────────────────────────────────────────────────
// Sorters at the machine report "this street was programmed to outlet X but the machine
// dropped it into Y". Each is an OPEN alert here; the office actions it (re-program / fix the
// route) then marks it actioned, or dismisses a false alarm.
function MisrouteAlertsView({ flash, onChange }) {
  const [reports, setReports] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try { const r = await api('nsListMisroutes', {}); setReports(r && r.success ? r.reports : []); if (onChange) onChange(); }
    catch { setReports([]); }
  }, [onChange]);
  useEffect(() => { load(); }, [load]);

  async function resolve(id, status) {
    setBusy(id + status);
    try {
      const r = await api('nsResolveMisroute', { id, status, resolvedBy: 'Office' });
      if (!r || !r.success) throw new Error((r && r.error) || 'Could not update.');
      flash(status === 'actioned' ? 'Marked as actioned.' : status === 'dismissed' ? 'Dismissed.' : 'Reopened.');
      await load();
    } catch (e) { flash(e.message, false); } finally { setBusy(''); }
  }

  const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };

  if (reports === null) return <div style={card}><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading alerts…</div>;
  const open = reports.filter(r => r.status === 'open');
  const resolved = reports.filter(r => r.status !== 'open');
  const list = showResolved ? reports : open;

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: 0 }}><Flag size={18} style={{ verticalAlign: -3, marginRight: 6 }} />Outlet mis-route alerts</h2>
        <span style={pill(open.length ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)', open.length ? 'var(--hp-red, #EF4444)' : 'var(--hp-green, #22C55E)')}>{open.length} open</span>
        <button onClick={load} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', marginLeft: 'auto', padding: '7px 12px', fontSize: '0.8rem' }}><RefreshCw size={13} /> Refresh</button>
        {resolved.length > 0 && (
          <button onClick={() => setShowResolved(s => !s)} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '7px 12px', fontSize: '0.8rem' }}>
            {showResolved ? 'Hide resolved' : `Show resolved (${resolved.length})`}
          </button>
        )}
      </div>
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.86rem', margin: '0 0 16px', lineHeight: 1.6 }}>
        Reported by sorters from the Sort Guide when the machine sends a street to a different outlet than it's programmed for. Action the machine programming / route, then mark it actioned.
      </p>

      {list.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--hp-dim)' }}>
          <CheckCircle2 size={28} color="var(--hp-green, #22C55E)" />
          <div style={{ marginTop: 10, fontSize: '0.9rem' }}>{showResolved ? 'No alerts on record.' : 'No open alerts — all clear.'}</div>
        </div>
      ) : list.map(r => {
        const resolvedRow = r.status !== 'open';
        return (
          <div key={r.id} style={{ border: '1px solid var(--hp-border)', borderLeft: `3px solid ${resolvedRow ? 'var(--hp-border)' : 'var(--hp-red, #EF4444)'}`, borderRadius: 10, padding: '12px 14px', marginBottom: 10, opacity: resolvedRow ? 0.7 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--hp-text)' }}>{r.street}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.86rem' }}>
                <span style={pill('rgba(56,189,248,0.14)', 'var(--hp-sky, #38BDF8)')}>Programmed: Outlet {r.expectedOutlet || '—'}</span>
                <ArrowLeftRight size={14} color="var(--hp-dim)" />
                <span style={pill('rgba(239,68,68,0.14)', 'var(--hp-red, #EF4444)')}>Machine: Outlet {r.actualOutlet || '—'}</span>
              </span>
              {resolvedRow && <span style={pill('rgba(148,163,184,0.14)', 'var(--hp-dim)')}>{r.status === 'actioned' ? 'Actioned' : 'Dismissed'}</span>}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginBottom: r.note ? 8 : 10 }}>
              <span>{r.area || '—'}{r.roundLogin ? ` · Round ${r.roundLogin}` : ''}</span>
              <span>Chute {r.expectedChute || '—'} · Bay {r.expectedBay || '—'}</span>
              <span>Reported by {r.sorterName || r.sorterLogin || 'a sorter'}</span>
              <span>{fmtWhen(r.createdAt)}</span>
            </div>
            {r.note && <div style={{ fontSize: '0.82rem', color: 'var(--hp-text)', background: 'var(--hp-card, #1E1E24)', borderRadius: 6, padding: '6px 10px', marginBottom: 10 }}>“{r.note}”</div>}
            {!resolvedRow ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => resolve(r.id, 'actioned')} disabled={!!busy} style={{ ...btn('var(--hp-green, #22C55E)'), padding: '8px 14px', fontSize: '0.82rem' }}>
                  {busy === r.id + 'actioned' ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Check size={14} />} Mark actioned
                </button>
                <button onClick={() => resolve(r.id, 'dismissed')} disabled={!!busy} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '8px 14px', fontSize: '0.82rem' }}>
                  <X size={14} /> Dismiss
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.76rem', color: 'var(--hp-dim)' }}>
                <span>{r.status === 'actioned' ? 'Actioned' : 'Dismissed'} {r.resolvedAt ? fmtWhen(r.resolvedAt) : ''}{r.resolvedBy ? ` · ${r.resolvedBy}` : ''}</span>
                <button onClick={() => resolve(r.id, 'open')} disabled={!!busy} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', padding: '5px 10px', fontSize: '0.74rem', marginLeft: 'auto' }}>Reopen</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function SortersAdminPage({ rosterOnly = false, draftMode = false, managerName = '' } = {}) {
  const [sorters, setSorters] = useState([]);
  const [vaultLogin, setVaultLogin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [newPin, setNewPin] = useState(null);
  const [view, setView] = useState('roster');
  const [testMode, setTestMode] = useState(true); // assume TEST until the server says otherwise
  const [testMobile, setTestMobile] = useState('');
  const [misrouteOpen, setMisrouteOpen] = useState(0); // open outlet mis-route alerts (badge/banner)
  // Open "can't work" requests. A sorter asks from their portal and nothing announces it,
  // so unanswered the night just turns up short — hence a banner on every tab of this page
  // and a red count in the sidebar (AppContext.sorterOfferAlertCount, same endpoint).
  const [openOffers, setOpenOffers] = useState([]);

  const flash = (msg, good = true) => { setToast({ msg, good }); setTimeout(() => setToast(null), 5000); };

  useEffect(() => { if (rosterOnly) return; api('nsGetTestMode', {}).then(r => { if (r && r.success) setTestMode(!!r.testMode); }).catch(() => {}); }, [rosterOnly]);

  const refreshMisrouteCount = useCallback(() => {
    if (rosterOnly) return;
    api('nsListMisroutes', {}).then(r => { if (r && r.success) setMisrouteOpen(r.open || 0); }).catch(() => {});
  }, [rosterOnly]);
  useEffect(() => { refreshMisrouteCount(); }, [refreshMisrouteCount]);

  const refreshOpenOffers = useCallback(() => {
    if (rosterOnly) return;   // the scoped manager view is not admin — the count would 403
    api('getSorterAlertCount', {}).then(r => { if (r && r.success) setOpenOffers(r.offers || []); }).catch(() => {});
  }, [rosterOnly]);
  useEffect(() => {
    refreshOpenOffers();
    // The roster grid resolves offers inside its own component; it shouts when it does.
    window.addEventListener('hp-sorter-offers-changed', refreshOpenOffers);
    return () => window.removeEventListener('hp-sorter-offers-changed', refreshOpenOffers);
  }, [refreshOpenOffers]);

  async function flipTestMode() {
    const goingLive = testMode;
    if (goingLive && !window.confirm('Go LIVE? SMS broadcasts will reach ALL active sorters with a mobile on file.')) return;
    const r = await api('nsSetTestMode', { on: !testMode });
    if (r && r.success) { setTestMode(r.testMode); flash(r.testMode ? 'Test mode ON — broadcasts reach TEST accounts only.' : 'LIVE — broadcasts now reach the whole team.'); }
    else flash((r && r.error) || 'Could not change mode', false);
  }

  async function saveTestMobile() {
    if (!testMobile.trim()) return;
    const r = await api('updateSorter', { sorter: { login: 'TEST', mobile: testMobile.trim() } });
    if (r && r.success) { flash('TEST account mobile saved — test broadcasts will SMS that number.'); setTestMobile(''); }
    else flash((r && r.error) || 'Could not save', false);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (rosterOnly) {
        // Manager view — name-only list, never the bank/PII feed that /sorters returns.
        const data = await api('listSortersForRoster', {});
        if (data && data.success) setSorters(data.sorters || []);
      } else {
        const res = await fetch('/.netlify/functions/sorters');
        const data = await res.json();
        if (data.success) setSorters(data.sorters || []);
      }
    } catch {} finally { setLoading(false); }
  }, [rosterOnly]);

  useEffect(() => { load(); }, [load]);

  async function addSorter() {
    if (!form.login.trim() || !form.name.trim()) { flash('Login and name are required.', false); return; }
    setBusy(true); setNewPin(null);
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'addSorter', sorter: form }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Could not add.');
      setNewPin({ login: data.login, pin: data.pin });
      flash(`Added ${data.login}. Share the PIN below so they can sign in.`);
      setForm(EMPTY);
      load();
    } catch (e) { flash(e.message, false); }
    finally { setBusy(false); }
  }

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Flip a single sorter Active Y/N (updateSorter accepts `active`). No UI existed
  // for this — needed for go-live (activate the team) and ongoing (leavers/new hires).
  async function toggleActive(login, makeActive) {
    setBusy('act:' + login);
    try {
      const r = await api('updateSorter', { sorter: { login, active: makeActive ? 'Y' : 'N' } });
      if (!r || !r.success) throw new Error((r && r.error) || 'Could not update.');
      flash(`${login} ${makeActive ? 'activated' : 'deactivated'}.`);
      await load();
    } catch (e) { flash(e.message, false); } finally { setBusy(false); }
  }
  // Change an existing sorter's grade (drives the pay level). No edit UI existed —
  // the 19 were all loaded as grade1; this lets you grade them before go-live.
  async function saveGrade(login, grade) {
    setBusy('grade:' + login);
    try {
      const r = await api('updateSorter', { sorter: { login, grade } });
      if (!r || !r.success) throw new Error((r && r.error) || 'Could not update grade.');
      const lbl = (GRADES.find(g => g.id === grade) || {}).label || grade;
      flash(`${login} → ${lbl}.`);
      await load();
    } catch (e) { flash(e.message, false); } finally { setBusy(false); }
  }
  // Bulk activate every real sorter (skips TEST*/ZZ* accounts) — the go-live flip.
  async function activateAllForGoLive() {
    const targets = sorters.filter(s => {
      const l = (s.Login || '').toUpperCase();
      return (s.Active || 'Y').toUpperCase() !== 'Y' && !l.startsWith('TEST') && !l.startsWith('ZZ');
    });
    if (!targets.length) { flash('No inactive real sorters to activate.'); return; }
    // List NAMES, not just logins. An inactive row is not always a not-yet-started
    // hire — it can be someone who has LEFT, and a bare login (BERNALRUJ) in a list
    // of twenty is invisible. Seeing "Juan Bernal Ruiz" is what makes a leaver
    // catchable before they're handed a working sign-in.
    const lines = targets.map(s => `  • ${s.Name || s.Login} (${s.Login})`).join('\n');
    if (!window.confirm(
      `Activate ${targets.length} sorter${targets.length === 1 ? '' : 's'} for go-live? They will be able to sign in immediately.\n\n${lines}\n\n`
      + `Check this list for anyone who has LEFT — inactive does not always mean "not started yet".`
    )) return;
    setBusy('golive');
    let ok = 0;
    for (const s of targets) {
      try { const r = await api('updateSorter', { sorter: { login: s.Login, active: 'Y' } }); if (r && r.success) ok++; } catch {}
    }
    flash(`Activated ${ok} of ${targets.length}.`, ok === targets.length);
    await load();
    setBusy(false);
  }

  // Scoped roster-manager view — only the builder, in draft (submit-for-approval) mode.
  if (rosterOnly) {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 1100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Moon size={24} color="var(--hp-gold)" />
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', letterSpacing: '0.03em', margin: 0 }}>Night Sort Roster</h1>
          {managerName && <span style={pill('rgba(201,168,76,0.14)', 'var(--hp-gold)')}>{managerName}</span>}
          <button onClick={load} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', marginLeft: 'auto' }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
        <p style={{ color: 'var(--hp-dim)', fontSize: '0.9rem', margin: '0 0 20px', maxWidth: 720, lineHeight: 1.6 }}>
          Build the week, then <strong>Submit for approval</strong>. Nothing reaches the team until an admin approves it — you can keep editing and re-submit any time.
        </p>
        {toast && (
          <div style={{ ...pill(toast.good ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', toast.good ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)'), padding: '9px 14px', marginBottom: 16 }}>
            {toast.good ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {toast.msg}
          </div>
        )}
        <RosterBuilder sorters={sorters} testMode={testMode} draftMode={draftMode} managerName={managerName} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Moon size={24} color="var(--hp-gold)" />
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', letterSpacing: '0.03em', margin: 0 }}>Night Sorters</h1>
        <button onClick={flipTestMode} title={testMode ? 'Click to go LIVE' : 'Click to return to test mode'}
          style={{ ...btn(testMode ? 'var(--hp-amber, #F59E0B)' : 'var(--hp-green, #22C55E)'), marginLeft: 'auto', padding: '8px 14px', fontSize: '0.8rem' }}>
          {testMode ? 'TEST MODE' : 'LIVE'}
        </button>
        <button onClick={load} style={{ ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>
      {testMode && (
        <div style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid var(--hp-amber, #F59E0B)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <AlertTriangle size={15} color="var(--hp-amber, #F59E0B)" />
          <span style={{ fontSize: '0.82rem' }}>
            <strong>Test period</strong> — build rosters with real names freely; SMS broadcasts go <strong>only</strong> to TEST accounts, real staff get nothing.
            View the sorter portal as login <code>TEST</code>.
          </span>
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
            <input style={{ ...input, width: 150, marginBottom: 0, padding: '6px 10px', fontSize: '0.78rem' }} placeholder="04xx xxx xxx" value={testMobile} onChange={e => setTestMobile(e.target.value)} />
            <button onClick={saveTestMobile} style={{ ...btn('transparent'), color: 'var(--hp-amber, #F59E0B)', border: '1px solid var(--hp-amber, #F59E0B)', padding: '6px 10px', fontSize: '0.74rem' }}>Set test SMS number</button>
          </span>
        </div>
      )}
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.9rem', margin: '0 0 20px', maxWidth: 720, lineHeight: 1.6 }}>
        Casual night sorters (PAYG) under the Road Transport &amp; Distribution Award [MA000038]. Onboard a sorter here, then share their PIN — they complete their
        TFN &amp; super declarations in the <strong>Night Sorters portal</strong> (<code>/sorters</code>).
      </p>

      {openOffers.length > 0 && (
        <div style={{ ...card, borderColor: 'var(--hp-red, #EF4444)', background: 'rgba(239,68,68,0.06)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Inbox size={20} color="var(--hp-red, #EF4444)" />
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>
              {openOffers.length === 1 ? 'A sorter can\u2019t work a shift' : `${openOffers.length} sorters can\u2019t work a shift`} — waiting on you
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 3 }}>
              {openOffers.map(o => `${o.name || '—'} · ${nightLabel(o.date)} · ${o.slot}`).join('  ·  ')}
            </div>
          </div>
          <button
            onClick={() => { setView('roster'); setTimeout(() => { const el = document.getElementById('sorter-shift-offers'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 120); }}
            style={btn('var(--hp-red, #EF4444)')}>Answer it</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {[['roster', openOffers.length > 0 ? `Roster (${openOffers.length})` : 'Roster'], ['peak', 'Peak availability'], ['actualtimes', 'Actual times'], ['apsheets', 'AP Sheets'], ['approvals', 'Approvals'], ['payroll', 'Payroll'], ['xeropush', 'Push to Xero'], ['xeromap', 'Xero mapping'], ['clockqr', 'Clock QR'], ['briefs', 'Briefs'], ['misroutes', misrouteOpen > 0 ? `Outlet alerts (${misrouteOpen})` : 'Outlet alerts'], ['onboarding', 'Team & onboarding']].map(([v, lbl]) => {
          const alert = (v === 'misroutes' && misrouteOpen > 0 && view !== v) || (v === 'roster' && openOffers.length > 0 && view !== v);
          return (
          <button key={v} onClick={() => setView(v)} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${view === v ? 'var(--hp-gold)' : alert ? 'var(--hp-red, #EF4444)' : 'var(--hp-border)'}`, background: view === v ? 'rgba(201,168,76,0.12)' : alert ? 'rgba(239,68,68,0.10)' : 'transparent', color: view === v ? 'var(--hp-gold)' : alert ? 'var(--hp-red, #EF4444)' : 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>{lbl}</button>
          );
        })}
      </div>

      {toast && (
        <div style={{ ...pill(toast.good ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', toast.good ? 'var(--hp-green, #22C55E)' : 'var(--hp-red, #EF4444)'), padding: '9px 14px', marginBottom: 16 }}>
          {toast.good ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {toast.msg}
        </div>
      )}

      {misrouteOpen > 0 && view !== 'misroutes' && (
        <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid var(--hp-red, #EF4444)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Flag size={16} color="var(--hp-red, #EF4444)" />
          <span style={{ fontSize: '0.85rem' }}><strong>{misrouteOpen} outlet mis-route alert{misrouteOpen === 1 ? '' : 's'}</strong> from the floor need review.</span>
          <button onClick={() => setView('misroutes')} style={{ ...btn('var(--hp-red, #EF4444)'), color: '#fff', marginLeft: 'auto', padding: '7px 14px', fontSize: '0.8rem' }}>Review</button>
        </div>
      )}

      {view === 'misroutes' && <MisrouteAlertsView flash={flash} onChange={refreshMisrouteCount} />}

      {view === 'peak' && <PeakAvailabilityView />}
      {view === 'roster' && <RosterBuilder sorters={sorters} testMode={testMode} />}

      {view === 'actualtimes' && <ActualTimesView testMode={testMode} />}

      {view === 'apsheets' && <ApSheetsView sorters={sorters} />}

      {view === 'approvals' && <ApprovalsView testMode={testMode} sorters={sorters} />}

      {view === 'payroll' && <PayrollView />}
      {view === 'xeropush' && <XeroPushView />}
      {view === 'xeromap' && <XeroMapView sorters={sorters} />}
      {view === 'xeromap' && <XeroProfileSyncCard />}

      {view === 'clockqr' && <ClockQrView flash={flash} />}

      {view === 'briefs' && <SorterBriefsView />}

      {view === 'onboarding' && (<>
      <div style={card}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: '0 0 16px' }}><UserPlus size={18} style={{ verticalAlign: -3, marginRight: 6 }} />Add a sorter</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 14 }}>
          <div><label style={label}>Login *</label><input style={input} value={form.login} onChange={set('login')} placeholder="SMITHJ" /></div>
          <div><label style={label}>Full name *</label><input style={input} value={form.name} onChange={set('name')} /></div>
          <div><label style={label}>Classification</label><select style={input} value={form.grade} onChange={set('grade')}>{GRADES.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}</select></div>
          <div><label style={label}>Facility</label><select style={input} value={form.facility} onChange={set('facility')}><option value="">—</option>{FACILITIES.map(f => <option key={f} value={f}>{f}</option>)}</select></div>
          <div><label style={label}>Mobile</label><input style={input} value={form.mobile} onChange={set('mobile')} /></div>
          <div><label style={label}>Email</label><input style={input} value={form.email} onChange={set('email')} /></div>
          <div><label style={label}>Start date</label><input style={input} type="date" value={form.startDate} onChange={set('startDate')} /></div>
          <div><label style={label}>Base rate (optional)</label><input style={input} value={form.baseRate} onChange={set('baseRate')} placeholder="auto from award" /></div>
          <div><label style={label}>Institution</label><input style={input} value={form.bankName} onChange={set('bankName')} /></div>
          <div><label style={label}>Account name</label><input style={input} value={form.accountName} onChange={set('accountName')} /></div>
          <div><label style={label}>BSB</label><input style={input} value={form.bsb} onChange={set('bsb')} /></div>
          <div><label style={label}>Account number</label><input style={input} value={form.accNumber} onChange={set('accNumber')} /></div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
          <button style={btn('var(--hp-gold)')} disabled={busy} onClick={addSorter}>{busy ? <Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> : <UserPlus size={16} />} Add sorter</button>
          {newPin && (
            <span style={pill('rgba(201,168,76,0.14)', 'var(--hp-gold)')}>
              <ShieldCheck size={13} /> {newPin.login} PIN: <strong style={{ fontFamily: 'var(--font-mono, monospace)' }}>{newPin.pin}</strong>
            </span>
          )}
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', margin: '0 0 4px' }}>Roster &amp; onboarding status</h2>
            <p style={{ color: 'var(--hp-dim)', fontSize: '0.82rem', margin: '0 0 14px' }}>{sorters.length} sorter{sorters.length === 1 ? '' : 's'} · {sorters.filter(s => (s.Active || 'Y').toUpperCase() === 'Y').length} active. Set each sorter's grade in the <b>Class</b> column (drives the pay level L1/L2/L3) and click the <b>Active</b> badge to flip sign-in on/off.</p>
          </div>
          <button onClick={activateAllForGoLive} disabled={busy === 'golive'} title="Activate every real sorter (skips TEST/ZZ accounts)" style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--hp-green, #22C55E)', color: '#0B0B0F', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)', fontSize: '0.82rem' }}>
            {busy === 'golive' ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <CheckCircle2 size={14} />} Activate all for go-live
          </button>
        </div>
        {loading ? (
          <div style={{ color: 'var(--hp-dim)', padding: 20 }}><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>
        ) : sorters.length === 0 ? (
          <div style={{ color: 'var(--hp-dim)', padding: '20px 0' }}>No sorters yet. Add your first one above. (The registry sheet is created automatically on the first add.)</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--hp-dim)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  <th style={{ padding: '8px 10px' }}>Login</th><th style={{ padding: '8px 10px' }}>Name</th>
                  <th style={{ padding: '8px 10px' }}>Class</th><th style={{ padding: '8px 10px' }}>Facility</th>
                  <th style={{ padding: '8px 10px' }}>TFN</th><th style={{ padding: '8px 10px' }}>Super</th>
                  <th style={{ padding: '8px 10px' }}>Active</th>
                  <th style={{ padding: '8px 10px' }} />
                </tr>
              </thead>
              <tbody>
                {sorters.map((s, i) => (
                  <Fragment key={s.Login || i}>
                  <tr style={{ borderTop: '1px solid var(--hp-border)' }}>
                    <td style={{ padding: '10px', fontFamily: 'var(--font-mono, monospace)' }}>{s.Login}</td>
                    <td style={{ padding: '10px' }}>{s.Name}</td>
                    <td style={{ padding: '10px' }}>
                      <select value={GRADES.some(g => g.id === s.Grade) ? s.Grade : 'grade1'} onChange={e => saveGrade(s.Login, e.target.value)} disabled={busy === 'grade:' + s.Login || busy === 'golive'} title={s.Classification ? 'Classification: ' + s.Classification : 'Sets the pay level'} style={{ background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 6, color: 'var(--hp-text)', fontSize: '0.82rem', padding: '5px 8px', fontFamily: 'var(--font-body)', cursor: 'pointer', maxWidth: 210 }}>
                        {GRADES.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
                      </select>
                      {busy === 'grade:' + s.Login && <Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite', marginLeft: 6, verticalAlign: 'middle' }} />}
                    </td>
                    <td style={{ padding: '10px' }}>{s.Facility || '—'}</td>
                    <td style={{ padding: '10px' }}>{(s['TFN Status'] === 'Provided') ? ok('Provided') : warn('Pending')}</td>
                    <td style={{ padding: '10px' }}>{(s['Super Decl Status'] === 'Provided') ? ok('Provided') : warn('Pending')}</td>
                    <td style={{ padding: '10px' }}>
                      {(() => { const isActive = (s.Active || 'Y').toUpperCase() === 'Y'; const b = busy === 'act:' + s.Login; return (
                        <button onClick={() => toggleActive(s.Login, !isActive)} disabled={!!busy} title={isActive ? 'Click to deactivate (blocks sign-in)' : 'Click to activate (allows sign-in)'} style={{ border: 'none', background: 'transparent', cursor: busy ? 'default' : 'pointer', padding: 0 }}>
                          {b ? <Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> : (isActive ? ok('Active') : warn('Inactive'))}
                        </button>
                      ); })()}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>
                      <button onClick={() => setVaultLogin(v => v === s.Login ? null : s.Login)} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${vaultLogin === s.Login ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: vaultLogin === s.Login ? 'rgba(201,168,76,0.12)' : 'transparent', color: vaultLogin === s.Login ? 'var(--hp-gold)' : 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <ShieldCheck size={13} /> Documents
                      </button>
                    </td>
                  </tr>
                  {vaultLogin === s.Login && (
                    <tr>
                      <td colSpan={8} style={{ padding: '0 10px 6px' }}><VaultDocsPanel login={s.Login} name={s.Name} flash={flash} onChanged={load} /></td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}
